import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrash, faFilePdf, faEnvelope, faPaperclip, faBan, faRotateLeft, faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import { PDFDocument, rgb, StandardFonts, PDFImage } from 'pdf-lib';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import SearchableDropdown from '../components/SearchableDropdown';
import {
  fetchOrderByOrderNumber,
  voidOrder,
  unvoidOrder,
  updateOrder,
  fetchOrderInvoices,
  fetchOrderActivityLog,
  addOrderActivityLog,
  createInvoiceWithPdf,
  sendInvoiceConfirmation,
  sendContractEmail,
  sendElevatorSurvey,
  fetchOrderInstallationActivity,
  setOrderPilotTerm,
  orderAssigneeNames,
  type OrderRow,
  type OrderInvoiceRow,
  type InvoiceConfirmationPayload,
  type OrderInstallationActivity,
} from '../api/orderApi';
import { fetchVerifiedUsers, type VerifiedUser } from '../api/users';
import { createTask } from '../api/tasks';
import TaskAssigneePicker from '../components/TaskAssigneePicker';
import { createInstallationAppointment, type SeedProduct } from '../api/installationAppointments';
import {
  fetchOrderElevatorSurveyLinks,
  generateOrderElevatorSurveyLink,
  revokeElevatorSurveyLink,
  type ElevatorInitialSurveyRow,
} from '../api/elevatorAppointments';
import { createSiteInspection } from '../api/siteInspectionAppointments';
import { type OrderProduct } from '../data/orderProducts';
import { normalizeSalesChecklistState } from '../data/salesChecklist';
import { drawTrialAgreementScopeText, TRIAL_SCOPE_PLACEMENT } from '../utils/trialContractScopePdf';
import { TEXT_PLACEMENTS as SERVICE_TEXT_PLACEMENTS } from '../utils/serviceAgreementLayout';
import {
  CATALOG_LINES_PAGE_INDEX,
  customProductsTotal,
  drawCatalogLines,
  drawPage1Extras,
  drawClientAuthorizationName,
  drawSecondClientSignatureBox,
  insertContractRevisionPages,
  planContractExtras,
  shiftPageIndex,
  type ContractCustomProduct,
} from '../utils/contractExtrasPdf';
import {
  CATALOG_ROWS,
  catalogRowLabel,
  catalogSectionTotal,
  type ContractUnitPrices,
} from '../utils/contractPricing';
import ContractProductModal, { type ContractProductSection } from '../components/orders/ContractProductModal';
import ContractPdfEditor from '../components/ContractPdfEditor';
import type { ContractPdfEditModel } from '../utils/contractPdfEdits';
import ContractPriceModal from '../components/orders/ContractPriceModal';
import { useInventory } from '../context/InventoryContext';
import { ensureInventoryProducts } from '../api/inventoryApi';
import { fetchClients, type ClientRow } from '../api/clients';
import { SortableTh, StaticTh } from '../components/SortableTh';
import { useTableSortState } from '../hooks/useTableSort';
import { sortRows } from '../utils/tableSort';
import FileDropZone from '../components/FileDropZone';
import { APP_TIME_ZONE, formatDateTimePT } from '../utils/timezone';
import { formatTaskDate } from '../utils/formatTaskDate';
import './Page.css';
import './OrderDetail.css';
import './Orders.css';

type OrderWorkflowStage = 'Contract' | 'Delivery' | 'Installation' | 'Completed';

function categoryToWorkflowStage(category: string): OrderWorkflowStage {
  if (category === 'Sales') return 'Contract';
  if (category === 'Contract') return 'Contract';
  if (category === 'Inventory') return 'Delivery';
  if (category === 'Completed') return 'Completed';
  return 'Installation';
}

function workflowStageToCategory(stage: OrderWorkflowStage): string {
  return stage === 'Delivery' ? 'Inventory' : stage;
}

/** Shape used by OrderDetail (camelCase) */
interface OrderDetailData {
  id: number;
  companyName: string;
  orderNumber: string;
  status: string;
  /** The primary assignee. */
  employee: string | null;
  /** Everyone assigned to the order, primary first. */
  assignees: string[];
  category: 'Contract' | 'Inventory' | 'Installation' | 'Completed';
  salesChecklistState: Record<string, string | boolean>;
  lastContactDate?: string;
  trackingNumber?: string;
  estimatedDeliveryDate?: string;
  shippingAddress?: string;
  deliverTo?: string;
  installationAppointmentTime?: string;
  installationAppointmentId?: number | null;
  installationEmployee?: string;
  siteLocation?: string;
  notes?: string;
  isVoided?: boolean;
  voidReason?: string | null;
  pilotTermDays: number | null;
  pilotTermStartDate: string | null;
  pilotTermEndDate: string | null;
}

/** Calendar date N days after a YYYY-MM-DD date. */
function addDaysToYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

function mapApiOrderToDetail(row: OrderRow): OrderDetailData {
  return {
    id: row.id,
    companyName: row.company_name,
    orderNumber: row.order_number,
    status: row.status,
    employee: row.employee_name ?? null,
    assignees: orderAssigneeNames(row),
    category: row.category as OrderDetailData['category'],
    salesChecklistState: normalizeSalesChecklistState(row.sales_checklist_state),
    lastContactDate: row.last_contact_date ?? undefined,
    trackingNumber: row.tracking_number ?? undefined,
    estimatedDeliveryDate: row.estimated_delivery_date ?? undefined,
    shippingAddress: row.shipping_address ?? undefined,
    deliverTo: row.deliver_to ?? undefined,
    installationAppointmentTime: row.installation_appointment_time ?? undefined,
    installationAppointmentId: row.installation_appointment_id ?? null,
    installationEmployee: row.installation_employee_name ?? undefined,
    siteLocation: row.site_location ?? undefined,
    notes: row.notes != null ? String(row.notes) : undefined,
    isVoided: row.is_voided === true,
    voidReason: row.void_reason ?? null,
    pilotTermDays: row.pilot_term_days ?? null,
    pilotTermStartDate: row.pilot_term_start_date ?? null,
    pilotTermEndDate: row.pilot_term_end_date ?? null,
  };
}

/** Express API base. Defaults to 3001 (the actual server port) so local dev works without REACT_APP_API_URL; the CRA dev server sits on 3000 and would 404 on POSTs. */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const INSTALLATION_CONFIRM_STORAGE_KEY = 'installation_confirm';

/** Matches the server's contract upload limit; express caps JSON bodies at 25 MB. */
const MAX_CONTRACT_UPLOAD_BYTES = 20 * 1024 * 1024;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface ContractRow {
  id: number;
  contract_id: string;
  order_number: string;
  contract_type: string | null;
  status: string;
  /** 'generated' by the portal, or 'uploaded' as an already-signed file. */
  source?: string | null;
  uploaded_file_name?: string | null;
  uploaded_by?: string | null;
  voided_at?: string | null;
  void_reason?: string | null;
  voided_by?: string | null;
  generated_at: string;
  signed_at: string | null;
  created_at: string;
}

interface ActivityLogEntry {
  id: number;
  timestamp: string;
  action: string;
  user: string;
}

interface ChatMessage {
  id: number;
  timestamp: string;
  message: string;
  user: string;
}

/** Form data used to fill PDF contract fields; persisted on the server — client opens a short /contract/:id link */
export interface ContractFormData {
  // Form 1: Client info & agreement basics
  businessName: string;
  serviceAddress: string;
  city: string;
  state: string;
  zip: string;
  /** Combined for PDF/link; set when building payload from city + state + zip */
  cityStateZip?: string;
  locationContactName: string;
  locationContactPhone: string;
  /** Combined for PDF/link; set when building payload from locationContactName + locationContactPhone */
  locationContactNamePhone?: string;
  locationContactEmail: string;
  authorizedPersonName: string;
  authorizedPersonTitle: string;
  authorizedPersonEmail: string;
  authorizedPersonPhone: string;
  effectiveDate: string;
  termStartDate: string;
  /** New field on the E-Series Service Agreement (e.g. "36 months"). Optional for back-compat. */
  agreementTermLength?: string;
  // Form 2: Costs & inventory
  implementationCost: string;
  shippingFee: string;
  // Robot / monthly service quantities
  qtyTimEBot: string;
  qtyTimECharger: string;
  qtyBaseMetalMonthly: string;
  qtyInsulatedFoodTransportMonthly: string;
  qtyWheeledBinMonthly: string;
  qtyUniversalPlatformMonthly: string;
  qtyDoorOpenersMonthly: string;
  qtyNeuralTechBrainMonthly: string;
  qtyElevatorHardwareMonthly: string;
  qtyLuggageCartMonthly: string;
  qtyKebbi: string;
  monthlyRoboticServiceCost: string;
  // BIM-E robots (trial agreement only)
  qtyBIME: string;
  // Additional accessories for sale (one-time)
  qtyBaseMetalOneTime: string;
  qtyInsulatedFoodTransportOneTime: string;
  qtyWheeledBinOneTime: string;
  qtyUniversalPlatformOneTime: string;
  qtyPlasticBags: string;
  qtyDoorOpenerHardwareOneTime: string;
  qtyHandheldTablet: string;
  additionalAccessoriesCost: string;
  totalMonthlyCost: string;
  implementationCostDue: string;
  discountTarget: string;
  discountType: string;
  discountValue: string;
  /** TechForce signature data URL; included in link so client sees it on the PDF */
  techForceSignature?: string;
  /** Trial agreement only: scope / description text */
  scopeText?: string;
  /** Trial agreement only: quantity of bins */
  qtyBinsTrial?: string;
  /** Trial agreement only: quantity of bases */
  qtyBasesTrial?: string;
  /** Products added on top of the pre-printed catalog lines; printed on page 1 of the PDF. */
  customProducts?: ContractCustomProduct[];
  /** Revisions: extra clauses printed on their own page ahead of the signature page. */
  addendums?: string[];
  /**
   * Negotiated prices for catalog lines on this contract only, keyed by qty
   * field key. Missing keys charge the catalog default.
   */
  unitPrices?: ContractUnitPrices;
  /** Revisions: adds a second Client signature box to the Authorization section. */
  additionalSignatureRequested?: boolean;
  /** Second client signature, captured on the signing page. */
  clientSignatureSecond?: string;
  clientSecondName?: string;
  clientSecondPosition?: string;
}

/** Map contract form qty field keys to product display names for order product table */
const CONTRACT_QTY_TO_PRODUCT_NAME: Record<string, string> = {
  qtyTimEBot: 'TIM-E Bot',
  qtyTimECharger: 'TIM-E Charger',
  qtyBaseMetalMonthly: 'Base - Metal',
  qtyInsulatedFoodTransportMonthly: 'Insulated Food Transport',
  qtyWheeledBinMonthly: 'Wheeled Bin',
  qtyUniversalPlatformMonthly: 'Universal Platform',
  qtyDoorOpenersMonthly: 'Door Openers',
  qtyNeuralTechBrainMonthly: 'NeuralTech Brain',
  qtyElevatorHardwareMonthly: 'Elevator Hardware + Software',
  qtyLuggageCartMonthly: 'Luggage Cart',
  qtyKebbi: 'Kebb-i',
  qtyBIME: 'BIM-E bot',
  qtyBaseMetalOneTime: 'Base - Metal (One-time)',
  qtyInsulatedFoodTransportOneTime: 'Insulated Food Transport (One-time)',
  qtyWheeledBinOneTime: 'Wheeled Bin (One-time)',
  qtyUniversalPlatformOneTime: 'Universal Platform (One-time)',
  qtyPlasticBags: 'Plastic Bags',
  qtyDoorOpenerHardwareOneTime: 'Door Opener Hardware',
  qtyHandheldTablet: 'Handheld Tablet w/ App',
  qtyBinsTrial: 'Bins (Trial)',
  qtyBasesTrial: 'Bases (Trial)',
};

function buildOrderProductsFromContractForm(orderNumber: string, formData: Record<string, string>): OrderProduct[] {
  const expanded: OrderProduct[] = [];
  let idBase = Date.now();
  for (const [key, productName] of Object.entries(CONTRACT_QTY_TO_PRODUCT_NAME)) {
    const qty = parseInt(formData[key] || '0', 10) || 0;
    for (let i = 0; i < qty; i++) {
      expanded.push({
        id: idBase++,
        orderNumber,
        productName,
        quantity: 1,
        unitPrice: 0,
        total: 0,
        serialNumber: `${productName.replace(/\s+/g, '')}-${String(i + 1).padStart(3, '0')}`,
        status: 'Pending',
      });
    }
  }
  return expanded;
}

const EXTRA_EMAIL_FILE_LIMIT = 10;
const EXTRA_EMAIL_FILE_BYTES = 15 * 1024 * 1024;
const EXTRA_EMAIL_TOTAL_BYTES = 20 * 1024 * 1024;
const BLOCKED_EMAIL_ATTACHMENT = /\.(exe|bat|cmd|com|msi|scr|vbs|js|jar|ps1|dll|hta|cpl)$/i;

function extraEmailFileProblems(existing: File[], incoming: File[]): { next: File[]; problems: string[] } {
  const next = [...existing];
  const problems: string[] = [];
  for (const file of incoming) {
    const name = file.name.trim() || 'attachment';
    if (BLOCKED_EMAIL_ATTACHMENT.test(name)) {
      problems.push(`${name} can't be attached to an email.`);
      continue;
    }
    if (file.size <= 0) {
      problems.push(`${name} is empty.`);
      continue;
    }
    if (file.size > EXTRA_EMAIL_FILE_BYTES) {
      problems.push(`${name} is larger than 15 MB.`);
      continue;
    }
    if (next.length >= EXTRA_EMAIL_FILE_LIMIT) {
      problems.push('You can add up to 10 additional files.');
      break;
    }
    const total = next.reduce((sum, item) => sum + item.size, 0) + file.size;
    if (total > EXTRA_EMAIL_TOTAL_BYTES) {
      problems.push('Additional files together must be 20 MB or smaller.');
      break;
    }
    const duplicate = next.some(
      (item) => item.name === file.name && item.size === file.size && item.lastModified === file.lastModified
    );
    if (!duplicate) next.push(file);
  }
  return { next, problems };
}

const OrderDetail: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const { products: inventoryProducts, units: inventoryUnits, setProducts: setInventoryProducts, setUnits: setInventoryUnits, refreshProducts } = useInventory();
  const [orderData, setOrderData] = useState<OrderDetailData | null>(null);
  const [loading, setLoading] = useState(!!orderNumber);
  const [products, setProducts] = useState<OrderProduct[]>([]);
  const [verifiedUsers, setVerifiedUsers] = useState<VerifiedUser[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const users = await fetchVerifiedUsers();
        if (!cancelled) setVerifiedUsers(users);
      } catch {
        if (!cancelled) setVerifiedUsers([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!orderNumber) return;
    let cancelled = false;
    setLoading(true);
    fetchOrderByOrderNumber(orderNumber)
      .then((apiOrder: OrderRow | null) => {
        if (cancelled) return;
        if (apiOrder) {
          setOrderData(mapApiOrderToDetail(apiOrder));
        } else {
          setOrderData(null);
        }
      })
      .catch(() => {
        if (!cancelled) setOrderData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [orderNumber]);

  // Site inspections, installation appointments, and customer sign-offs tied to this order.
  useEffect(() => {
    if (!orderNumber) {
      setInstallActivity({ inspections: [], installations: [], signoffs: [] });
      return;
    }
    let cancelled = false;
    fetchOrderInstallationActivity(orderNumber)
      .then((a) => { if (!cancelled) setInstallActivity(a); })
      .catch(() => { if (!cancelled) setInstallActivity({ inspections: [], installations: [], signoffs: [] }); });
    return () => { cancelled = true; };
  }, [orderNumber]);

  const [apiClient, setApiClient] = useState<ClientRow | null>(null);

  useEffect(() => {
    if (!orderData?.companyName) {
      setApiClient(null);
      return;
    }
    let cancelled = false;
    const target = orderData.companyName.trim().toLowerCase();
    // Match clients first, then fall back to leads — both open at /client/:id.
    Promise.all([fetchClients('client'), fetchClients('lead').catch(() => [])])
      .then(([clients, leads]) => {
        if (cancelled) return;
        const find = (list: ClientRow[]) => list.find((c) => c.company.trim().toLowerCase() === target);
        setApiClient(find(clients) ?? find(leads) ?? null);
      })
      .catch(() => {
        if (!cancelled) setApiClient(null);
      });
    return () => { cancelled = true; };
  }, [orderData?.companyName]);

  const [stage, setStage] = useState<OrderWorkflowStage>(
    orderData ? categoryToWorkflowStage(orderData.category) : 'Installation'
  );
  const [status, setStatus] = useState(orderData?.status || 'Pending');
  const [employee, setEmployee] = useState(orderData?.employee || '');
  const [lastContactDate, setLastContactDate] = useState(orderData?.lastContactDate || '');
  const [trackingNumber, setTrackingNumber] = useState(orderData?.trackingNumber || '');
  const [estimatedDeliveryDate, setEstimatedDeliveryDate] = useState(orderData?.estimatedDeliveryDate || '');
  const [shippingAddress, setShippingAddress] = useState(orderData?.shippingAddress || '');
  const [deliverTo, setDeliverTo] = useState(orderData?.deliverTo || '');
  const [streetAddress, setStreetAddress] = useState('');
  const [aptNumber, setAptNumber] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [country, setCountry] = useState('United States');
  const [installationAppointmentTime, setInstallationAppointmentTime] = useState(orderData?.installationAppointmentTime || '');
  const [installationEmployee, setInstallationEmployee] = useState(orderData?.installationEmployee || '');
  const [siteLocation, setSiteLocation] = useState(orderData?.siteLocation || '');
  const [notes, setNotes] = useState(orderData?.notes || '');

  useEffect(() => {
    if (!orderData) return;
    setStage(categoryToWorkflowStage(orderData.category));
    setStatus(orderData.status || 'Pending');
    setEmployee(orderData.employee || '');
    setLastContactDate(orderData.lastContactDate || '');
    setTrackingNumber(orderData.trackingNumber || '');
    setEstimatedDeliveryDate(orderData.estimatedDeliveryDate || '');
    setShippingAddress(orderData.shippingAddress || '');
    setDeliverTo(orderData.deliverTo || '');
    setInstallationAppointmentTime(orderData.installationAppointmentTime || '');
    setInstallationEmployee(orderData.installationEmployee || '');
    setSiteLocation(orderData.siteLocation || '');
    setNotes(orderData.notes || '');
  }, [orderData]);
  const [installationDate, setInstallationDate] = useState('');
  const [installationTime, setInstallationTime] = useState('');
  const [siteStreetAddress, setSiteStreetAddress] = useState('');
  const [siteAptNumber, setSiteAptNumber] = useState('');
  const [siteCity, setSiteCity] = useState('');
  const [siteState, setSiteState] = useState('');
  const [siteZipCode, setSiteZipCode] = useState('');
  const [siteCountry, setSiteCountry] = useState('United States');
  const [isShippingModalOpen, setIsShippingModalOpen] = useState(false);
  const [isInstallationModalOpen, setIsInstallationModalOpen] = useState(false);
  const [isConfirmInventoryModalOpen, setIsConfirmInventoryModalOpen] = useState(false);
  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
  const [deliveryCardData, setDeliveryCardData] = useState<{
    businessName: string;
    serviceAddress: string;
    city: string;
    state: string;
    zip: string;
    locationContactName: string;
    locationContactPhone: string;
    locationContactEmail: string;
  } | null>(null);
  const [deliveryModalStep, setDeliveryModalStep] = useState<'card' | 'products'>('card');
  const [installationProductStatuses, setInstallationProductStatuses] = useState<Record<number, string>>({});
  const [installationSerialsByProductId, setInstallationSerialsByProductId] = useState<Record<number, string>>({});
  const [installationContractData, setInstallationContractData] = useState<{ businessName: string; city: string } | null>(null);
  const [robotPickerForProduct, setRobotPickerForProduct] = useState<{ orderProductIndex: number; productName: string } | null>(null);
  const installationConfirmLoadedRef = useRef(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [isContractModalFullscreen, setIsContractModalFullscreen] = useState(false);
  const [contractModalStep, setContractModalStep] = useState<'choose' | 'form' | 'form2' | 'inventory_trial' | 'scope' | 'revisions' | 'edit-pdf' | 'signature' | 'email' | 'upload'>('choose');
  /** PDF after the revisions "Edit contract" step. Signature is stamped onto this, and this file is emailed. */
  const [editedContractPdf, setEditedContractPdf] = useState<Uint8Array | null>(null);
  /** Pristine filled PDF the editor applies changes onto, plus the in-progress edit model. */
  const [contractPdfBase, setContractPdfBase] = useState<Uint8Array | null>(null);
  const [contractPdfModel, setContractPdfModel] = useState<ContractPdfEditModel | null>(null);
  const [contractEditorBytes, setContractEditorBytes] = useState<Uint8Array | null>(null);
  const [isOpeningContractEditor, setIsOpeningContractEditor] = useState(false);
  const contractModalBodyRef = React.useRef<HTMLDivElement>(null);
  /** Which contract section the "Add product" dialog is adding to (null = closed). */
  const [contractProductSection, setContractProductSection] = useState<ContractProductSection | null>(null);
  /** Which catalog list the "Edit prices" dialog is repricing (null = closed). */
  const [contractPriceSection, setContractPriceSection] = useState<ContractProductSection | null>(null);
  const [selectedContractType, setSelectedContractType] = useState<'service' | 'trial' | null>(null);
  const [orderContracts, setOrderContracts] = useState<ContractRow[]>([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [contractToDelete, setContractToDelete] = useState<{ id: number; label: string } | null>(null);
  const [contractToVoid, setContractToVoid] = useState<ContractRow | null>(null);
  const [contractVoidReason, setContractVoidReason] = useState('');
  const [contractVoidSubmitting, setContractVoidSubmitting] = useState(false);
  const [contractVoidError, setContractVoidError] = useState('');
  const [voidOrderModalOpen, setVoidOrderModalOpen] = useState(false);
  const [voidReasonInput, setVoidReasonInput] = useState('');
  const [voidOrderSubmitting, setVoidOrderSubmitting] = useState(false);
  const [voidOrderError, setVoidOrderError] = useState('');

  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    contracts: false,
    invoice: false,
    products: false,
    activityLog: false,
    appointments: false,
  });
  const toggleSection = (key: string) =>
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  const [installActivity, setInstallActivity] = useState<OrderInstallationActivity>({
    inspections: [],
    installations: [],
    signoffs: [],
  });
  const [orderInvoices, setOrderInvoices] = useState<OrderInvoiceRow[]>([]);
  const [orderInvoicesLoading, setOrderInvoicesLoading] = useState(false);
  const [isGenerateInvoiceModalOpen, setIsGenerateInvoiceModalOpen] = useState(false);
  const [generateInvoiceStep, setGenerateInvoiceStep] = useState<'confirm' | 'dates'>('confirm');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceDueDate, setInvoiceDueDate] = useState('');
  const [invoiceTermsType, setInvoiceTermsType] = useState<'One Time' | 'Net'>('One Time');
  const [invoiceTermsNetDays, setInvoiceTermsNetDays] = useState('30');
  const [invoiceBillingFromClient, setInvoiceBillingFromClient] = useState('');
  const [invoiceBillingEntityFromClient, setInvoiceBillingEntityFromClient] = useState('');
  const [invoiceShippingFromContract, setInvoiceShippingFromContract] = useState('');
  const [invoiceStageShippingFromContract, setInvoiceStageShippingFromContract] = useState('');
  const [orderProductsFromContract, setOrderProductsFromContract] = useState<OrderProduct[]>([]);
  const [invoiceProductsFromContract, setInvoiceProductsFromContract] = useState<OrderProduct[]>([]);
  const [generateInvoiceSubmitting, setGenerateInvoiceSubmitting] = useState(false);
  const [isUploadPdfModalOpen, setIsUploadPdfModalOpen] = useState(false);
  const [uploadPdfFile, setUploadPdfFile] = useState<File | null>(null);
  const [uploadPdfSubmitting, setUploadPdfSubmitting] = useState(false);
  const [contractFormData, setContractFormData] = useState<ContractFormData>({
    businessName: '',
    serviceAddress: '',
    city: '',
    state: '',
    zip: '',
    locationContactName: '',
    locationContactPhone: '',
    locationContactEmail: '',
    authorizedPersonName: '',
    authorizedPersonTitle: '',
    authorizedPersonEmail: '',
    authorizedPersonPhone: '',
    effectiveDate: '',
    termStartDate: '',
    agreementTermLength: '',
    implementationCost: '',
    shippingFee: '',
    qtyTimEBot: '',
    qtyTimECharger: '',
    qtyBaseMetalMonthly: '',
    qtyInsulatedFoodTransportMonthly: '',
    qtyWheeledBinMonthly: '',
    qtyUniversalPlatformMonthly: '',
    qtyDoorOpenersMonthly: '',
    qtyNeuralTechBrainMonthly: '',
    qtyElevatorHardwareMonthly: '',
    qtyLuggageCartMonthly: '',
    qtyKebbi: '',
    monthlyRoboticServiceCost: '',
    qtyBIME: '',
    qtyBaseMetalOneTime: '',
    qtyInsulatedFoodTransportOneTime: '',
    qtyWheeledBinOneTime: '',
    qtyUniversalPlatformOneTime: '',
    qtyPlasticBags: '',
    qtyDoorOpenerHardwareOneTime: '',
    qtyHandheldTablet: '',
    additionalAccessoriesCost: '',
    totalMonthlyCost: '',
    implementationCostDue: '',
    discountTarget: '',
    discountType: 'flat',
    discountValue: '',
  });
  const [contractLink, setContractLink] = useState('');
  const [generatedContractPdfUrl, setGeneratedContractPdfUrl] = useState<string | null>(null);
  const [isGeneratingContractPdf, setIsGeneratingContractPdf] = useState(false);
  // Uploading a contract that was completed outside the portal.
  const [uploadContractType, setUploadContractType] = useState<'service' | 'trial'>('service');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState('');
  const [isUploadingContract, setIsUploadingContract] = useState(false);
  const [generatedContractDbContractId, setGeneratedContractDbContractId] = useState<string>('');
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [emailRecipientInput, setEmailRecipientInput] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [isSendingContractEmail, setIsSendingContractEmail] = useState(false);
  const [contractEmailSent, setContractEmailSent] = useState(false);
  // The Certificate of Insurance and the W-9 are attached to contract emails by
  // default, since clients ask for both to set us up as a vendor. The sender can
  // remove either before sending.
  const [coiAttached, setCoiAttached] = useState(true);
  const [w9Attached, setW9Attached] = useState(true);
  const [extraEmailFiles, setExtraEmailFiles] = useState<File[]>([]);
  const extraEmailFileInputRef = useRef<HTMLInputElement>(null);
  // Listed rather than hand-written twice so both get the same chip and attach
  // affordances, and so the order stays put when one of them is removed.
  const contractEmailDocs = [
    { key: 'coi', filename: 'Techforce COI.pdf', attached: coiAttached, setAttached: setCoiAttached },
    { key: 'w9', filename: 'Techforce W-9.pdf', attached: w9Attached, setAttached: setW9Attached },
  ];
  // Include a link to the Elevator Initial Survey in the contract email (default on).
  const [includeElevatorSurvey, setIncludeElevatorSurvey] = useState(true);
  // Standalone "Send Initial Survey" action (contract stage) — send the survey link on its own.
  const [surveyModalOpen, setSurveyModalOpen] = useState(false);
  const [surveyEmails, setSurveyEmails] = useState('');
  const [surveySending, setSurveySending] = useState(false);
  const [surveySent, setSurveySent] = useState(false);
  const [surveyError, setSurveyError] = useState('');
  const [surveyLinks, setSurveyLinks] = useState<ElevatorInitialSurveyRow[]>([]);
  const [surveyLinksLoading, setSurveyLinksLoading] = useState(false);
  const [surveyLinkBusy, setSurveyLinkBusy] = useState(false);
  const [surveyLinkCopied, setSurveyLinkCopied] = useState(false);
  const [pilotTermModalOpen, setPilotTermModalOpen] = useState(false);
  const [pilotTermDaysInput, setPilotTermDaysInput] = useState('');
  const [pilotTermStartInput, setPilotTermStartInput] = useState('');
  const [pilotTermSaving, setPilotTermSaving] = useState(false);
  const [pilotTermError, setPilotTermError] = useState('');

  /** Products added beyond the pre-printed catalog lines (see the Revisions/product modal). */
  const contractCustomProducts = contractFormData.customProducts ?? [];

  /** Prices this contract negotiated; catalog defaults apply to the rest. */
  const contractUnitPrices = contractFormData.unitPrices;

  // Computed: sum of (qty × unit price) for all monthly service items, including
  // any custom products the team added to that section.
  const computedMonthlyRoboticServiceCost = React.useMemo(
    () =>
      catalogSectionTotal('monthly', contractFormData, contractFormData.unitPrices) +
      customProductsTotal(contractFormData.customProducts ?? [], 'monthly'),
    [contractFormData]
  );

  // Computed: sum of (qty × unit price) for one-time accessories
  const computedAdditionalAccessoriesCost = React.useMemo(
    () =>
      catalogSectionTotal('accessory', contractFormData, contractFormData.unitPrices) +
      customProductsTotal(contractFormData.customProducts ?? [], 'accessory'),
    [contractFormData]
  );

  // Computed: 50% of Implementation Cost
  const computedImplementationCostDue = React.useMemo(() => {
    const raw = contractFormData.implementationCost.replace(/[$,]/g, '');
    const num = parseFloat(raw) || 0;
    return Math.round(num * 0.5 * 100) / 100;
  }, [contractFormData.implementationCost]);

  // Apply discount: returns amount to subtract (flat or percent of base)
  const getDiscountAmount = (base: number) => {
    const target = contractFormData.discountTarget;
    const type = contractFormData.discountType;
    const val = parseFloat(contractFormData.discountValue) || 0;
    if (!target || (!val && val !== 0)) return 0;
    if (type === 'percent') return Math.round((base * val / 100) * 100) / 100;
    return Math.min(val, base);
  };

  const discountedMonthlyRoboticServiceCost = React.useMemo(() => {
    const base = computedMonthlyRoboticServiceCost;
    if (contractFormData.discountTarget !== 'robots') return base;
    return Math.max(0, base - getDiscountAmount(base));
  }, [computedMonthlyRoboticServiceCost, contractFormData.discountTarget, contractFormData.discountType, contractFormData.discountValue]);

  const discountedAdditionalAccessoriesCost = React.useMemo(() => {
    const base = computedAdditionalAccessoriesCost;
    if (contractFormData.discountTarget !== 'accessories') return base;
    return Math.max(0, base - getDiscountAmount(base));
  }, [computedAdditionalAccessoriesCost, contractFormData.discountTarget, contractFormData.discountType, contractFormData.discountValue]);

  // Total Monthly Cost = Monthly Robotic Service Cost + Additional Accessories Cost
  const computedTotalMonthlyCost = React.useMemo(() => {
    return discountedMonthlyRoboticServiceCost + discountedAdditionalAccessoriesCost;
  }, [discountedMonthlyRoboticServiceCost, discountedAdditionalAccessoriesCost]);

  const [signature, setSignature] = useState<string | null>(null);
  const signatureCanvasRef = React.useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [filledPdfUrl, setFilledPdfUrl] = useState<string | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [editStage, setEditStage] = useState<OrderWorkflowStage>(
    orderData ? categoryToWorkflowStage(orderData.category) : 'Installation'
  );
  const [editStatus, setEditStatus] = useState(orderData?.status || 'Pending');
  const [editAssignees, setEditAssignees] = useState<string[]>(orderData?.assignees ?? []);
  const [editLastContactDate, setEditLastContactDate] = useState(orderData?.lastContactDate || '');
  const [editTrackingNumber, setEditTrackingNumber] = useState(orderData?.trackingNumber || '');
  const [editEstimatedDeliveryDate, setEditEstimatedDeliveryDate] = useState(orderData?.estimatedDeliveryDate || '');
  const [editShippingAddress, setEditShippingAddress] = useState(orderData?.shippingAddress || '');
  const [editDeliverTo, setEditDeliverTo] = useState(orderData?.deliverTo || '');
  const [editStreetAddress, setEditStreetAddress] = useState('');
  const [editAptNumber, setEditAptNumber] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editState, setEditState] = useState('');
  const [editZipCode, setEditZipCode] = useState('');
  const [editCountry, setEditCountry] = useState('United States');
  const [editInstallationAppointmentTime, setEditInstallationAppointmentTime] = useState(orderData?.installationAppointmentTime || '');
  const [editInstallationEmployee, setEditInstallationEmployee] = useState(orderData?.installationEmployee || '');
  const [editSiteLocation, setEditSiteLocation] = useState(orderData?.siteLocation || '');
  const [editNotes, setEditNotes] = useState(orderData?.notes || '');
  const [editInstallationDate, setEditInstallationDate] = useState('');
  const [editInstallationTime, setEditInstallationTime] = useState('');
  const [editSiteStreetAddress, setEditSiteStreetAddress] = useState('');
  const [editSiteAptNumber, setEditSiteAptNumber] = useState('');
  const [editSiteCity, setEditSiteCity] = useState('');
  const [editSiteState, setEditSiteState] = useState('');
  const [editSiteZipCode, setEditSiteZipCode] = useState('');
  const [editSiteCountry, setEditSiteCountry] = useState('United States');
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>([]);

  // "Set Up Installation Appointment" (Invoice stage) — prefill + bring-over flow.
  const [isSetupInstallOpen, setIsSetupInstallOpen] = useState(false);
  const [setupBusinessName, setSetupBusinessName] = useState('');
  const [setupBusinessAddress, setSetupBusinessAddress] = useState('');
  const [setupPoc, setSetupPoc] = useState('');
  const [setupPocEmail, setSetupPocEmail] = useState('');
  const [setupPocPhone, setSetupPocPhone] = useState('');
  const [setupInstallAt, setSetupInstallAt] = useState('');
  const [setupInstallerIds, setSetupInstallerIds] = useState<number[]>([]);
  const [setupBusy, setSetupBusy] = useState(false);
  const [setupError, setSetupError] = useState('');

  const contractsSort = useTableSortState();
  const invoicesSort = useTableSortState();
  const activitySort = useTableSortState();
  const productsReadonlySort = useTableSortState();

  // Employees with the Installation role are listed first when picking installers.
  const installerPickerUsers = useMemo(() => {
    const hasRole = (u: VerifiedUser) => (u.roles ?? []).includes('installation');
    return [...verifiedUsers]
      .sort((a, b) => {
        const ai = hasRole(a) ? 0 : 1;
        const bi = hasRole(b) ? 0 : 1;
        if (ai !== bi) return ai - bi;
        return a.username.localeCompare(b.username);
      })
      .map((u) => ({ id: u.id, username: u.username }));
  }, [verifiedUsers]);

  // Orders store employees by name. Anyone already on the order stays listed so they
  // can be removed even without a portal account; the picker's ids are list positions.
  const orderAssigneeOptions = useMemo(() => {
    const names = new Set(verifiedUsers.map((u) => u.username));
    for (const name of orderData?.assignees ?? []) names.add(name);
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [verifiedUsers, orderData?.assignees]);
  const orderAssigneePickerUsers = useMemo(
    () => orderAssigneeOptions.map((username, i) => ({ id: i + 1, username })),
    [orderAssigneeOptions]
  );

  // Bring the order's contract products over to the appointment. Serialized types
  // (Robot / Accessory) are flagged so the installer picks a real unit on the appointment.
  const buildSeedProductsFromOrder = useCallback((): SeedProduct[] => {
    const typeByName = new Map<string, string>();
    for (const p of inventoryProducts) typeByName.set(p.name, p.type);
    const rows = orderProductsFromContract.length > 0 ? orderProductsFromContract : products;
    return rows.map((r) => {
      const type = typeByName.get(r.productName);
      const tracks = type === 'Robot' || type === 'Accessory';
      return {
        product_id: null,
        product_name: r.productName,
        tracks_serial: tracks,
        quantity: 1,
      };
    });
  }, [inventoryProducts, orderProductsFromContract, products]);

  const openSetupInstall = useCallback(() => {
    setSetupBusinessName(orderData?.companyName || '');
    setSetupBusinessAddress(
      apiClient?.site_location || apiClient?.billing_address || orderData?.siteLocation || orderData?.shippingAddress || ''
    );
    setSetupPoc(apiClient?.point_of_contact || '');
    setSetupPocEmail(apiClient?.contact_email || '');
    setSetupPocPhone(apiClient?.contact_phone || '');
    setSetupInstallAt('');
    setSetupInstallerIds([]);
    setSetupError('');
    setIsSetupInstallOpen(true);
  }, [orderData, apiClient]);

  const submitSetupInstall = useCallback(async () => {
    if (!user?.id || !orderNumber) return;
    const name = setupBusinessName.trim();
    if (!name) {
      setSetupError('Business name is required.');
      return;
    }
    if (!setupInstallAt) {
      setSetupError('Date and time of installation is required.');
      return;
    }
    if (setupInstallerIds.length === 0) {
      setSetupError('Select at least one installer.');
      return;
    }
    setSetupBusy(true);
    setSetupError('');
    try {
      const created = await createInstallationAppointment({
        user_id: user.id,
        order_number: orderNumber,
        client_id: apiClient?.id ?? null,
        business_name: name,
        business_address: setupBusinessAddress.trim() || null,
        point_of_contact: setupPoc.trim() || null,
        poc_email: setupPocEmail.trim() || null,
        poc_phone: setupPocPhone.trim() || null,
        installation_at: new Date(setupInstallAt).toISOString(),
        assignee_user_ids: setupInstallerIds,
        products: buildSeedProductsFromOrder(),
      });
      addActivityLog('Installation appointment set up; order moved to Installation stage', employee || 'System');
      setIsSetupInstallOpen(false);
      navigate(`/installation/${created.id}`);
    } catch (err) {
      setSetupError((err as Error).message || 'Failed to set up installation appointment.');
    } finally {
      setSetupBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.id,
    orderNumber,
    setupBusinessName,
    setupBusinessAddress,
    setupPoc,
    setupPocEmail,
    setupPocPhone,
    setupInstallAt,
    setupInstallerIds,
    apiClient,
    buildSeedProductsFromOrder,
    employee,
    navigate,
  ]);

  const sortedOrderContracts = useMemo(
    () =>
      sortRows(orderContracts, contractsSort.sortKey, contractsSort.sortDir, (c, key) => {
        switch (key) {
          case 'type':
            return c.contract_type === 'trial' ? 'Trial' : 'Service';
          case 'status':
            return c.status ?? '';
          case 'generated':
            return new Date(c.generated_at).getTime();
          case 'signed':
            return c.signed_at ? new Date(c.signed_at).getTime() : 0;
          default:
            return '';
        }
      }),
    [orderContracts, contractsSort.sortKey, contractsSort.sortDir]
  );

  const sortedOrderInvoices = useMemo(
    () =>
      sortRows(orderInvoices, invoicesSort.sortKey, invoicesSort.sortDir, (inv, key) => {
        switch (key) {
          case 'item':
            return inv.invoice_number || `Invoice #${inv.id}`;
          case 'pdf':
            return inv.has_pdf ? 1 : 0;
          default:
            return '';
        }
      }),
    [orderInvoices, invoicesSort.sortKey, invoicesSort.sortDir]
  );

  const sortedActivityLog = useMemo(
    () =>
      sortRows(activityLog, activitySort.sortKey, activitySort.sortDir, (e, key) => {
        switch (key) {
          case 'timestamp':
            return new Date(e.timestamp).getTime();
          case 'action':
            return e.action;
          case 'user':
            return e.user;
          default:
            return '';
        }
      }),
    [activityLog, activitySort.sortKey, activitySort.sortDir]
  );

  const sortedOrderProductsReadonly = useMemo(
    () =>
      sortRows(
        orderProductsFromContract,
        productsReadonlySort.sortKey,
        productsReadonlySort.sortDir,
        (p, key) => {
          switch (key) {
            case 'name':
              return p.productName;
            case 'serial':
              return p.serialNumber ?? '';
            case 'status':
              return p.status ?? '';
            default:
              return '';
          }
        }
      ),
    [orderProductsFromContract, productsReadonlySort.sortKey, productsReadonlySort.sortDir]
  );

  const sortedCombinedOrderProducts = useMemo(
    () =>
      sortRows(
        orderProductsFromContract.length > 0 ? orderProductsFromContract : products,
        productsReadonlySort.sortKey,
        productsReadonlySort.sortDir,
        (p, key) => {
          switch (key) {
            case 'name':
              return p.productName;
            case 'serial':
              return p.serialNumber ?? '';
            case 'status':
              return p.status ?? '';
            default:
              return '';
          }
        }
      ),
    [orderProductsFromContract, products, productsReadonlySort.sortKey, productsReadonlySort.sortDir]
  );

  const sortedProductsOnly = useMemo(
    () =>
      sortRows(
        products,
        productsReadonlySort.sortKey,
        productsReadonlySort.sortDir,
        (p, key) => {
          switch (key) {
            case 'name':
              return p.productName;
            case 'serial':
              return p.serialNumber ?? '';
            case 'status':
              return p.status ?? '';
            default:
              return '';
          }
        }
      ),
    [products, productsReadonlySort.sortKey, productsReadonlySort.sortDir]
  );

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTemplatedModalOpen, setIsTemplatedModalOpen] = useState(false);
  const [isChatLogModalOpen, setIsChatLogModalOpen] = useState(false);
  const chatEndRef = React.useRef<HTMLDivElement>(null);

  const anyModalOpen =
    isEditModalOpen ||
    isShippingModalOpen ||
    isInstallationModalOpen ||
    isConfirmInventoryModalOpen ||
    !!robotPickerForProduct ||
    isDeliveryModalOpen ||
    isContractModalOpen ||
    !!contractToDelete ||
    voidOrderModalOpen ||
    isGenerateInvoiceModalOpen ||
    isUploadPdfModalOpen ||
    isTemplatedModalOpen ||
    isChatLogModalOpen;

  useEffect(() => {
    document.body.style.overflow = anyModalOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [anyModalOpen]);

  // Load installation confirm state when viewing an order (from API so all users see the same state; fallback to localStorage if API fails)
  useEffect(() => {
    if (!orderNumber) return;
    installationConfirmLoadedRef.current = false;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}/installation-confirm`);
        if (cancelled) return;
        if (res.ok) {
          const data = (await res.json()) as { statuses?: Record<string, string>; serials?: Record<string, string> };
          const statuses: Record<number, string> = {};
          Object.entries(data.statuses || {}).forEach(([k, v]) => { statuses[Number(k)] = v; });
          const serials: Record<number, string> = {};
          Object.entries(data.serials || {}).forEach(([k, v]) => { serials[Number(k)] = v; });
          if (!cancelled) {
            setInstallationProductStatuses(statuses);
            setInstallationSerialsByProductId(serials);
            installationConfirmLoadedRef.current = true;
          }
          return;
        }
      } catch {
        // fall through to localStorage fallback
      }
      if (cancelled) return;
      try {
        const raw = localStorage.getItem(`${INSTALLATION_CONFIRM_STORAGE_KEY}_${orderNumber}`);
        if (!raw) {
          installationConfirmLoadedRef.current = true;
          return;
        }
        const data = JSON.parse(raw) as { statuses?: Record<string, string>; serials?: Record<string, string> };
        const statuses: Record<number, string> = {};
        Object.entries(data.statuses || {}).forEach(([k, v]) => { statuses[Number(k)] = v; });
        const serials: Record<number, string> = {};
        Object.entries(data.serials || {}).forEach(([k, v]) => { serials[Number(k)] = v; });
        setInstallationProductStatuses(statuses);
        setInstallationSerialsByProductId(serials);
        installationConfirmLoadedRef.current = true;
      } catch {
        installationConfirmLoadedRef.current = true;
      }
    })();
    return () => { cancelled = true; };
  }, [orderNumber]);

  // Persist installation confirm state when it changes (save to API for all users, and to localStorage as backup)
  useEffect(() => {
    if (!orderNumber || !installationConfirmLoadedRef.current) return;
    const statuses: Record<string, string> = {};
    Object.entries(installationProductStatuses).forEach(([k, v]) => { statuses[k] = v; });
    const serials: Record<string, string> = {};
    Object.entries(installationSerialsByProductId).forEach(([k, v]) => { serials[k] = v; });
    (async () => {
      try {
        await fetch(`${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}/installation-confirm`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ statuses, serials }),
        });
      } catch {
        // offline or server error; localStorage backup below
      }
    })();
    try {
      localStorage.setItem(`${INSTALLATION_CONFIRM_STORAGE_KEY}_${orderNumber}`, JSON.stringify({ statuses, serials }));
    } catch {
      // ignore
    }
  }, [orderNumber, installationProductStatuses, installationSerialsByProductId]);

  // Default contract form data from order and client (service address from client page when available)
  const getDefaultContractFormData = useCallback((): ContractFormData => {
    const today = new Date();
    const effectiveDate = `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}/${today.getFullYear()}`;
    const parseAddress = (address: string) => {
      if (!address) return { street: '', city: '', state: '', zip: '' };
      const trimmed = address.trim();
      const lines = trimmed.split(/\n/).map((l) => l.trim()).filter(Boolean);
      const stateZipRegex = /\b([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)\s*$/;
      const cityStateZipRegex = /^(.+?),\s*([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)\s*$/;

      if (lines.length >= 2) {
        const cityStateZip = lines[1];
        const match = cityStateZip.match(cityStateZipRegex);
        if (match) {
          return {
            street: lines[0],
            city: match[1].trim(),
            state: match[2].trim().toUpperCase(),
            zip: match[3].trim()
          };
        }
      }

      const singleLineMatch = trimmed.match(/^(.+?),\s*([^,]+?),\s*([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)\s*$/);
      if (singleLineMatch) {
        return {
          street: singleLineMatch[1].trim(),
          city: singleLineMatch[2].trim(),
          state: singleLineMatch[3].trim().toUpperCase(),
          zip: singleLineMatch[4].trim()
        };
      }

      const parts = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
      if (parts.length >= 4) {
        const last = parts[parts.length - 1];
        const zipOnlyMatch = last.match(/^(\d{5}(?:-\d{4})?)\s*$/);
        if (zipOnlyMatch) {
          const zip = zipOnlyMatch[1];
          const state = parts[parts.length - 2];
          const city = parts[parts.length - 3];
          const street = parts.slice(0, -3).join(', ');
          return {
            street,
            city,
            state: state.length === 2 ? state.toUpperCase() : state,
            zip
          };
        }
      }
      if (parts.length >= 3) {
        const last = parts[parts.length - 1];
        const stateZipMatch = last.match(stateZipRegex);
        if (stateZipMatch) {
          const state = stateZipMatch[1].toUpperCase();
          const zip = stateZipMatch[2];
          const city = parts[parts.length - 2];
          const street = parts.slice(0, -2).join(', ');
          return { street, city, state, zip };
        }
      }

      if (lines.length >= 1) {
        return { street: lines[0], city: '', state: '', zip: '' };
      }
      return { street: trimmed, city: '', state: '', zip: '' };
    };
    const addressSource = apiClient?.site_location || siteLocation || orderData?.shippingAddress || '';
    const addr = parseAddress(addressSource);
    const contactName = apiClient?.point_of_contact ?? '';
    const contactEmail = apiClient?.contact_email ?? '';
    const contactPhone = apiClient?.contact_phone ?? '';
    return {
      businessName: orderData?.companyName || '',
      serviceAddress: addr.street,
      city: addr.city,
      state: addr.state,
      zip: addr.zip,
      locationContactName: contactName,
      locationContactPhone: contactPhone,
      locationContactEmail: contactEmail || '',
      authorizedPersonName: contactName || '',
      authorizedPersonTitle: '',
      authorizedPersonEmail: contactEmail || '',
      authorizedPersonPhone: contactPhone || '',
      effectiveDate,
      termStartDate: effectiveDate,
      agreementTermLength: '',
      implementationCost: '',
      shippingFee: '',
      qtyTimEBot: '',
      qtyTimECharger: '',
      qtyBaseMetalMonthly: '',
      qtyInsulatedFoodTransportMonthly: '',
      qtyWheeledBinMonthly: '',
      qtyUniversalPlatformMonthly: '',
      qtyDoorOpenersMonthly: '',
      qtyNeuralTechBrainMonthly: '',
      qtyElevatorHardwareMonthly: '',
      qtyLuggageCartMonthly: '',
      qtyKebbi: '',
      monthlyRoboticServiceCost: '',
      qtyBIME: '',
      qtyBaseMetalOneTime: '',
      qtyInsulatedFoodTransportOneTime: '',
      qtyWheeledBinOneTime: '',
      qtyUniversalPlatformOneTime: '',
      qtyPlasticBags: '',
      qtyDoorOpenerHardwareOneTime: '',
      qtyHandheldTablet: '',
      additionalAccessoriesCost: '',
      totalMonthlyCost: '',
      implementationCostDue: '',
      discountTarget: '',
      discountType: 'flat',
      discountValue: '',
      scopeText: '',
      qtyBinsTrial: '',
      qtyBasesTrial: '',
      customProducts: [],
      addendums: [],
      additionalSignatureRequested: false,
    };
  }, [orderData, apiClient, siteLocation]);

  // Templated messages
  const templatedMessages = [
    {
      id: 1,
      title: 'Initial Contact',
      message: `Dear ${orderData?.companyName || 'Client'},

Thank you for your order. Someone from the Techforce Team will contact you shortly to discuss the details and next steps.

If you have any questions in the meantime, please do not hesitate to reach out.

Best regards,
Techforce Team`
    },
    {
      id: 2,
      title: 'Order Confirmation',
      message: `Dear ${orderData?.companyName || 'Client'},

We have received your order ${orderData?.orderNumber || ''} and are currently processing it. We will keep you updated on the status as we move forward.

Thank you for choosing Techforce.

Best regards,
Techforce Team`
    },
    {
      id: 3,
      title: 'Status Update',
      message: `Dear ${orderData?.companyName || 'Client'},

This is an update regarding your order ${orderData?.orderNumber || ''}. 

[Please provide specific status update here]

Please feel free to reach out if you have any questions or concerns.

Best regards,
Techforce Team`
    },
    {
      id: 4,
      title: 'Follow Up',
      message: `Dear ${orderData?.companyName || 'Client'},

We wanted to follow up on your order ${orderData?.orderNumber || ''} to ensure everything is proceeding smoothly.

Is there anything we can assist you with at this time?

Best regards,
Techforce Team`
    }
  ];

  // Helper function to format timestamp
  const getCurrentTimestamp = React.useCallback((): string => {
    const now = new Date();
    const date = now.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const time = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${date} ${time}`;
  }, []);

  // Format activity log timestamp from API for display
  const formatActivityTimestamp = React.useCallback((ts: string) => {
    try {
      const d = new Date(ts);
      return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) + ' ' +
        d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch {
      return ts;
    }
  }, []);

  // Helper to add activity log entry (persisted to server when orderNumber exists)
  const addActivityLog = React.useCallback(async (action: string, user: string = 'System') => {
    if (orderNumber) {
      try {
        const row = await addOrderActivityLog(orderNumber, { action, user });
        const newEntry: ActivityLogEntry = {
          id: row.id,
          timestamp: formatActivityTimestamp(row.timestamp),
          action: row.action,
          user: row.user,
        };
        setActivityLog(prev => [newEntry, ...prev]);
      } catch {
        const newEntry: ActivityLogEntry = {
          id: Date.now(),
          timestamp: getCurrentTimestamp(),
          action,
          user,
        };
        setActivityLog(prev => [newEntry, ...prev]);
      }
    } else {
      const newEntry: ActivityLogEntry = {
        id: Date.now(),
        timestamp: getCurrentTimestamp(),
        action,
        user,
      };
      setActivityLog(prev => [newEntry, ...prev]);
    }
  }, [orderNumber, getCurrentTimestamp, formatActivityTimestamp]);

  // Scroll chat to bottom when new messages are added
  React.useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Handle sending a chat message
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (newMessage.trim()) {
      const timestamp = getCurrentTimestamp();
      const messageText = newMessage.trim();
      const messageUser = employee || 'System';
      
      const message: ChatMessage = {
        id: Date.now(),
        timestamp: timestamp,
        message: messageText,
        user: messageUser
      };
      setChatMessages(prev => [...prev, message]);
      setNewMessage('');
      
      // Add activity log entry for sent message
      const messagePreview = messageText.length > 50 ? messageText.substring(0, 50) + '...' : messageText;
      addActivityLog(`Sent chat message: "${messagePreview}"`, messageUser);
      
      // Update last contact date when a message is sent
      if (stage === 'Contract') {
        setLastContactDate(timestamp);
        setEditLastContactDate(timestamp);
      }
    }
  };

  // Handle opening templated messages modal
  const handleOpenTemplatedMessages = () => {
    setIsTemplatedModalOpen(true);
  };

  // Handle selecting a templated message
  const handleSelectTemplatedMessage = (message: string) => {
    setNewMessage(message);
    setIsTemplatedModalOpen(false);
  };

  // Helper function to convert 12-hour time to 24-hour format
  const convertTo24Hour = (time12h: string): string => {
    const [time, modifier] = time12h.split(/\s*(AM|PM)/i);
    if (!time) return '';
    let [hours, minutes] = time.split(':');
    if (hours === '12') {
      hours = modifier?.toUpperCase() === 'AM' ? '00' : '12';
    } else if (modifier?.toUpperCase() === 'PM') {
      hours = String(parseInt(hours, 10) + 12).padStart(2, '0');
    } else {
      hours = hours.padStart(2, '0');
    }
    return `${hours}:${minutes || '00'}`;
  };

  // Helper function to convert 24-hour time to 12-hour format
  const convertTo12Hour = (time24h: string): string => {
    if (!time24h) return '';
    const [hours, minutes] = time24h.split(':');
    const hour24 = parseInt(hours, 10);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes || '00'} ${ampm}`;
  };

  // Get status options based on stage
  const getStatusOptions = (currentStage: OrderWorkflowStage): string[] => {
    switch (currentStage) {
      case 'Contract':
        return ['Pending', 'In Progress', 'Approved', 'Pilot'];
      case 'Delivery':
        return ['Pending', 'Awaiting Invoice', 'Invoice Ready'];
      case 'Installation':
        return ['Pending', 'Scheduled', 'In Progress'];
      case 'Completed':
        return ['Completed'];
      default:
        return ['Pending'];
    }
  };

  // Format installation appointment time (ISO or date string) for readable display
  const formatInstallationAppointmentTime = (value: string): string => {
    if (!value?.trim()) return value || '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
  };

  useEffect(() => {
    if (orderData) {
      const initialStage = categoryToWorkflowStage(orderData.category);
      const validStatuses = getStatusOptions(initialStage);
      const statusForStage = validStatuses.includes(orderData.status) ? orderData.status : validStatuses[0];
      setStage(initialStage);
      setStatus(statusForStage);
      setEmployee(orderData.employee || '');
      setLastContactDate(orderData.lastContactDate || '');
      setTrackingNumber(orderData.trackingNumber || '');
      setEstimatedDeliveryDate(orderData.estimatedDeliveryDate || '');
      setShippingAddress(orderData.shippingAddress || '');
      setDeliverTo(orderData.deliverTo || '');
      setInstallationAppointmentTime(orderData.installationAppointmentTime || '');
      setInstallationEmployee(orderData.installationEmployee || '');
      setSiteLocation(orderData.siteLocation || '');
      // Parse installation date and time
      if (orderData.installationAppointmentTime) {
        const dateTimeMatch = orderData.installationAppointmentTime.match(/(\d{4}-\d{2}-\d{2})\s+(\d{1,2}:\d{2}\s+(?:AM|PM))/i);
        if (dateTimeMatch) {
          setInstallationDate(dateTimeMatch[1]);
          // Convert 12-hour format to 24-hour format for time input
          const time12Hour = dateTimeMatch[2];
          const time24Hour = convertTo24Hour(time12Hour);
          setInstallationTime(time24Hour);
        } else {
          // Try to parse as date and time separately
          const parts = orderData.installationAppointmentTime.split(' ');
          if (parts.length >= 2) {
            setInstallationDate(parts[0]);
            const timePart = parts.slice(1).join(' ');
            // Check if it's already in 24-hour format
            if (timePart.match(/^\d{2}:\d{2}$/)) {
              setInstallationTime(timePart);
            } else {
              // Convert 12-hour format to 24-hour format
              setInstallationTime(convertTo24Hour(timePart));
            }
          }
        }
      }
      // Parse site location if it exists
      if (orderData.siteLocation) {
        const addressLines = orderData.siteLocation.split('\n');
        if (addressLines.length > 0) {
          const firstLine = addressLines[0];
          const aptMatch = firstLine.match(/(.*?)(?:Apt|Unit|#|Suite)\s*([A-Z0-9-]+)/i);
          if (aptMatch) {
            setSiteStreetAddress(aptMatch[1].trim());
            setSiteAptNumber(aptMatch[2].trim());
          } else {
            setSiteStreetAddress(firstLine);
            setSiteAptNumber('');
          }
          if (addressLines.length > 1) {
            const cityStateZip = addressLines[1];
            const match = cityStateZip.match(/(.+?),\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?)/);
            if (match) {
              setSiteCity(match[1].trim());
              setSiteState(match[2].trim());
              setSiteZipCode(match[3].trim());
            }
          }
          if (addressLines.length > 2) {
            setSiteCountry(addressLines[2].trim());
          }
        }
      }
      setEditStage(initialStage);
      setEditStatus(statusForStage);
      setEditAssignees(orderData.assignees);
      setEditLastContactDate(orderData.lastContactDate || '');
      setEditTrackingNumber(orderData.trackingNumber || '');
      setEditEstimatedDeliveryDate(orderData.estimatedDeliveryDate || '');
      setEditShippingAddress(orderData.shippingAddress || '');
      setEditDeliverTo(orderData.deliverTo || '');
      setEditInstallationAppointmentTime(orderData.installationAppointmentTime || '');
      setEditInstallationEmployee(orderData.installationEmployee || '');
      setEditSiteLocation(orderData.siteLocation || '');
      setNotes(orderData.notes || '');
      setEditNotes(orderData.notes || '');
    }
  }, [orderData]);

  // Fetch persisted activity log when order is loaded
  useEffect(() => {
    if (!orderNumber) return;
    let cancelled = false;
    fetchOrderActivityLog(orderNumber)
      .then((entries) => {
        if (cancelled) return;
        const formatted: ActivityLogEntry[] = entries.map((e) => ({
          id: e.id,
          timestamp: (() => {
            try {
              const d = new Date(e.timestamp);
              return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) + ' ' +
                d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
            } catch { return e.timestamp; }
          })(),
          action: e.action,
          user: e.user,
        }));
        setActivityLog(formatted);
      })
      .catch(() => {
        if (!cancelled) setActivityLog([]);
      });
    return () => { cancelled = true; };
  }, [orderNumber]);

  // When stage changes, validate and update status if needed
  useEffect(() => {
    const validStatuses = getStatusOptions(stage);
    if (!validStatuses.includes(status)) {
      setStatus(validStatuses[0]); // Reset to first option if current status is not valid for new stage
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // When edit stage changes, validate and update edit status if needed
  useEffect(() => {
    const validStatuses = getStatusOptions(editStage);
    if (!validStatuses.includes(editStatus)) {
      setEditStatus(validStatuses[0]); // Reset to first option if current status is not valid for new stage
    }
    // Note: We don't clear stage-specific fields when stage changes in edit mode
    // This allows users to see and edit the fields for the current stage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editStage]);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle save logic here
    console.log('Order updated:', { orderNumber, stage, status, employee });
    // Navigate back to orders
    navigate('/orders');
  };

  const [editSaveError, setEditSaveError] = useState('');
  const [orderMessage, setOrderMessage] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderNumber) return;
    setEditSaveError('');
    setEditSaving(true);

    // Track changes and add to activity log
    const changes: string[] = [];
    if (stage !== editStage) changes.push(`Stage changed from ${stage} to ${editStage}`);
    if (status !== editStatus) changes.push(`Status changed from ${status} to ${editStatus}`);
    const currentAssignees = orderData?.assignees ?? [];
    if (currentAssignees.join('\n') !== editAssignees.join('\n')) {
      changes.push(
        `Assigned employees changed from ${currentAssignees.join(', ') || 'unassigned'} to ${editAssignees.join(', ') || 'unassigned'}`
      );
    }
    if (lastContactDate !== editLastContactDate && editLastContactDate) {
      changes.push(lastContactDate ? `Last contact date changed to ${editLastContactDate}` : `Last contact date set to ${editLastContactDate}`);
    }
    if (editStage !== 'Delivery') {
      if (trackingNumber !== editTrackingNumber && editTrackingNumber) {
        changes.push(trackingNumber ? `Tracking number changed to ${editTrackingNumber}` : `Tracking number set to ${editTrackingNumber}`);
      }
      if (estimatedDeliveryDate !== editEstimatedDeliveryDate && editEstimatedDeliveryDate) {
        changes.push(estimatedDeliveryDate ? `Estimated delivery date changed to ${editEstimatedDeliveryDate}` : `Estimated delivery date set to ${editEstimatedDeliveryDate}`);
      }
    }
    if (installationAppointmentTime !== editInstallationAppointmentTime && editInstallationAppointmentTime) {
      changes.push(installationAppointmentTime ? `Installation appointment changed to ${editInstallationAppointmentTime}` : `Installation appointment set to ${editInstallationAppointmentTime}`);
    }
    const nextNotes = editNotes.trim();
    if ((notes || '').trim() !== nextNotes) {
      changes.push(nextNotes ? 'Order notes updated' : 'Order notes cleared');
    }
    changes.forEach(change => addActivityLog(change, editAssignees[0] || 'System'));
    if (changes.length === 0) addActivityLog('Order information updated', editAssignees[0] || 'System');

    try {
      const payload: Parameters<typeof updateOrder>[1] = {
        employee_names: editAssignees,
        category: workflowStageToCategory(editStage),
        status: editStatus,
        last_contact_date: editLastContactDate?.trim() || null,
        shipping_address: editShippingAddress?.trim() || null,
        deliver_to: editDeliverTo?.trim() || null,
        installation_appointment_time: editInstallationAppointmentTime?.trim() || null,
        installation_employee_name: editInstallationEmployee?.trim() || null,
        site_location: editSiteLocation?.trim() || null,
        notes: nextNotes || null,
        acting_user: editAssignees[0] || (user?.username ?? undefined) || undefined,
      };
      if (editStage !== 'Delivery') {
        payload.tracking_number = editTrackingNumber?.trim() || null;
        payload.estimated_delivery_date = editEstimatedDeliveryDate?.trim() || null;
      }
      const updated = await updateOrder(orderNumber, payload);
      setOrderData(mapApiOrderToDetail(updated));
      setStage(editStage);
      setStatus(editStatus);
      setEmployee(updated.employee_name ?? '');
      setLastContactDate(editLastContactDate);
      setTrackingNumber(editTrackingNumber);
      setEstimatedDeliveryDate(editEstimatedDeliveryDate);
      setInstallationAppointmentTime(editInstallationAppointmentTime);
      setShippingAddress(editShippingAddress);
      setDeliverTo(editDeliverTo);
      setInstallationEmployee(editInstallationEmployee);
      setSiteLocation(editSiteLocation);
      setNotes(nextNotes);
      setEditNotes(nextNotes);
      setIsEditModalOpen(false);
      setOrderMessage('Order updated successfully.');
      setTimeout(() => setOrderMessage(''), 4000);
    } catch (err) {
      setEditSaveError(err instanceof Error ? err.message : 'Failed to save order.');
    } finally {
      setEditSaving(false);
    }

    // When moving from Contract to Delivery: fill shipping from most recent signed contract and product table from contract quantities
    const wasContractToDelivery = stage === 'Contract' && editStage === 'Delivery';
    if (wasContractToDelivery && orderNumber) {
      try {
        const res = await fetch(`${API_BASE}/api/contracts?order_number=${encodeURIComponent(orderNumber)}`);
        if (!res.ok) return;
        const data = await res.json();
        const contracts: ContractRow[] = data.contracts || [];
        const signed = contracts.filter((c) => c.status === 'signed').sort((a, b) => (new Date(b.signed_at || 0).getTime() - new Date(a.signed_at || 0).getTime()));
        const latestSigned = signed[0];
        if (!latestSigned) return;
        const formRes = await fetch(`${API_BASE}/api/contracts/${latestSigned.id}/form-data`);
        if (!formRes.ok) return;
        const formData = await formRes.json();
        const fd = (formData.form_data || {}) as Record<string, string>;
        const serviceAddr = (fd.serviceAddress || '').trim();
        const city = (fd.city || '').trim();
        const state = (fd.state || '').trim();
        const zip = (fd.zip || '').trim();
        const cityStateZip = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
        const fullAddress = [serviceAddr, cityStateZip].filter(Boolean).join('\n');
        if (fullAddress) {
          setShippingAddress(fullAddress);
          setEditShippingAddress(fullAddress);
          setStreetAddress(serviceAddr);
          setCity(city);
          setState(state);
          setZipCode(zip);
          setEditStreetAddress(serviceAddr);
          setEditCity(city);
          setEditState(state);
          setEditZipCode(zip);
        }
        const built = buildOrderProductsFromContractForm(orderNumber, fd);
        if (built.length > 0) setProducts(built);
      } catch {
        // ignore
      }
    }
  };

  const handleEditCancel = () => {
    setEditSaveError('');
    // Reset edit values to current values
    setEditStage(stage);
    setEditStatus(status);
    setEditAssignees(orderData?.assignees ?? []);
    setEditLastContactDate(lastContactDate);
    setEditTrackingNumber(trackingNumber);
    setEditEstimatedDeliveryDate(estimatedDeliveryDate);
    setEditShippingAddress(shippingAddress);
    setEditDeliverTo(deliverTo);
    setEditInstallationAppointmentTime(installationAppointmentTime);
    setEditInstallationEmployee(installationEmployee);
    setEditSiteLocation(siteLocation);
    setEditNotes(notes);
    setIsEditModalOpen(false);
  };

  // PDF Form Field Mapping Configuration
  // Update these field names to match your PDF form field names
  // To find the actual field names, use the inspectPdfFields function below
  const PDF_FIELD_MAPPING: Record<string, string> = {
    // Map our data fields to PDF form field names
    businessName: 'Business Name / Location',
    serviceAddress: 'Service Address',
    city: 'City',
    state: 'State',
    zip: 'Zip',
    locationContactName: 'Location Contact Name',
    locationContactPhone: 'Location Contact Phone',
    locationContactEmail: 'Location Contact Email',
    authorizedPersonName: 'Authorized Person Name',
    authorizedPersonTitle: 'Authorized Person Title',
    authorizedPersonEmail: 'Authorized Person Email',
    authorizedPersonPhone: 'Authorized Person Phone',
    effectiveDate: 'Effective Date',
    // Add more mappings as needed
  };

  // Utility function to inspect PDF form fields (for debugging)
  const inspectPdfFields = async () => {
    try {
      const pdfUrl = '/trialAgreementLocation.pdf';
      const existingPdfBytes = await fetch(pdfUrl).then(res => res.arrayBuffer());
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const form = pdfDoc.getForm();
      
      const fields: string[] = [];
      form.getFields().forEach((field) => {
        const fieldName = field.getName();
        fields.push(fieldName);
      });
      
      console.log('=== PDF Form Fields ===');
      console.log('Available form fields:', fields);
      console.log('Field count:', fields.length);
      fields.forEach((field, index) => {
        console.log(`${index + 1}. "${field}"`);
      });
      console.log('======================');
      
      // Also show in an alert for easy copying
      alert(`Found ${fields.length} form fields:\n\n${fields.join('\n')}\n\nCheck console for details.`);
      
      return fields;
    } catch (error) {
      console.error('Error inspecting PDF fields:', error);
      return [];
    }
  };

  // E-Series service agreement field placements: imported from the shared
  // layout module so they always match the template generated by
  // `scripts/generateServiceAgreement.mjs`.
  const TEXT_PLACEMENTS: Record<string, { x: number; y: number; fontSize?: number; width?: number; height?: number; pageIndex?: number }> = SERVICE_TEXT_PLACEMENTS;

  /**
   * Trial agreement PDF placements. The Trial Agreement template is now
   * structurally identical to the Service Agreement (only the title differs),
   * so both share the same coordinates from the shared layout module.
   */
  const TRIAL_TEXT_PLACEMENTS: Record<string, { x: number; y: number; fontSize?: number; width?: number; height?: number; pageIndex?: number }> = SERVICE_TEXT_PLACEMENTS;

  /** Generate a filled copy of the contract PDF with form data (for preview and for client view). Optionally place TechForce signature on last page. */
  const generateFilledContractPdf = useCallback(async (payload: ContractFormData, contractType: 'service' | 'trial', techForceSignature?: string | null): Promise<string | null> => {
    const pdfUrl = contractType === 'service' ? '/agreementLocation.pdf' : '/trialAgreementLocation.pdf';
    try {
      // Revalidate: nginx serves the template without Cache-Control, and an
      // older cached copy would print text that is now drawn at fill time.
      const existingPdfBytes = await fetch(pdfUrl, { cache: 'no-cache' }).then(res => res.arrayBuffer());
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaBoldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // Monthly products added to the contract continue the list on page 1,
      // and the accessories and one-time products print under its paragraphs;
      // whatever does not fit is carried onto the revision page alongside the
      // addendums. Revision pages are inserted before `getPages()` so no index
      // goes stale.
      const extrasPlan = planContractExtras(
        {
          quantities: payload,
          unitPrices: payload.unitPrices,
          customProducts: payload.customProducts ?? [],
          addendums: payload.addendums ?? [],
        },
        helveticaFont
      );
      const insertedPages = insertContractRevisionPages(
        pdfDoc,
        extrasPlan,
        helveticaFont,
        helveticaBoldFont
      );
      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      const placements = contractType === 'trial' ? TRIAL_TEXT_PLACEMENTS : TEXT_PLACEMENTS;

      const placeText = (key: string, value: string | undefined) => {
        const placement = placements[key];
        if (!placement || !('fontSize' in placement)) return;
        const text = String(value ?? '').trim();
        if (!text) return;
        const rawPageIndex = 'pageIndex' in placement ? (placement as { pageIndex?: number }).pageIndex ?? 0 : 0;
        const page = pages[shiftPageIndex(rawPageIndex, insertedPages)] ?? firstPage;
        page.drawText(text, {
          x: placement.x,
          y: placement.y,
          size: placement.fontSize ?? 10,
          font: helveticaFont,
          color: rgb(0, 0, 0),
        });
      };

      // Client/agreement info is shared by both contract types.
      placeText('effectiveDate', payload.effectiveDate);
      placeText('businessName', payload.businessName);
      placeText('serviceAddress', payload.serviceAddress);
      placeText('cityStateZip', payload.cityStateZip ?? '');
      placeText('locationContactNamePhone', payload.locationContactNamePhone ?? '');
      placeText('locationContactEmail', payload.locationContactEmail);
      placeText('authorizedPersonName', payload.authorizedPersonName);
      placeText('authorizedPersonTitle', payload.authorizedPersonTitle);
      placeText('authorizedPersonEmail', payload.authorizedPersonEmail);
      placeText('authorizedPersonPhone', payload.authorizedPersonPhone);
      placeText('clientName', payload.authorizedPersonName);
      placeText('clientPosition', payload.authorizedPersonTitle);

      if (contractType === 'trial') {
        // Trial keeps the lighter form: term dates + TIM-E + bins + bases +
        // BIM-E + scope. Map onto the new template's quantity lines (bins ->
        // Wheeled Bin, bases -> Base - Metal). BIM-E has no line on the new
        // doc, so it is intentionally not drawn. Scope is drawn below the cost table.
        placeText('agreementTermLength', payload.agreementTermLength);
        placeText('termStartDate', payload.termStartDate);
        placeText('qtyTimEBot', payload.qtyTimEBot);
        placeText('qtyBaseMetalMonthly', payload.qtyBasesTrial);
        placeText('qtyWheeledBinMonthly', payload.qtyBinsTrial);
        placeText('qtyKebbi', payload.qtyKebbi);
        if (payload.scopeText && payload.scopeText.trim()) {
          const scopePage = pages[shiftPageIndex(TRIAL_SCOPE_PLACEMENT.pageIndex, insertedPages)] ?? firstPage;
          drawTrialAgreementScopeText(scopePage, payload.scopeText, helveticaFont);
        }
      } else {
        placeText('agreementTermLength', payload.agreementTermLength);
        placeText('termStartDate', payload.termStartDate);
        placeText('implementationCost', payload.implementationCost);
        placeText('shippingFee', payload.shippingFee);
        placeText('qtyTimEBot', payload.qtyTimEBot);
        placeText('qtyTimECharger', payload.qtyTimECharger);
        placeText('qtyBaseMetalMonthly', payload.qtyBaseMetalMonthly);
        placeText('qtyInsulatedFoodTransportMonthly', payload.qtyInsulatedFoodTransportMonthly);
        placeText('qtyWheeledBinMonthly', payload.qtyWheeledBinMonthly);
        placeText('qtyUniversalPlatformMonthly', payload.qtyUniversalPlatformMonthly);
        placeText('qtyDoorOpenersMonthly', payload.qtyDoorOpenersMonthly);
        placeText('qtyNeuralTechBrainMonthly', payload.qtyNeuralTechBrainMonthly);
        placeText('qtyElevatorHardwareMonthly', payload.qtyElevatorHardwareMonthly);
        placeText('qtyLuggageCartMonthly', payload.qtyLuggageCartMonthly);
        placeText('qtyKebbi', payload.qtyKebbi);
        placeText('monthlyRoboticServiceCost', payload.monthlyRoboticServiceCost);
        // The one-time accessories have no reserved lines on the template; they
        // are itemised by the extras block below.
        placeText('additionalAccessoriesCost', payload.additionalAccessoriesCost);
        placeText('implementationCostPage2', payload.implementationCost);
        placeText('totalMonthlyCost', payload.totalMonthlyCost);
        placeText('implementationCostDue', payload.implementationCostDue);
      }

      // The monthly list's labels and prices, the products added to it, the
      // paragraphs under it, and the itemised accessories.
      const catalogPage = pages[shiftPageIndex(CATALOG_LINES_PAGE_INDEX, insertedPages)];
      if (catalogPage) {
        drawCatalogLines(catalogPage, payload.unitPrices, helveticaFont);
        drawPage1Extras(catalogPage, extrasPlan, helveticaFont, helveticaBoldFont);
      }

      const lastPage = pages[pages.length - 1];
      const sigPlacement = (contractType === 'trial' ? TRIAL_TEXT_PLACEMENTS : TEXT_PLACEMENTS).techforceSignature as { x: number; y: number; width?: number; height?: number; pageIndex?: number } | undefined;
      const signaturePage =
        sigPlacement?.pageIndex !== undefined
          ? pages[shiftPageIndex(sigPlacement.pageIndex, insertedPages)] ?? lastPage
          : lastPage;

      if (signaturePage) {
        drawClientAuthorizationName(signaturePage, helveticaBoldFont, payload.businessName);
      }

      // Second Client signature box — drawn empty here; the client fills it in on the signing page.
      if (payload.additionalSignatureRequested && signaturePage) {
        drawSecondClientSignatureBox(signaturePage, helveticaFont, helveticaBoldFont, payload.businessName);
      }

      if (techForceSignature && sigPlacement && 'width' in sigPlacement && signaturePage) {
        const imageBytes = await fetch(techForceSignature).then((r) => r.arrayBuffer());
        const img = await pdfDoc.embedPng(imageBytes);
        const w = sigPlacement.width ?? 180;
        const h = sigPlacement.height ?? 54;
        signaturePage.drawImage(img, { x: sigPlacement.x, y: sigPlacement.y, width: w, height: h });
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
      return URL.createObjectURL(blob);
    } catch (err) {
      console.error('Error generating filled contract PDF:', err);
      return null;
    }
  }, []);

  /**
   * Draw the TechForce signature onto a PDF that was already filled and edited.
   * Extra revision pages sit in front of the signature page, same as generation.
   */
  const stampSignatureOnEditedPdf = useCallback(async (
    pdfBytes: Uint8Array,
    techForceSignature: string,
    contractType: 'service' | 'trial'
  ): Promise<string | null> => {
    try {
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pages = pdfDoc.getPages();
      const insertedPages = Math.max(0, pages.length - 5);
      const placements = contractType === 'trial' ? TRIAL_TEXT_PLACEMENTS : TEXT_PLACEMENTS;
      const sigPlacement = placements.techforceSignature as { x: number; y: number; width?: number; height?: number; pageIndex?: number };
      const signaturePage = pages[shiftPageIndex(sigPlacement.pageIndex ?? pages.length - 1, insertedPages)] ?? pages[pages.length - 1];
      const imageBytes = await fetch(techForceSignature).then((r) => r.arrayBuffer());
      const img = await pdfDoc.embedPng(imageBytes);
      signaturePage.drawImage(img, {
        x: sigPlacement.x,
        y: sigPlacement.y,
        width: sigPlacement.width ?? 180,
        height: sigPlacement.height ?? 54,
      });
      const saved = await pdfDoc.save();
      return URL.createObjectURL(new Blob([new Uint8Array(saved)], { type: 'application/pdf' }));
    } catch (err) {
      console.error('Error stamping signature on edited contract:', err);
      return null;
    }
  }, []);

  const fillPdfWithData = async (signatureImage?: string | null) => {
    if (!orderData || !apiClient) return null;

    try {
      setIsGeneratingPdf(true);
      
      // Fetch the PDF template
      const pdfUrl = '/trialAgreementLocation.pdf';
      const existingPdfBytes = await fetch(pdfUrl).then(res => res.arrayBuffer());
      
      // Load the PDF
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      
      // Get current date for effective date
      const today = new Date();
      const formattedDate = `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}/${today.getFullYear()}`;
      
      // Count products (robots, bins, bases)
      const robotCount = products.filter(p => p.productName.includes('Robot')).length;
      const binCount = products.filter(p => p.productName.includes('Bin')).length;
      const baseCount = products.filter(p => p.productName.includes('Base')).length;
      
      // Parse addresses
      const parseAddress = (address: string) => {
        if (!address) return { street: '', city: '', state: '', zip: '' };
        const lines = address.split('\n');
        const street = lines[0] || '';
        const cityStateZip = lines[1] || '';
        const match = cityStateZip.match(/(.+?),\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?)/);
        if (match) {
          return {
            street,
            city: match[1].trim(),
            state: match[2].trim(),
            zip: match[3].trim()
          };
        }
        return { street, city: '', state: '', zip: '' };
      };
      
      // Use placeholder addresses or parse from available data
      const serviceAddress = parseAddress(siteLocation || shippingAddress || '');
      const billingAddr = parseAddress(shippingAddress || '');
      
      // Get pages (signature is typically on the last page)
      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      const lastPage = pages[pages.length - 1]; // Use last page for signature
      
      // Load a font
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaBoldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      
      // Helper function to place text on PDF
      const placeText = (key: string, text: string, page = firstPage) => {
        const placement = TEXT_PLACEMENTS[key];
        if (!placement) {
          console.warn(`No placement coordinates found for: ${key}`);
          return false;
        }
        
        try {
          page.drawText(text, {
            x: placement.x,
            y: placement.y,
            size: placement.fontSize || 10,
            font: helveticaFont,
            color: rgb(0, 0, 0),
          });
          console.log(`✓ Placed "${key}" at (${placement.x}, ${placement.y}): ${text}`);
          return true;
        } catch (e) {
          console.error(`Error placing text for "${key}":`, e);
          return false;
        }
      };
      
      // Try to fill form fields first (if any exist)
      try {
        const form = pdfDoc.getForm();
        const fields = form.getFields();
        console.log(`Found ${fields.length} form fields, attempting to fill...`);
        
        // Try filling the ENVELOPEID field if it exists (though it's not useful)
        // This is just to show the form field approach still works
      } catch (e) {
        console.log('No form fields found or form not accessible, using text placement only');
      }
      
      // Place text on the PDF using coordinates
      console.log('=== Placing Text on PDF ===');
      placeText('effectiveDate', formattedDate);
      placeText('businessName', orderData.companyName);
      placeText('serviceAddress', serviceAddress.street);
      placeText('cityStateZip', `${serviceAddress.city}, ${serviceAddress.state} ${serviceAddress.zip}`);
      placeText('locationContactNamePhone', `${apiClient.point_of_contact} ${apiClient.contact_phone || ''}`);
      placeText('locationContactEmail', apiClient.contact_email || '');
      placeText('authorizedPersonName', apiClient.point_of_contact);
      placeText('authorizedPersonEmail', apiClient.contact_email || '');
      placeText('authorizedPersonPhone', apiClient.contact_phone || '');
      console.log('=== Finished Placing Text ===');
      
      // Place signature image if provided
      if (signatureImage) {
        try {
          console.log('=== Placing Signature on PDF ===');
          const signaturePlacement = TEXT_PLACEMENTS.clientSignature;
          
          // Get page dimensions for reference
          const pageSize = lastPage.getSize();
          console.log(`Page dimensions: ${pageSize.width} × ${pageSize.height} points`);
          
          // Convert data URL to image bytes
          const imageBytes = await fetch(signatureImage).then(res => res.arrayBuffer());
          const signaturePdfImage = await pdfDoc.embedPng(imageBytes);
          
          // Get signature dimensions
          const sigWidth = signaturePlacement.width || 200;
          const sigHeight = signaturePlacement.height || 60;
          
          // Place signature on the last page (where signature section typically is)
          // Using exact coordinates from TEXT_PLACEMENTS (no offset)
          lastPage.drawImage(signaturePdfImage, {
            x: signaturePlacement.x,
            y: signaturePlacement.y,
            width: sigWidth,
            height: sigHeight,
          });
          
          console.log(`✓ Placed signature at (${signaturePlacement.x}, ${signaturePlacement.y})`);
          console.log(`  Signature size: ${sigWidth} × ${sigHeight} points`);
          console.log('=== Finished Placing Signature ===');
        } catch (error) {
          console.error('Error placing signature on PDF:', error);
        }
      }
      
      // Generate PDF bytes
      const pdfBytes = await pdfDoc.save();
      
      // Create blob URL
      const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      setIsGeneratingPdf(false);
      return url;
    } catch (error) {
      console.error('Error filling PDF:', error);
      setIsGeneratingPdf(false);
      return null;
    }
  };

  const handleGenerateContract = () => {
    setContractModalStep('choose');
    setSelectedContractType(null);
    setContractFormData(getDefaultContractFormData());
    setContractLink('');
    setIsContractModalOpen(true);
    if (signatureCanvasRef.current) {
      const ctx = signatureCanvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, signatureCanvasRef.current.width, signatureCanvasRef.current.height);
      }
    }
  };

  const loadSurveyLinks = useCallback(async () => {
    if (!orderNumber) return;
    setSurveyLinksLoading(true);
    try {
      setSurveyLinks(await fetchOrderElevatorSurveyLinks(orderNumber));
    } catch {
      setSurveyLinks([]);
    } finally {
      setSurveyLinksLoading(false);
    }
  }, [orderNumber]);

  const openSurveyModal = () => {
    setSurveyEmails(apiClient?.contact_email || '');
    setSurveySent(false);
    setSurveyError('');
    setSurveyLinkCopied(false);
    setSurveyModalOpen(true);
    void loadSurveyLinks();
  };

  /** The link a client can still open: not retired and not answered yet. */
  const activeSurveyLink = surveyLinks.find((s) => !s.revoked_at && s.status === 'Sent') ?? null;

  const handleGenerateSurveyLink = async () => {
    if (!orderNumber) return;
    setSurveyLinkBusy(true);
    setSurveyError('');
    setSurveyLinkCopied(false);
    try {
      const created = await generateOrderElevatorSurveyLink(orderNumber, user?.id);
      await loadSurveyLinks();
      addActivityLog('Generated a new elevator initial survey link', employee || 'System');
      await navigator.clipboard?.writeText(created.link).catch(() => undefined);
      setSurveyLinkCopied(true);
    } catch (err) {
      setSurveyError((err as Error).message || 'Failed to generate survey link.');
    } finally {
      setSurveyLinkBusy(false);
    }
  };

  const handleRevokeSurveyLink = async (surveyId: number) => {
    setSurveyLinkBusy(true);
    setSurveyError('');
    try {
      await revokeElevatorSurveyLink(surveyId, user?.id);
      await loadSurveyLinks();
      addActivityLog('Disabled an elevator initial survey link', employee || 'System');
    } catch (err) {
      setSurveyError((err as Error).message || 'Failed to disable survey link.');
    } finally {
      setSurveyLinkBusy(false);
    }
  };

  const handleCopySurveyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setSurveyLinkCopied(true);
    } catch {
      setSurveyError('Could not copy the link. Select it and copy manually.');
    }
  };

  const handleSendSurvey = async () => {
    if (!orderNumber) return;
    const recipients = surveyEmails
      .split(/[,\n;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (recipients.length === 0) {
      setSurveyError('Please enter at least one recipient email.');
      return;
    }
    setSurveySending(true);
    setSurveyError('');
    try {
      await sendElevatorSurvey(orderNumber, { recipients, user_id: user?.id });
      setSurveySent(true);
      addActivityLog(`Elevator initial survey sent to ${recipients.join(', ')}`, employee || 'System');
      void loadSurveyLinks();
    } catch (err) {
      setSurveyError((err as Error).message || 'Failed to send survey.');
    } finally {
      setSurveySending(false);
    }
  };

  const pilotTermTodayYmd = new Date().toLocaleDateString('en-CA', { timeZone: APP_TIME_ZONE });

  const openPilotTermModal = () => {
    setPilotTermDaysInput(orderData?.pilotTermDays != null ? String(orderData.pilotTermDays) : '');
    setPilotTermStartInput(orderData?.pilotTermStartDate || pilotTermTodayYmd);
    setPilotTermError('');
    setPilotTermModalOpen(true);
  };

  const pilotTermDaysValue = /^\d+$/.test(pilotTermDaysInput.trim()) && Number(pilotTermDaysInput) > 0
    ? Number(pilotTermDaysInput)
    : null;
  const pilotTermStartValue = /^\d{4}-\d{2}-\d{2}$/.test(pilotTermStartInput) ? pilotTermStartInput : null;
  const pilotTermPreviewEnd =
    pilotTermDaysValue != null && pilotTermStartValue ? addDaysToYmd(pilotTermStartValue, pilotTermDaysValue) : null;

  const handleSavePilotTerm = async () => {
    if (!orderNumber || pilotTermDaysValue == null || pilotTermStartValue == null || pilotTermSaving) return;
    const previous =
      orderData?.pilotTermDays != null && orderData.pilotTermStartDate
        ? { days: orderData.pilotTermDays, start: orderData.pilotTermStartDate, end: orderData.pilotTermEndDate }
        : null;
    if (previous && previous.days === pilotTermDaysValue && previous.start === pilotTermStartValue) {
      setPilotTermModalOpen(false);
      return;
    }
    setPilotTermSaving(true);
    setPilotTermError('');
    try {
      const term = await setOrderPilotTerm(orderNumber, pilotTermDaysValue, pilotTermStartValue);
      setOrderData((prev) =>
        prev
          ? {
              ...prev,
              pilotTermDays: term.pilot_term_days,
              pilotTermStartDate: term.pilot_term_start_date,
              pilotTermEndDate: term.pilot_term_end_date,
            }
          : prev
      );
      setPilotTermModalOpen(false);
      const describe = (days: number, start: string | null, end: string | null) =>
        `${days} day${days === 1 ? '' : 's'}, ${start ? formatTaskDate(start) : 'no start date'} to ${end ? formatTaskDate(end) : 'no end date'}`;
      const now = describe(term.pilot_term_days ?? pilotTermDaysValue, term.pilot_term_start_date, term.pilot_term_end_date);
      addActivityLog(
        previous
          ? `Pilot term changed to ${now} (was ${describe(previous.days, previous.start, previous.end)})`
          : `Pilot term set to ${now}`,
        employee || 'System'
      );
    } catch (err) {
      setPilotTermError((err as Error).message || 'Failed to set the term period.');
    } finally {
      setPilotTermSaving(false);
    }
  };

  const handleSelectContractType = (type: 'service' | 'trial') => {
    setSelectedContractType(type);
    setContractFormData(getDefaultContractFormData());
    setContractModalStep('form');
  };

  const handleContractFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setContractModalStep(selectedContractType === 'trial' ? 'inventory_trial' : 'form2');
  };

  const handleContractForm2Submit = (e: React.FormEvent) => {
    e.preventDefault();
    setContractModalStep('revisions');
  };

  const handleContractInventoryTrialSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setContractModalStep('scope');
  };

  const handleContractScopeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setContractModalStep('revisions');
  };

  const handleContractRevisionsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setContractModalStep('signature');
  };

  /** Products the team added on top of the pre-printed catalog lines. */
  const handleAddContractProduct = (product: ContractCustomProduct) => {
    setContractFormData((prev) => ({
      ...prev,
      customProducts: [...(prev.customProducts ?? []), product],
    }));
    setContractProductSection(null);
  };

  const handleRemoveContractProduct = (id: string) => {
    setContractFormData((prev) => ({
      ...prev,
      customProducts: (prev.customProducts ?? []).filter((item) => item.id !== id),
    }));
  };

  const handleContractProductQtyChange = (id: string, qty: string) => {
    setContractFormData((prev) => ({
      ...prev,
      customProducts: (prev.customProducts ?? []).map((item) =>
        item.id === id ? { ...item, qty } : item
      ),
    }));
  };

  /** Revisions — addendums are stored as one entry per typed clause. */
  const contractAddendums = contractFormData.addendums ?? [];

  const setContractAddendums = (next: string[]) => {
    setContractFormData((prev) => ({ ...prev, addendums: next }));
  };

  const handleAddAddendum = () => {
    setContractAddendums([...contractAddendums, '']);
  };

  const handleAddendumChange = (index: number, text: string) => {
    setContractAddendums(contractAddendums.map((entry, idx) => (idx === index ? text : entry)));
  };

  const handleRemoveAddendum = (index: number) => {
    setContractAddendums(contractAddendums.filter((_, idx) => idx !== index));
  };

  /**
   * Quantity inputs for the custom products in one contract section, plus the
   * button that opens the add-product dialog. Rendered inside the same grid as
   * the pre-printed catalog lines so a custom product behaves like the rest.
   */
  const renderContractProductFields = (section: ContractProductSection) => {
    const items = contractCustomProducts.filter((item) => item.section === section);
    return (
      <>
        {items.map((item) => (
          <div className="contract-form-group contract-custom-product" key={item.id}>
            <label className="form-label">
              {item.name} (${item.unitPrice.toLocaleString()}
              {section === 'accessory' ? ' each, one-time' : ' each'})
              <button
                type="button"
                className="contract-custom-product-remove"
                onClick={() => handleRemoveContractProduct(item.id)}
                aria-label={`Remove ${item.name} from the contract`}
                title="Remove from contract"
              >
                ×
              </button>
            </label>
            <input
              type="text"
              className="form-input form-input-qty"
              value={item.qty}
              onChange={(e) => handleContractProductQtyChange(item.id, e.target.value)}
              placeholder="Qty"
            />
            {item.addToInventory && (
              <span className="contract-custom-product-tag">Will be added to inventory</span>
            )}
          </div>
        ))}
        <div className="contract-form-group contract-custom-product-add">
          <button
            type="button"
            className="contract-add-product-button"
            onClick={() => setContractProductSection(section)}
          >
            + Add product
          </button>
        </div>
      </>
    );
  };

  /**
   * Quantity inputs for one catalog section. Labels carry the price that is
   * actually being charged, so a repriced line reads correctly here and on the
   * PDF without the two drifting apart.
   */
  const renderCatalogQtyFields = (section: ContractProductSection) => (
    <>
      {CATALOG_ROWS[section].map((row) => (
        <div className="contract-form-group" key={row.key}>
          <label className="form-label" htmlFor={`contract-${row.key}`}>
            {catalogRowLabel(row, contractUnitPrices)}
          </label>
          <input
            type="text"
            id={`contract-${row.key}`}
            className="form-input form-input-qty"
            value={(contractFormData[row.key as keyof ContractFormData] as string) ?? ''}
            onChange={(e) => setContractFormData(prev => ({ ...prev, [row.key]: e.target.value }))}
            placeholder="Qty"
          />
        </div>
      ))}
      {renderContractProductFields(section)}
    </>
  );

  /** Heading for a catalog section, with the button that reprices it. */
  const renderCatalogSectionTitle = (section: ContractProductSection, title: string) => (
    <div className="contract-form-section-header">
      <h4 className="contract-form-section-title">{title}</h4>
      <button
        type="button"
        className="contract-edit-prices-button"
        onClick={() => setContractPriceSection(section)}
      >
        Edit prices
      </button>
    </div>
  );

  /**
   * Upload flow for a contract that was completed outside the portal. It is
   * filed exactly as uploaded — already signed, so there is no form to fill in
   * and no signing link to send.
   */
  const handleStartContractUpload = () => {
    setSelectedContractType(null);
    setUploadFile(null);
    setUploadError('');
    setUploadContractType('service');
    setContractModalStep('upload');
  };

  const handleContractFileChosen = (file: File | null) => {
    setUploadError('');
    if (!file) {
      setUploadFile(null);
      return;
    }
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setUploadFile(null);
      setUploadError('Only PDF files can be uploaded.');
      return;
    }
    if (file.size > MAX_CONTRACT_UPLOAD_BYTES) {
      setUploadFile(null);
      setUploadError(`That file is ${formatFileSize(file.size)}. The limit is 20 MB.`);
      return;
    }
    setUploadFile(file);
  };

  const handleUploadContract = async () => {
    if (!uploadFile || !orderNumber || isUploadingContract) return;
    setIsUploadingContract(true);
    setUploadError('');
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
        reader.onerror = () => reject(new Error('Could not read the file.'));
        reader.readAsDataURL(uploadFile);
      });
      const res = await fetch(`${API_BASE}/api/contracts/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_number: orderNumber,
          contract_type: uploadContractType,
          pdf_base64: base64,
          file_name: uploadFile.name,
          user_id: user?.id,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to upload the contract.');
      await fetchOrderContracts();
      if (data.order_status === 'Approved') {
        setStatus('Approved');
        setEditStatus('Approved');
        setOrderData((prev) => (prev ? { ...prev, status: 'Approved' } : prev));
      }
      addActivityLog('Uploaded a completed contract', employee || user?.username || 'System');
      setUploadFile(null);
      closeContractModal();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Failed to upload the contract.');
    } finally {
      setIsUploadingContract(false);
    }
  };

  const fetchOrderContracts = useCallback(async () => {
    if (!orderNumber) return;
    setContractsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/contracts?order_number=${encodeURIComponent(orderNumber)}`);
      if (!res.ok) {
        setOrderContracts([]);
        return;
      }
      const data = await res.json();
      setOrderContracts(data.contracts || []);
    } catch {
      setOrderContracts([]);
    } finally {
      setContractsLoading(false);
    }
  }, [orderNumber]);

  useEffect(() => {
    if (orderNumber && (stage === 'Contract' || stage === 'Delivery' || stage === 'Installation')) {
      fetchOrderContracts();
    }
  }, [orderNumber, stage, fetchOrderContracts]);

  const fetchInvoicesForOrder = useCallback(async () => {
    if (!orderNumber) return;
    setOrderInvoicesLoading(true);
    try {
      const list = await fetchOrderInvoices(orderNumber);
      setOrderInvoices(list);
    } catch {
      setOrderInvoices([]);
    } finally {
      setOrderInvoicesLoading(false);
    }
  }, [orderNumber]);

  useEffect(() => {
    if (orderNumber) fetchInvoicesForOrder();
  }, [orderNumber, fetchInvoicesForOrder]);

  // No contracts: clear contract-derived product table and shipping
  useEffect(() => {
    if (orderContracts.length === 0) {
      setOrderProductsFromContract([]);
      if (stage !== 'Delivery' && stage !== 'Installation') setInvoiceStageShippingFromContract('');
    }
  }, [orderContracts.length, stage]);

  // Contract stage: load products from most recently created contract
  useEffect(() => {
    if (stage !== 'Contract' || !orderNumber || orderContracts.length === 0) return;
    const byCreated = [...orderContracts].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    const latest = byCreated[0];
    if (!latest) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/contracts/${latest.id}/form-data`);
        if (!res.ok || cancelled) return;
        const formData = await res.json();
        const fd = (formData.form_data || {}) as Record<string, string>;
        const built = buildOrderProductsFromContractForm(orderNumber, fd);
        if (!cancelled) setOrderProductsFromContract(built);
      } catch {
        if (!cancelled) setOrderProductsFromContract([]);
      }
    })();
    return () => { cancelled = true; };
  }, [orderNumber, stage, orderContracts]);

  // Invoice/Installation stage: load shipping address and products from latest signed contract
  useEffect(() => {
    if ((stage !== 'Delivery' && stage !== 'Installation') || !orderNumber || orderContracts.length === 0) {
      if (stage !== 'Delivery' && stage !== 'Installation') {
        setInvoiceStageShippingFromContract('');
      }
      return;
    }
    const signed = orderContracts.filter((c) => c.status === 'signed').sort((a, b) => (new Date(b.signed_at || 0).getTime() - new Date(a.signed_at || 0).getTime()));
    const latest = signed[0];
    if (!latest) {
      setInvoiceStageShippingFromContract('');
      setOrderProductsFromContract([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/contracts/${latest.id}/form-data`);
        if (!res.ok || cancelled) return;
        const formData = await res.json();
        const fd = (formData.form_data || {}) as Record<string, string>;
        const serviceAddr = (fd.serviceAddress ?? fd.service_address ?? '').trim();
        const city = (fd.city ?? '').trim();
        const state = (fd.state ?? '').trim();
        const zip = (fd.zip ?? '').trim();
        const cityStateZipCombined = (fd.cityStateZip ?? fd.city_state_zip ?? '').trim();
        const cityStateZip = cityStateZipCombined || [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
        const shipping = [serviceAddr, cityStateZip].filter(Boolean).join('\n');
        const built = buildOrderProductsFromContractForm(orderNumber, fd);
        if (!cancelled) {
          setInvoiceStageShippingFromContract(shipping);
          setOrderProductsFromContract(built);
        }
      } catch {
        if (!cancelled) {
          setInvoiceStageShippingFromContract('');
          setOrderProductsFromContract([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [orderNumber, stage, orderContracts]);

  // Order line items (products) are not loaded from static data; use contract form or leave empty

  // Contract stage: derive status from contracts (no live contracts → Pending;
  // has one but none signed → In Progress; any signed → Approved). Voided
  // contracts are retired, so they don't hold the order in progress.
  // Pilot is set by staff and stays until they choose another status.
  useEffect(() => {
    if (stage !== 'Contract') return;
    if (orderData?.status === 'Pilot') return;
    const live = orderContracts.filter((c) => c.status !== 'void');
    if (live.length === 0) {
      setStatus('Pending');
      return;
    }
    const hasSigned = live.some((c) => c.status === 'signed');
    setStatus(hasSigned ? 'Approved' : 'In Progress');
  }, [stage, orderContracts, orderData?.status]);

  // "Client submitted signed contract" is logged only on the server when the client signs via the link (PATCH /api/contracts/:contractId/signed), not when the portal loads the order.

  /**
   * Copy in the same synchronous turn as the click (Safari requires a user gesture; await/fetch before copy breaks it).
   * Tries execCommand first, then Clipboard API.
   */
  const copyTextToClipboard = (text: string): boolean => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText =
        'position:fixed;left:0;top:0;width:2px;height:2px;padding:0;margin:0;border:none;outline:none;box-shadow:none;background:transparent;opacity:0;';
      ta.setAttribute('aria-hidden', 'true');
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) return true;
    } catch {
      /* fall through */
    }
    return false;
  };

  const fetchShareableContractLink = async (c: ContractRow): Promise<string> => {
    try {
      const res = await fetch(`${API_BASE}/api/contracts/${c.id}/link`);
      if (res.ok) {
        const data = (await res.json()) as { link?: string };
        if (data.link && /^https?:\/\//i.test(data.link)) return data.link;
      }
    } catch {
      /* fall through to client-side fallback */
    }
    return `${window.location.origin}/contract/${c.contract_id}`;
  };

  const handleCopyContractLink = async (c: ContractRow) => {
    const link = await fetchShareableContractLink(c);
    if (copyTextToClipboard(link)) {
      alert('Link copied to clipboard.');
      return;
    }
    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(link).then(
        () => alert('Link copied to clipboard.'),
        () => {
          window.prompt('Copy this link:', link);
        }
      );
      return;
    }
    window.prompt('Copy this link:', link);
  };

  const handleConfirmDeleteContract = async () => {
    if (!contractToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/api/contracts/${contractToDelete.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert((err as { error?: string }).error || 'Failed to delete contract.');
        return;
      }
      setContractToDelete(null);
      addActivityLog('Contract deleted', employee || 'System');
      fetchOrderContracts();
    } catch {
      alert('Failed to delete contract.');
    }
  };

  /**
   * Voiding retires a contract that is not going to be used — typically one
   * generated by mistake and replaced by another. The row and its PDF stay,
   * but the follow-up reminders stop and the client can no longer sign it.
   */
  const handleConfirmVoidContract = async () => {
    if (!contractToVoid || contractVoidSubmitting) return;
    setContractVoidSubmitting(true);
    setContractVoidError('');
    try {
      const res = await fetch(`${API_BASE}/api/contracts/${contractToVoid.id}/void`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voided: true, user_id: user?.id, reason: contractVoidReason.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to void the contract.');
      setContractToVoid(null);
      setContractVoidReason('');
      addActivityLog('Contract voided', employee || 'System');
      await fetchOrderContracts();
    } catch (err) {
      setContractVoidError(err instanceof Error ? err.message : 'Failed to void the contract.');
    } finally {
      setContractVoidSubmitting(false);
    }
  };

  const handleRestoreContract = async (contract: ContractRow) => {
    try {
      const res = await fetch(`${API_BASE}/api/contracts/${contract.id}/void`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voided: false, user_id: user?.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to restore the contract.');
      addActivityLog('Contract restored', employee || 'System');
      await fetchOrderContracts();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to restore the contract.');
    }
  };

  /**
   * One row of a contracts table. Every stage renders the same contract data;
   * only the trailing actions differ, so they are opted into per table.
   */
  const renderContractRow = (c: ContractRow, opts: { survey?: boolean; deletable?: boolean }) => {
    // An uploaded contract arrives already signed, so it has no client signing
    // link to copy or email. A voided one has been retired, same result.
    const isUploaded = c.source === 'uploaded';
    const isVoided = c.status === 'void';
    const typeLabel = c.contract_type === 'trial' ? 'Trial' : 'Service';
    return (
      <tr key={c.id} className={isVoided ? 'contract-row--void' : undefined}>
        <td>
          {typeLabel}
          {isUploaded ? (
            <span
              className="contract-source-pill"
              title={c.uploaded_file_name ? `Uploaded file: ${c.uploaded_file_name}` : 'Uploaded'}
            >
              Uploaded
            </span>
          ) : null}
        </td>
        <td>
          <span className={`contract-status contract-status-${c.status}`}>
            {c.status === 'signed' ? 'Signed' : isVoided ? 'Void' : 'Pending'}
          </span>
          {isVoided ? (
            <div className="contract-void-note">
              {c.void_reason ? c.void_reason : 'No reason given'}
              {c.voided_by ? ` — ${c.voided_by}` : ''}
            </div>
          ) : null}
        </td>
        <td>{formatDateTimePT(c.generated_at)}</td>
        <td>{formatDateTimePT(c.signed_at)}</td>
        <td>
          {isUploaded || isVoided ? (
            <span className="contracts-table-muted">—</span>
          ) : (
            <button type="button" className="contract-copy-link-button" onClick={() => handleCopyContractLink(c)}>
              Copy link
            </button>
          )}
        </td>
        <td className="contracts-table-actions">
          <button
            type="button"
            className="contract-view-pdf-button"
            onClick={() => window.open(`${API_BASE}/api/contracts/${c.id}/pdf`, '_blank')}
            aria-label="View PDF"
            title="View PDF"
          >
            <FontAwesomeIcon icon={faFilePdf} />
          </button>
          {!isUploaded && !isVoided ? (
            <button
              type="button"
              className="contract-resend-email-button"
              onClick={() => handleResendContractEmail(c)}
              aria-label="Send email again"
              title="Send email again"
            >
              <FontAwesomeIcon icon={faEnvelope} />
            </button>
          ) : null}
          {opts.survey ? (
            <button
              type="button"
              className="contract-send-survey-button"
              onClick={openSurveyModal}
              aria-label="Send initial survey"
              title="Send initial survey"
            >
              <FontAwesomeIcon icon={faClipboardList} />
            </button>
          ) : null}
          {isVoided ? (
            <button
              type="button"
              className="contract-restore-button"
              onClick={() => void handleRestoreContract(c)}
              aria-label="Restore contract"
              title="Restore contract"
            >
              <FontAwesomeIcon icon={faRotateLeft} />
            </button>
          ) : c.status !== 'signed' ? (
            <button
              type="button"
              className="contract-void-button"
              onClick={() => {
                setContractVoidReason('');
                setContractVoidError('');
                setContractToVoid(c);
              }}
              aria-label="Void contract"
              title="Void contract — stops follow-up reminders"
            >
              <FontAwesomeIcon icon={faBan} />
            </button>
          ) : null}
          {opts.deletable ? (
            <button
              type="button"
              className="contract-delete-button"
              onClick={() => setContractToDelete({ id: c.id, label: `${typeLabel} contract` })}
              aria-label="Delete contract"
              title="Delete contract"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          ) : null}
        </td>
      </tr>
    );
  };

  const openVoidOrderModal = () => {
    setVoidReasonInput('');
    setVoidOrderError('');
    setVoidOrderModalOpen(true);
  };

  const closeVoidOrderModal = () => {
    if (voidOrderSubmitting) return;
    setVoidOrderModalOpen(false);
    setVoidOrderError('');
  };

  const handleVoidOrder = async () => {
    if (!orderNumber) return;
    const reason = voidReasonInput.trim();
    if (!reason) {
      setVoidOrderError('Please enter a reason for voiding this order.');
      return;
    }
    setVoidOrderSubmitting(true);
    setVoidOrderError('');
    try {
      const updated = await voidOrder(orderNumber, reason, employee || 'System');
      setOrderData((prev) => (prev ? { ...prev, isVoided: true, voidReason: updated.void_reason ?? reason } : prev));
      addActivityLog(`Order voided — ${reason}`, employee || 'System');
      setVoidOrderModalOpen(false);
      setVoidReasonInput('');
    } catch (err) {
      setVoidOrderError(err instanceof Error ? err.message : 'Failed to void order.');
    } finally {
      setVoidOrderSubmitting(false);
    }
  };

  const handleUnvoidOrder = async () => {
    if (!orderNumber) return;
    setVoidOrderSubmitting(true);
    try {
      await unvoidOrder(orderNumber, employee || 'System');
      setOrderData((prev) => (prev ? { ...prev, isVoided: false, voidReason: null } : prev));
      addActivityLog('Order unvoided', employee || 'System');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to unvoid order.');
    } finally {
      setVoidOrderSubmitting(false);
    }
  };

  const buildContractPdfPayload = () => {
    const locationContactNamePhone = `${contractFormData.locationContactName || ''} ${contractFormData.locationContactPhone || ''}`.trim();
    const stateZip = [contractFormData.state, contractFormData.zip].filter(Boolean).join(' ');
    const cityStateZip = [contractFormData.city, stateZip].filter(Boolean).join(', ');
    return {
      ...contractFormData,
      locationContactNamePhone,
      cityStateZip,
      monthlyRoboticServiceCost: String(discountedMonthlyRoboticServiceCost),
      additionalAccessoriesCost: String(discountedAdditionalAccessoriesCost),
      totalMonthlyCost: String(computedTotalMonthlyCost),
      implementationCostDue: String(computedImplementationCostDue),
    };
  };

  const openContractPdfEditor = async () => {
    if (!selectedContractType) return;
    setIsOpeningContractEditor(true);
    try {
      let bytes = contractPdfBase;
      if (!bytes) {
        const url = await generateFilledContractPdf(buildContractPdfPayload(), selectedContractType, null);
        if (!url) {
          alert('Could not open the contract for editing. Please try again.');
          return;
        }
        const response = await fetch(url);
        bytes = new Uint8Array(await response.arrayBuffer());
        URL.revokeObjectURL(url);
      }
      setContractEditorBytes(bytes);
      setIsContractModalFullscreen(true);
      setContractModalStep('edit-pdf');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not open the contract for editing.');
    } finally {
      setIsOpeningContractEditor(false);
    }
  };

  const handleContractSignatureSubmit = async () => {
    if (!selectedContractType || !orderNumber || !signature) return;
    const payload = {
      ...buildContractPdfPayload(),
      techForceSignature: signature ?? undefined,
    };
    const contractId = (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : (() => {
      const array = new Uint8Array(16);
      crypto.getRandomValues(array);
      return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    })()).replace(/-/g, '');
    /** Short client URL — form data is loaded from the server (avoids huge hash / Safari clipboard limits). The canonical link is returned by the POST response (built from APP_URL on the server) so it works even when the team member's browser origin differs from the public app URL. */
    const fallbackClientLink = `${window.location.origin}/contract/${contractId}`;

    setIsGeneratingContractPdf(true);
    setGeneratedContractPdfUrl(null);
    const blobUrl = editedContractPdf
      ? await stampSignatureOnEditedPdf(editedContractPdf, signature, selectedContractType)
      : await generateFilledContractPdf(payload, selectedContractType, signature);
    setIsGeneratingContractPdf(false);

    if (!blobUrl) {
      alert('Could not generate the contract PDF. Please try again.');
      return;
    }
    setGeneratedContractPdfUrl(blobUrl);

    try {
      const res = await fetch(blobUrl);
      const blob = await res.blob();
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          resolve(dataUrl.split(',')[1] || '');
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const saveRes = await fetch(`${API_BASE}/api/contracts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_number: orderNumber,
          contract_id: contractId,
          form_data: payload,
          contract_type: selectedContractType,
          pdf_generated: base64,
        }),
      });
      if (!saveRes.ok) {
        const errBody = await saveRes.json().catch(() => ({}));
        const msg = (errBody as { error?: string }).error || saveRes.statusText || 'Failed to save contract.';
        alert(msg);
        return;
      }
      const saveBody = (await saveRes.json().catch(() => ({}))) as { link?: string };
      const canonicalLink = saveBody.link && /^https?:\/\//i.test(saveBody.link) ? saveBody.link : fallbackClientLink;
      setContractLink(canonicalLink);
      setGeneratedContractDbContractId(contractId);

      // Pre-populate email recipients from form data; billing@techforcerobotics.com is always included.
      const defaultRecipients: string[] = ['billing@techforcerobotics.com'];
      const locEmail = (contractFormData.locationContactEmail || '').trim();
      const authEmail = (contractFormData.authorizedPersonEmail || '').trim();
      const seen = new Set(defaultRecipients.map((r) => r.toLowerCase()));
      for (const e of [locEmail, authEmail]) {
        const lower = e.toLowerCase();
        if (e && !seen.has(lower)) {
          defaultRecipients.push(e);
          seen.add(lower);
        }
      }
      setEmailRecipients(defaultRecipients);
      setEmailRecipientInput('');
      setContractEmailSent(false);
      setCoiAttached(true);
      setW9Attached(true);
      setExtraEmailFiles([]);
      setIncludeElevatorSurvey(true);

      const greetingName = (contractFormData.authorizedPersonName || '').trim() || (contractFormData.locationContactName || '').trim();
      const businessName = (contractFormData.businessName || orderData?.companyName || '').trim();
      const contractTypeLabel = selectedContractType === 'trial' ? 'Trial' : 'Service';
      const subjectClient = businessName || orderData?.companyName || '';
      setEmailSubject(
        `TechForce Robotics ${contractTypeLabel} Agreement${subjectClient ? ` – ${subjectClient}` : ''}${orderNumber ? ` (Order ${orderNumber})` : ''}`
      );
      setEmailBody(
        `Hello${greetingName ? ` ${greetingName}` : ''},

Thank you for choosing TechForce Robotics. Please review and sign your ${contractTypeLabel.toLowerCase()} agreement using the secure link below:

${canonicalLink}

Once you complete the form and submit, our team will receive a fully signed copy automatically.

If you have any questions, simply reply to this email or contact us at billing@techforcerobotics.com.

Best regards,
TechForce Robotics`
      );

      // Write-ins the user asked to keep are filed in the catalog now that the
      // contract itself is saved. A failure here must not lose the contract.
      const productsToFile = (payload.customProducts ?? []).filter(
        (item) => item.addToInventory && item.name.trim()
      );
      if (productsToFile.length > 0) {
        try {
          const { created } = await ensureInventoryProducts(
            productsToFile.map((item) => ({
              name: item.name.trim(),
              type: item.inventoryType ?? 'Stock Item',
              replacementCostPerItem: item.unitPrice ? `$${item.unitPrice.toLocaleString()}` : '',
            }))
          );
          if (created.length > 0) {
            await refreshProducts();
            addActivityLog(
              `Added to inventory catalog from contract: ${created.map((p) => p.name).join(', ')}`,
              employee || 'System'
            );
          }
        } catch (err) {
          console.error('Failed to add custom contract products to the inventory catalog:', err);
          alert('The contract was saved, but the new products could not be added to the inventory catalog. Add them from the Inventory tab.');
        }
      }

      setContractModalStep('email');
      addActivityLog('Contract generated', employee || 'System');
      fetchOrderContracts();
    } catch (err) {
      console.error('Failed to save contract to database:', err);
      alert('Failed to save contract to database. Check the console and ensure the server is running.');
    }
  };

  const closeContractModal = () => {
    if (generatedContractPdfUrl && generatedContractPdfUrl.startsWith('blob:')) {
      URL.revokeObjectURL(generatedContractPdfUrl);
    }
    setGeneratedContractPdfUrl(null);
    setIsContractModalOpen(false);
    setContractModalStep('choose');
    setSelectedContractType(null);
    setIsContractModalFullscreen(false);
    setContractProductSection(null);
    setSignature(null);
    setEmailRecipients([]);
    setEmailRecipientInput('');
    setEmailSubject('');
    setEmailBody('');
    setIsSendingContractEmail(false);
    setContractEmailSent(false);
    setCoiAttached(true);
    setW9Attached(true);
    setExtraEmailFiles([]);
    setIncludeElevatorSurvey(true);
    setGeneratedContractDbContractId('');
    setEditedContractPdf(null);
    setContractPdfBase(null);
    setContractPdfModel(null);
    setContractEditorBytes(null);
    setUploadFile(null);
    setUploadError('');
    setIsUploadingContract(false);
  };

  useEffect(() => {
    if (['form2', 'inventory_trial', 'scope', 'revisions', 'edit-pdf', 'signature', 'email'].includes(contractModalStep)) {
      contractModalBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [contractModalStep]);

  // Cleanup blob URL on unmount or modal close
  useEffect(() => {
    return () => {
      if (filledPdfUrl && filledPdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(filledPdfUrl);
      }
    };
  }, [filledPdfUrl]);

  const handleCopyLink = () => {
    if (!contractLink) return;
    if (copyTextToClipboard(contractLink)) {
      alert('Contract link copied to clipboard!');
      return;
    }
    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(contractLink).then(
        () => alert('Contract link copied to clipboard!'),
        () => {
          window.prompt('Copy this link:', contractLink);
        }
      );
      return;
    }
    window.prompt('Copy this link:', contractLink);
  };

  /** Add an email to the recipients list (deduped, validated). Returns true on success. */
  const addEmailRecipient = (raw: string): boolean => {
    const value = raw.trim();
    if (!value) return false;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      alert(`"${value}" is not a valid email address.`);
      return false;
    }
    const lower = value.toLowerCase();
    if (emailRecipients.some((r) => r.toLowerCase() === lower)) {
      return false;
    }
    setEmailRecipients((prev) => [...prev, value]);
    return true;
  };

  const handleAddRecipient = () => {
    if (addEmailRecipient(emailRecipientInput)) {
      setEmailRecipientInput('');
    }
  };

  const handleRecipientInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      handleAddRecipient();
    } else if (e.key === 'Backspace' && emailRecipientInput === '' && emailRecipients.length > 0) {
      setEmailRecipients((prev) => prev.slice(0, -1));
    }
  };

  const handleRemoveRecipient = (email: string) => {
    setEmailRecipients((prev) => prev.filter((r) => r !== email));
  };

  const handleAddExtraEmailFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const { next, problems } = extraEmailFileProblems(extraEmailFiles, Array.from(list));
    setExtraEmailFiles(next);
    if (problems.length > 0) alert(problems.join('\n'));
  };

  const handleSendContractEmail = async () => {
    if (!generatedContractDbContractId) {
      alert('Contract is not ready to send. Try regenerating the contract.');
      return;
    }
    // If the user typed something in the input but didn't add it yet, fold it in.
    let recipients = emailRecipients;
    if (emailRecipientInput.trim()) {
      const added = addEmailRecipient(emailRecipientInput);
      if (added) {
        recipients = [...emailRecipients, emailRecipientInput.trim()];
        setEmailRecipientInput('');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailRecipientInput.trim())) {
        return;
      }
    }
    if (recipients.length === 0) {
      alert('Add at least one recipient before sending.');
      return;
    }
    if (!emailSubject.trim()) {
      alert('Email subject cannot be empty.');
      return;
    }
    if (!emailBody.trim()) {
      alert('Email body cannot be empty.');
      return;
    }
    setIsSendingContractEmail(true);
    try {
      await sendContractEmail(generatedContractDbContractId, {
        recipients,
        subject: emailSubject,
        body: emailBody,
        attachCoi: coiAttached,
        attachW9: w9Attached,
        includeElevatorSurvey,
        files: extraEmailFiles,
      });
      setContractEmailSent(true);
      const extraNote =
        extraEmailFiles.length > 0
          ? ` with ${extraEmailFiles.map((file) => file.name).join(', ')}`
          : '';
      addActivityLog(`Contract email sent to ${recipients.join(', ')}${extraNote}`, employee || 'System');
    } catch (err) {
      alert((err as Error).message || 'Failed to send contract email.');
    } finally {
      setIsSendingContractEmail(false);
    }
  };

  /** Reopen the email modal for an existing contract to send the signing link again. */
  const handleResendContractEmail = async (c: ContractRow) => {
    try {
      const [linkRes, formRes] = await Promise.all([
        fetch(`${API_BASE}/api/contracts/${c.id}/link`),
        fetch(`${API_BASE}/api/contracts/${c.id}/form-data`),
      ]);
      const linkData = (await linkRes.json().catch(() => ({}))) as { link?: string };
      const link =
        linkData.link && /^https?:\/\//i.test(linkData.link)
          ? linkData.link
          : `${window.location.origin}/contract/${c.contract_id}`;
      const formJson = (await formRes.json().catch(() => ({}))) as { form_data?: Record<string, string> };
      const fd = formJson.form_data || {};

      const locEmail = (fd.locationContactEmail || '').trim();
      const authEmail = (fd.authorizedPersonEmail || '').trim();
      const defaultRecipients: string[] = ['billing@techforcerobotics.com'];
      const seen = new Set(defaultRecipients.map((r) => r.toLowerCase()));
      for (const e of [locEmail, authEmail]) {
        const lower = e.toLowerCase();
        if (e && !seen.has(lower)) {
          defaultRecipients.push(e);
          seen.add(lower);
        }
      }

      const greetingName = (fd.authorizedPersonName || '').trim() || (fd.locationContactName || '').trim();
      const businessName = (fd.businessName || orderData?.companyName || '').trim();
      const contractTypeLabel = c.contract_type === 'trial' ? 'Trial' : 'Service';
      const subjectClient = businessName || orderData?.companyName || '';
      const subject = `TechForce Robotics ${contractTypeLabel} Agreement${subjectClient ? ` – ${subjectClient}` : ''}${orderNumber ? ` (Order ${orderNumber})` : ''}`;
      const body = `Hello${greetingName ? ` ${greetingName}` : ''},

This is a follow-up on your TechForce Robotics ${contractTypeLabel.toLowerCase()} agreement. Please review and sign using the secure link below:

${link}

Once you complete the form and submit, our team will receive a fully signed copy automatically.

If you have any questions, simply reply to this email or contact us at billing@techforcerobotics.com.

Best regards,
TechForce Robotics`;

      setSelectedContractType(c.contract_type === 'trial' ? 'trial' : 'service');
      setContractLink(link);
      setGeneratedContractDbContractId(c.contract_id);
      setEmailRecipients(defaultRecipients);
      setEmailRecipientInput('');
      setEmailSubject(subject);
      setEmailBody(body);
      setContractEmailSent(false);
      setCoiAttached(true);
      setW9Attached(true);
      setExtraEmailFiles([]);
      setIncludeElevatorSurvey(true);
      setIsSendingContractEmail(false);
      if (generatedContractPdfUrl && generatedContractPdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(generatedContractPdfUrl);
      }
      setGeneratedContractPdfUrl(null);
      setIsGeneratingContractPdf(true);
      setContractModalStep('email');
      setIsContractModalOpen(true);

      // Fetch the saved PDF as a blob so the preview iframe shows the actual contract
      try {
        const pdfRes = await fetch(`${API_BASE}/api/contracts/${c.id}/pdf`);
        if (pdfRes.ok) {
          const pdfBlob = await pdfRes.blob();
          const blobUrl = URL.createObjectURL(pdfBlob);
          setGeneratedContractPdfUrl(blobUrl);
        }
      } catch {
        // Preview is non-critical
      } finally {
        setIsGeneratingContractPdf(false);
      }
    } catch (err) {
      alert((err as Error).message || 'Failed to load contract for resending.');
    }
  };

  // Signature pad handlers
  const startDrawing = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
  }, []);

  const draw = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top;

    ctx.lineTo(x, y);
    ctx.stroke();
  }, [isDrawing]);

  const stopDrawing = useCallback(() => {
    setIsDrawing(false);
  }, []);

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature(null);
  };

  const saveSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const dataURL = canvas.toDataURL('image/png');
    setSignature(dataURL);
  };

  // Let the employee upload a saved signature image instead of drawing it every time.
  // The image is drawn onto the canvas and normalized to a PNG data URL so it embeds
  // in the contract PDF the same way a drawn signature does. (Client signs via the
  // emailed link, so this option is intentionally only on the TechForce step.)
  const signatureFileInputRef = React.useRef<HTMLInputElement>(null);
  const handleSignatureFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please choose an image file (PNG or JPG).');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const src = typeof reader.result === 'string' ? reader.result : '';
      if (!src) return;
      const img = new Image();
      img.onload = () => {
        const canvas = signatureCanvasRef.current;
        if (!canvas) {
          setSignature(src);
          return;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          setSignature(src);
          return;
        }
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Fit the image within the canvas, preserving aspect ratio.
        const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
        setSignature(canvas.toDataURL('image/png'));
      };
      img.onerror = () => alert('Could not read that image. Please try a different file.');
      img.src = src;
    };
    reader.onerror = () => alert('Could not read that file. Please try again.');
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (contractModalStep !== 'signature') return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [contractModalStep]);

  const handleEditClick = () => {
    // Set edit values to current values
    setEditStage(stage);
    setEditStatus(status);
    setEditAssignees(orderData?.assignees ?? []);
    setEditLastContactDate(lastContactDate);
    setEditTrackingNumber(trackingNumber);
    setEditEstimatedDeliveryDate(estimatedDeliveryDate);
    setEditShippingAddress(shippingAddress);
    setEditDeliverTo(deliverTo);
    setEditInstallationAppointmentTime(installationAppointmentTime);
    setEditInstallationEmployee(installationEmployee);
    setEditSiteLocation(siteLocation);
    setEditNotes(notes);
    setIsEditModalOpen(true);
  };

  const handleCompanyClick = () => {
    if (apiClient) {
      navigate(`/client/${apiClient.id}`);
    }
  };

  if (loading && !orderData) {
    return (
      <div className="page-container">
        <div className="page-layout">
          <Sidebar />
          <main className="page-main">
            <div className="page-content">
              <h2 className="page-title">Order Details</h2>
              <p className="page-subtitle">Loading order...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="page-container">
        <div className="page-layout">
          <Sidebar />
          <main className="page-main">
            <div className="page-content">
              <h2 className="page-title">Order Not Found</h2>
              <button onClick={() => navigate('/orders')}>Back to Orders</button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <PageHeader
            title={orderData ? `${orderData.companyName} — ${orderData.orderNumber}` : 'Order Details'}
            subtitle="View and update order information"
            onBack={() => navigate('/orders')}
            backLabel="Back"
          />
          <div className="page-content">
            {/* Order Information Card */}
            <div className={`order-info-card stage-${stage.toLowerCase()}`}>
              <div className="order-info-header">
                <h3 className="section-title">Order Information</h3>
                <div className="order-action-buttons">
                  <button
                    type="button"
                    className="order-action-btn order-action-btn--edit"
                    onClick={handleEditClick}
                    data-tooltip="Edit order"
                    aria-label="Edit order"
                  >
                    <FontAwesomeIcon icon={faPenToSquare} />
                  </button>
                  {orderData.isVoided ? (
                    <button
                      type="button"
                      className="order-action-btn order-action-btn--unvoid"
                      onClick={handleUnvoidOrder}
                      disabled={voidOrderSubmitting}
                      data-tooltip="Unvoid order"
                      aria-label="Unvoid order"
                    >
                      <FontAwesomeIcon icon={faRotateLeft} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="order-action-btn order-action-btn--void"
                      onClick={openVoidOrderModal}
                      data-tooltip="Void order"
                      aria-label="Void order"
                    >
                      <FontAwesomeIcon icon={faBan} />
                    </button>
                  )}
                </div>
              </div>

              {orderData.isVoided && orderData.voidReason ? (
                <div className="order-info-void-banner" role="status">
                  <strong>Order voided.</strong>{' '}
                  <span className="order-info-void-reason-label">Reason:</span>{' '}
                  <span className="order-info-void-reason">{orderData.voidReason}</span>
                </div>
              ) : null}

              <div className="order-info-grid">
                <div className="order-info-item">
                  <label className="order-info-label">Order Number</label>
                  <p className="order-info-value">{orderData.orderNumber}</p>
                </div>

                <div className="order-info-item">
                  <label className="order-info-label">Company Name</label>
                  {apiClient ? (
                    <button
                      type="button"
                      className="order-info-value order-company-link"
                      onClick={handleCompanyClick}
                      title="View client page"
                    >
                      {orderData.companyName}
                    </button>
                  ) : (
                    <p className="order-info-value">{orderData.companyName}</p>
                  )}
                </div>

                <div className="order-info-item">
                  <label className="order-info-label">Stage</label>
                  <p className="order-info-value">
                    <span className={`stage-badge stage-${stage.toLowerCase()}`}>
                      {stage === 'Delivery' ? 'Invoice' : stage}
                    </span>
                  </p>
                </div>

                <div className="order-info-item">
                  <label className="order-info-label">Status</label>
                  <p className="order-info-value">
                    <span className={`status-badge status-${status.toLowerCase().replace(/\s+/g, '-')}`}>
                      {status}
                    </span>
                  </p>
                </div>

                <div className="order-info-item">
                  <label className="order-info-label">{orderData.assignees.length > 1 ? 'Employees' : 'Employee'}</label>
                  <p className="order-info-value">{orderData.assignees.join(', ') || 'unassigned'}</p>
                </div>

                {stage === 'Contract' && (
                  <div className="order-info-item">
                    <label className="order-info-label">Last Contact Date</label>
                    <p className="order-info-value">
                      {chatMessages.length === 0 ? 'Not contacted' : (lastContactDate || 'Not set')}
                    </p>
                  </div>
                )}

                {stage === 'Contract' && status === 'Pilot' && (
                  <div className="order-info-item">
                    <label className="order-info-label">Term End Date</label>
                    <p className="order-info-value">
                      {orderData.pilotTermEndDate ? formatTaskDate(orderData.pilotTermEndDate) : 'Not set'}
                      {orderData.pilotTermEndDate && orderData.pilotTermStartDate && orderData.pilotTermDays != null ? (
                        <span className="order-info-term-detail">
                          {orderData.pilotTermDays}-day term from {formatTaskDate(orderData.pilotTermStartDate)}
                        </span>
                      ) : null}
                    </p>
                  </div>
                )}

                {stage === 'Delivery' && (
                  <div className="order-info-item">
                    <label className="order-info-label">Shipping Address</label>
                    <p className="order-info-value" style={{ whiteSpace: 'pre-wrap' }}>
                      {invoiceStageShippingFromContract || shippingAddress || 'Not set'}
                    </p>
                  </div>
                )}

                {stage === 'Installation' && (
                  <>
                    <div className="order-info-item">
                      <label className="order-info-label">Installation Appointment</label>
                      <p className="order-info-value">{installationAppointmentTime ? formatInstallationAppointmentTime(installationAppointmentTime) : 'Not set'}</p>
                    </div>
                    {installationEmployee && (
                      <div className="order-info-item">
                        <label className="order-info-label">Installation Employee</label>
                        <p className="order-info-value">{installationEmployee}</p>
                      </div>
                    )}
                    {siteLocation && (
                      <div className="order-info-item">
                        <label className="order-info-label">Site Location</label>
                        <p className="order-info-value">{siteLocation}</p>
                      </div>
                    )}
                  </>
                )}

                <div className="order-info-item order-info-item--full">
                  <label className="order-info-label">Notes</label>
                  <p className="order-info-value order-info-notes-value">
                    {notes.trim() ? notes : 'Not set'}
                  </p>
                </div>
              </div>
            </div>

            {orderMessage && (
              <p
                className="update-message-banner settings-success"
                role="alert"
                style={{ marginTop: '1rem' }}
              >
                {orderMessage}
              </p>
            )}

            {/* Generate Contract, Initial Survey, Set Term Period (Pilot), and Move to Invoice - Contract stage */}
            {stage === 'Contract' && (
              <div style={{ marginTop: '1rem', marginBottom: '1rem', display: 'flex', justifyContent: 'flex-start', gap: '0.75rem', paddingLeft: '1rem', flexWrap: 'wrap' }}>
                <button 
                  type="button"
                  className="generate-contract-button"
                  onClick={handleGenerateContract}
                >
                  Generate Contract
                </button>
                <button
                  type="button"
                  className="generate-survey-button"
                  onClick={openSurveyModal}
                >
                  Generate Initial Survey
                </button>
                {status === 'Pilot' && (
                  <button
                    type="button"
                    className="generate-survey-button"
                    onClick={openPilotTermModal}
                  >
                    Set Term Period
                  </button>
                )}
                {status === 'Approved' && (
                  <button
                    type="button"
                    className="generate-invoice-button"
                    onClick={async () => {
                      if (!orderNumber) return;
                      try {
                        const updated = await updateOrder(orderNumber, { category: 'Inventory' });
                        setOrderData(mapApiOrderToDetail(updated));
                        setStage('Delivery');
                        addActivityLog('Order moved to Invoice stage', employee || 'System');
                      } catch (err) {
                        alert((err as Error).message || 'Failed to move order to Invoice stage.');
                      }
                    }}
                  >
                    Move to Invoice Stage
                  </button>
                )}
              </div>
            )}

            <Modal
              isOpen={surveyModalOpen}
              onClose={() => setSurveyModalOpen(false)}
              title="Elevator Initial Survey"
              narrow
            >
              {surveySent ? (
                <>
                  <div className="modal-body">
                    <p style={{ margin: 0 }}>The initial survey link has been emailed to the client.</p>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="generate-contract-button" onClick={() => setSurveyModalOpen(false)}>
                      Done
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="modal-body">
                    <div className="survey-link-panel">
                      <div className="survey-link-panel__head">
                        <span className="survey-link-panel__title">Survey link</span>
                        <button
                          type="button"
                          className="survey-link-panel__generate"
                          onClick={() => void handleGenerateSurveyLink()}
                          disabled={surveyLinkBusy || surveyLinksLoading}
                        >
                          {surveyLinkBusy ? 'Working…' : activeSurveyLink ? 'Generate new link' : 'Generate link'}
                        </button>
                      </div>
                      {surveyLinksLoading ? (
                        <p className="survey-link-panel__hint">Loading links…</p>
                      ) : activeSurveyLink ? (
                        <>
                          <code className="survey-link-panel__url">{activeSurveyLink.link}</code>
                          <div className="survey-link-panel__actions">
                            <button
                              type="button"
                              className="survey-link-panel__button"
                              onClick={() => void handleCopySurveyLink(activeSurveyLink.link)}
                              disabled={surveyLinkBusy}
                            >
                              {surveyLinkCopied ? 'Copied' : 'Copy link'}
                            </button>
                            <button
                              type="button"
                              className="survey-link-panel__button survey-link-panel__button--danger"
                              onClick={() => void handleRevokeSurveyLink(activeSurveyLink.id)}
                              disabled={surveyLinkBusy}
                            >
                              Disable link
                            </button>
                          </div>
                        </>
                      ) : (
                        <p className="survey-link-panel__hint">
                          No active link. Generate one to share with the client, or send the survey by email below.
                        </p>
                      )}
                      {surveyLinks.length > (activeSurveyLink ? 1 : 0) ? (
                        <ul className="survey-link-panel__history">
                          {surveyLinks
                            .filter((s) => s.id !== activeSurveyLink?.id)
                            .map((s) => (
                              <li key={s.id}>
                                <div className="survey-link-panel__history-head">
                                  <span className={`survey-link-panel__badge survey-link-panel__badge--${s.revoked_at ? 'off' : s.status.toLowerCase()}`}>
                                    {s.revoked_at ? 'Disabled' : s.status}
                                  </span>
                                  <span className="survey-link-panel__history-date">
                                    {s.revoked_at
                                      ? `Disabled ${formatDateTimePT(s.revoked_at)}`
                                      : s.submitted_at
                                        ? `Submitted ${formatDateTimePT(s.submitted_at)}`
                                        : s.created_at
                                          ? `Created ${formatDateTimePT(s.created_at)}`
                                          : ''}
                                  </span>
                                </div>
                                <code
                                  className={`survey-link-panel__history-url${s.revoked_at ? ' survey-link-panel__history-url--off' : ''}`}
                                >
                                  {s.link}
                                </code>
                              </li>
                            ))}
                        </ul>
                      ) : null}
                    </div>
                    <p style={{ margin: '1rem 0 0.75rem', color: '#4b5563', fontSize: '0.9rem' }}>
                      Send the client a link to complete the Elevator Initial Survey on its own — independent of the contract email.
                    </p>
                    <label htmlFor="survey-recipients" style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>
                      Recipient email(s)
                    </label>
                    <textarea
                      id="survey-recipients"
                      value={surveyEmails}
                      onChange={(e) => setSurveyEmails(e.target.value)}
                      placeholder="client@example.com, contact@example.com"
                      rows={3}
                      disabled={surveySending}
                      style={{ width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: '0.9rem', boxSizing: 'border-box' }}
                    />
                    <p style={{ margin: '0.35rem 0 0', color: '#6b7280', fontSize: '0.8rem' }}>
                      Separate multiple addresses with commas.
                    </p>
                    {surveyError ? (
                      <p style={{ color: '#b91c1c', margin: '0.75rem 0 0', fontSize: '0.875rem' }}>{surveyError}</p>
                    ) : null}
                  </div>
                  <div className="modal-actions">
                    <button
                      type="button"
                      className="cancel-button"
                      onClick={() => setSurveyModalOpen(false)}
                      disabled={surveySending}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="generate-contract-button"
                      onClick={() => void handleSendSurvey()}
                      disabled={surveySending}
                    >
                      {surveySending ? 'Sending…' : 'Send Survey'}
                    </button>
                  </div>
                </>
              )}
            </Modal>

            <Modal
              isOpen={pilotTermModalOpen}
              onClose={() => setPilotTermModalOpen(false)}
              title="Set Term Period"
              narrow
            >
              <div className="modal-body">
                <label htmlFor="pilot-term-days" style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>
                  How many days is the term period?
                </label>
                <input
                  id="pilot-term-days"
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  value={pilotTermDaysInput}
                  onChange={(e) => setPilotTermDaysInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleSavePilotTerm();
                  }}
                  placeholder="e.g. 30"
                  disabled={pilotTermSaving}
                  autoFocus
                  style={{ width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
                <label htmlFor="pilot-term-start" style={{ display: 'block', fontWeight: 600, margin: '1rem 0 0.4rem' }}>
                  When does the term start?
                </label>
                <input
                  id="pilot-term-start"
                  type="date"
                  value={pilotTermStartInput}
                  onChange={(e) => setPilotTermStartInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleSavePilotTerm();
                  }}
                  disabled={pilotTermSaving}
                  style={{ width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
                <p style={{ margin: '0.75rem 0 0', color: '#4b5563', fontSize: '0.9rem' }}>
                  {pilotTermPreviewEnd && pilotTermStartValue ? (
                    <>
                      The term runs from {formatTaskDate(pilotTermStartValue)} to{' '}
                      <strong>{formatTaskDate(pilotTermPreviewEnd)}</strong>.
                      {pilotTermPreviewEnd < pilotTermTodayYmd ? ' That date has already passed, so no reminders will go out.' : null}
                    </>
                  ) : pilotTermStartValue ? (
                    'Enter the number of days in the pilot term.'
                  ) : (
                    'Choose the date the term starts.'
                  )}
                </p>
                {pilotTermPreviewEnd && pilotTermPreviewEnd < pilotTermTodayYmd ? null : (
                  <p style={{ margin: '0.35rem 0 0', color: '#6b7280', fontSize: '0.8rem' }}>
                    {orderData.assignees.length > 0
                      ? `${new Intl.ListFormat('en', { type: 'conjunction' }).format(orderData.assignees)} will be emailed 14, 7, 2 and 1 days before the term ends. The 2-day and 1-day emails remind them to tell the client we'll deactivate and collect the product.`
                      : 'No employee is assigned to this order. Reminder emails go to the employees on the order, so assign at least one to receive them.'}
                  </p>
                )}
                {pilotTermError ? (
                  <p style={{ color: '#b91c1c', margin: '0.75rem 0 0', fontSize: '0.875rem' }}>{pilotTermError}</p>
                ) : null}
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setPilotTermModalOpen(false)}
                  disabled={pilotTermSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="generate-contract-button"
                  onClick={() => void handleSavePilotTerm()}
                  disabled={pilotTermSaving || pilotTermDaysValue == null || pilotTermStartValue == null}
                >
                  {pilotTermSaving ? 'Saving…' : 'Set Term Period'}
                </button>
              </div>
            </Modal>

            {/* Generate Invoice and Move to Installation - Delivery (Invoice) stage */}
            {stage === 'Delivery' && (
              <div style={{ marginTop: '1rem', marginBottom: '1rem', display: 'flex', justifyContent: 'flex-start', gap: '0.75rem', paddingLeft: '1rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="generate-invoice-button"
                  onClick={async () => {
                    setGenerateInvoiceStep('confirm');
                    setIsGenerateInvoiceModalOpen(true);
                    if (!orderNumber) return;
                    try {
                      const res = await fetch(`${API_BASE}/api/contracts?order_number=${encodeURIComponent(orderNumber)}`);
                      if (!res.ok) return;
                      const data = await res.json();
                      const contracts: ContractRow[] = data.contracts || [];
                      const signed = contracts.filter((c) => c.status === 'signed').sort((a, b) => (new Date(b.signed_at || 0).getTime() - new Date(a.signed_at || 0).getTime()));
                      const latestSigned = signed[0];
                      if (!latestSigned) {
                        setInvoiceBillingFromClient('');
                        return;
                      }
                      const formRes = await fetch(`${API_BASE}/api/contracts/${latestSigned.id}/form-data`);
                      if (!formRes.ok) return;
                      const formData = await formRes.json();
                      const fd = (formData.form_data || {}) as Record<string, string>;
                      const billingEntity = (fd.billingEntity || '').trim();
                      const billingAddress = (fd.billingAddress || '').trim();
                      const billingCity = (fd.billingCity || '').trim();
                      const billingState = (fd.billingState || '').trim();
                      const billingZip = (fd.billingZip || '').trim();
                      const billingCityStateZip = [billingCity, [billingState, billingZip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
                      const hasClientBilling = !!(billingEntity || billingAddress || billingCity || billingState || billingZip);
                      const fullBilling = hasClientBilling
                        ? [billingAddress || billingEntity, billingCityStateZip].filter(Boolean).join('\n')
                        : '';
                      setInvoiceBillingFromClient(fullBilling);
                      setInvoiceBillingEntityFromClient(billingEntity);
                      const serviceAddr = (fd.serviceAddress || '').trim();
                      const city = (fd.city || '').trim();
                      const state = (fd.state || '').trim();
                      const zip = (fd.zip || '').trim();
                      const cityStateZip = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
                      setInvoiceShippingFromContract([serviceAddr, cityStateZip].filter(Boolean).join('\n'));
                      const builtProducts = buildOrderProductsFromContractForm(orderNumber, fd);
                      setInvoiceProductsFromContract(builtProducts);
                    } catch {
                      setInvoiceBillingFromClient('');
                      setInvoiceBillingEntityFromClient('');
                      setInvoiceShippingFromContract('');
                      setInvoiceProductsFromContract([]);
                    }
                  }}
                >
                  Generate Invoice
                </button>
                {orderInvoices.length > 0 && (
                  <button type="button" className="installation-appointment-button" onClick={openSetupInstall}>
                    Set Up Installation Appointment
                  </button>
                )}
                {status === 'Invoice Ready' && (
                  <button
                    type="button"
                    className="installation-appointment-button"
                    onClick={async () => {
                      if (!orderNumber) return;
                      try {
                        const updated = await updateOrder(orderNumber, { category: 'Installation', status: 'Pending' });
                        setOrderData(mapApiOrderToDetail(updated));
                        setStage('Installation');
                        setStatus('Pending');
                        addActivityLog('Order moved to Installation stage', employee || 'System');
                      } catch (err) {
                        alert((err as Error).message || 'Failed to move order to Installation stage.');
                      }
                    }}
                  >
                    Move to Installation Stage
                  </button>
                )}
              </div>
            )}

            {/* Installation stage buttons: Set Appointment + Confirm Inventory */}
            {stage === 'Installation' && (
              <div style={{ marginTop: '1rem', marginBottom: '1rem', display: 'flex', justifyContent: 'flex-start', gap: '0.75rem', paddingLeft: '1rem', flexWrap: 'wrap' }}>
                <button 
                  type="button"
                  className="installation-appointment-button"
                  onClick={async () => {
                    setEditInstallationAppointmentTime(installationAppointmentTime);
                    setEditInstallationDate(installationDate);
                    setEditInstallationTime(installationTime);
                    setEditInstallationEmployee(installationEmployee);
                    // Pre-fill site address: prefer latest signed contract form data (city/state/zip directly), then parsed address string
                    const signed = orderContracts.filter((c) => c.status === 'signed').sort((a, b) => (new Date(b.signed_at || 0).getTime() - new Date(a.signed_at || 0).getTime()));
                    const latestSigned = signed[0];
                    let street = '';
                    let city = '';
                    let state = '';
                    let zip = '';
                    let country = 'United States';
                    if (latestSigned) {
                      try {
                        const res = await fetch(`${API_BASE}/api/contracts/${latestSigned.id}/form-data`);
                        if (res.ok) {
                          const data = await res.json();
                          const fd = (data.form_data || {}) as Record<string, string>;
                          const fullStreet = (fd.serviceAddress ?? fd.service_address ?? '').trim();
                          const aptMatch = fullStreet.match(/(.*?)(?:Apt|Unit|#|Suite)\s*([A-Z0-9-]+)/i);
                          if (aptMatch) {
                            street = aptMatch[1].trim();
                            setEditSiteAptNumber(aptMatch[2].trim());
                          } else {
                            street = fullStreet;
                            setEditSiteAptNumber('');
                          }
                          city = (fd.city ?? '').trim();
                          state = (fd.state ?? '').trim().toUpperCase();
                          zip = (fd.zip ?? '').trim();
                        }
                      } catch {
                        // ignore
                      }
                    }
                    if (!street && !city && !state && !zip) {
                      const addressToParse = invoiceStageShippingFromContract || siteLocation || '';
                      setEditSiteLocation(addressToParse);
                      if (addressToParse) {
                        const addressLines = addressToParse.split('\n').map((l) => l.trim()).filter(Boolean);
                        if (addressLines.length > 0) {
                          const firstLine = addressLines[0];
                          const aptMatch = firstLine.match(/(.*?)(?:Apt|Unit|#|Suite)\s*([A-Z0-9-]+)/i);
                          if (aptMatch) {
                            street = aptMatch[1].trim();
                            setEditSiteAptNumber(aptMatch[2].trim());
                          } else {
                            street = firstLine;
                            setEditSiteAptNumber('');
                          }
                          if (addressLines.length > 1) {
                            const cityStateZip = addressLines[1];
                            const matchWithZip = cityStateZip.match(/(.+?),\s*([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)\s*$/);
                            const matchStateOnly = cityStateZip.match(/(.+?),\s*([A-Za-z]{2})\s*$/);
                            if (matchWithZip) {
                              city = matchWithZip[1].trim();
                              state = matchWithZip[2].trim().toUpperCase();
                              zip = matchWithZip[3].trim();
                            } else if (matchStateOnly) {
                              city = matchStateOnly[1].trim();
                              state = matchStateOnly[2].trim().toUpperCase();
                            }
                          }
                          if (addressLines.length > 2) country = addressLines[2].trim();
                        }
                      }
                    } else {
                      const addressLine2 = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
                      setEditSiteLocation([street, addressLine2, country].filter(Boolean).join('\n'));
                    }
                    setEditSiteStreetAddress(street);
                    setEditSiteCity(city);
                    setEditSiteState(state);
                    setEditSiteZipCode(zip);
                    setEditSiteCountry(country);
                    if (!street && !city && !state && !zip) {
                      setEditSiteStreetAddress('');
                      setEditSiteAptNumber('');
                      setEditSiteCountry('United States');
                    }
                    setIsInstallationModalOpen(true);
                  }}
                >
                  Set Site Inspection
                </button>
                <button
                  type="button"
                  className="generate-invoice-button"
                  onClick={async () => {
                    const signed = orderContracts.filter((c) => c.status === 'signed').sort((a, b) => (new Date(b.signed_at || 0).getTime() - new Date(a.signed_at || 0).getTime()));
                    const latestSigned = signed[0];
                    if (!latestSigned) {
                      alert('No signed contract found. Contract data (business name, site city) is required to confirm inventory.');
                      return;
                    }
                    try {
                      const res = await fetch(`${API_BASE}/api/contracts/${latestSigned.id}/form-data`);
                      if (!res.ok) throw new Error('Failed to load contract data');
                      const data = await res.json();
                      const fd = (data.form_data || {}) as Record<string, string>;
                      setInstallationContractData({
                        businessName: fd.businessName ?? fd.business_name ?? '',
                        city: fd.city ?? '',
                      });
                      setIsConfirmInventoryModalOpen(true);
                    } catch (err) {
                      alert((err as Error).message || 'Failed to load contract data.');
                    }
                  }}
                >
                  Confirm Inventory
                </button>
                <button
                  type="button"
                  className="generate-invoice-button"
                  onClick={async () => {
                    const signed = orderContracts.filter((c) => c.status === 'signed').sort((a, b) => (new Date(b.signed_at || 0).getTime() - new Date(a.signed_at || 0).getTime()));
                    const latestSigned = signed[0];
                    if (!latestSigned) {
                      alert('No signed contract found. Contract data is required for delivery information.');
                      return;
                    }
                    try {
                      const res = await fetch(`${API_BASE}/api/contracts/${latestSigned.id}/form-data`);
                      if (!res.ok) throw new Error('Failed to load contract data');
                      const data = await res.json();
                      const fd = (data.form_data || {}) as Record<string, string>;
                      setDeliveryCardData({
                        businessName: (fd.businessName ?? fd.business_name ?? '').trim(),
                        serviceAddress: (fd.serviceAddress ?? fd.service_address ?? '').trim(),
                        city: (fd.city ?? '').trim(),
                        state: (fd.state ?? '').trim(),
                        zip: (fd.zip ?? '').trim(),
                        locationContactName: (fd.locationContactName ?? fd.location_contact_name ?? '').trim(),
                        locationContactPhone: (fd.locationContactPhone ?? fd.location_contact_phone ?? '').trim(),
                        locationContactEmail: (fd.locationContactEmail ?? fd.location_contact_email ?? '').trim(),
                      });
                      setDeliveryModalStep('card');
                      setIsDeliveryModalOpen(true);
                    } catch (err) {
                      alert((err as Error).message || 'Failed to load delivery information.');
                    }
                  }}
                >
                  Delivery
                </button>
              </div>
            )}

            {/* Edit Modal */}
            {isEditModalOpen && (
              <div className="modal-overlay">
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">Edit Order Information</h3>
                    <button 
                      className="modal-close-button"
                      onClick={handleEditCancel}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                  
                  <form className="modal-form" onSubmit={handleEditSave}>
                <div className="form-group">
                      <label htmlFor="editOrderNumber" className="form-label">Order Number</label>
                  <input
                    type="text"
                        id="editOrderNumber"
                    className="form-input"
                    value={orderData.orderNumber}
                    disabled
                    readOnly
                  />
                </div>

                <div className="form-group">
                      <label htmlFor="editCompanyName" className="form-label">Company Name</label>
                  <input
                    type="text"
                        id="editCompanyName"
                        className="form-input"
                    value={orderData.companyName}
                        disabled
                    readOnly
                  />
                </div>

                <div className="form-group">
                      <label htmlFor="editStage" className="form-label">Stage</label>
                  <select
                        id="editStage"
                    className="form-select"
                        value={editStage}
                        onChange={(e) => setEditStage(e.target.value as OrderWorkflowStage)}
                  >
                    <option value="Contract">Contract</option>
                    <option value="Delivery">Invoice</option>
                    <option value="Installation">Installation</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>

                <div className="form-group">
                      <label htmlFor="editStatus" className="form-label">Status</label>
                  <select
                        id="editStatus"
                    className="form-select"
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value)}
                  >
                        {getStatusOptions(editStage).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                      <label className="form-label">Employees</label>
                      <p style={{ marginTop: 0, marginBottom: '0.5rem', fontSize: '0.8125rem', color: '#6b7280' }}>
                        Everyone added gets the order&apos;s reminder emails. The first person is the primary employee.
                      </p>
                      <TaskAssigneePicker
                        users={orderAssigneePickerUsers}
                        value={editAssignees.map((name) => orderAssigneeOptions.indexOf(name) + 1).filter((id) => id > 0)}
                        onChange={(ids) => setEditAssignees(ids.map((id) => orderAssigneeOptions[id - 1]))}
                        addPlaceholder="Search employee to add…"
                      />
                    </div>

                    {editStage === 'Contract' && (
                      <div className="form-group">
                        <label htmlFor="editLastContactDate" className="form-label">Last Contact Date</label>
                        <input
                          type="text"
                          id="editLastContactDate"
                          className="form-input"
                          value={editLastContactDate}
                          onChange={(e) => setEditLastContactDate(e.target.value)}
                          placeholder="e.g., 2024-01-15 10:30 AM"
                        />
                      </div>
                    )}

                    {editStage === 'Installation' && (
                      <div className="form-group">
                        <label htmlFor="editInstallationAppointmentTime" className="form-label">Installation Appointment Time</label>
                        <input
                          type="text"
                          id="editInstallationAppointmentTime"
                          className="form-input"
                          value={editInstallationAppointmentTime}
                          onChange={(e) => setEditInstallationAppointmentTime(e.target.value)}
                          placeholder="e.g., 2024-01-20 09:00 AM"
                        />
                      </div>
                    )}

                    <div className="form-group">
                      <label htmlFor="editOrderNotes" className="form-label">Notes</label>
                      <textarea
                        id="editOrderNotes"
                        className="form-input order-edit-notes-textarea"
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        placeholder="Internal notes for this order…"
                        rows={4}
                      />
                    </div>

                    {editSaveError && (
                      <p className="create-order-error" role="alert" style={{ marginBottom: '1rem' }}>{editSaveError}</p>
                    )}
                    <div className="modal-actions">
                      <button type="button" className="cancel-button" onClick={handleEditCancel} disabled={editSaving}>
                        Cancel
                      </button>
                      <button type="submit" className="save-button" disabled={editSaving}>
                        {editSaving ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Shipping Information Modal */}
            {isShippingModalOpen && (
              <div className="modal-overlay">
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">Enter Shipping Information</h3>
                    <button 
                      className="modal-close-button"
                      onClick={() => setIsShippingModalOpen(false)}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                  
                  <form className="modal-form" onSubmit={(e) => {
                    e.preventDefault();
                    setTrackingNumber(editTrackingNumber);
                    setDeliverTo(editDeliverTo);
                    
                    // Build full address string from components
                    const addressParts = [];
                    if (editStreetAddress) addressParts.push(editStreetAddress);
                    if (editAptNumber) addressParts.push(`Apt ${editAptNumber}`);
                    const addressLine1 = addressParts.join(', ');
                    
                    const addressLine2 = [];
                    if (editCity) addressLine2.push(editCity);
                    if (editState) addressLine2.push(editState);
                    if (editZipCode) addressLine2.push(editZipCode);
                    const cityStateZip = addressLine2.join(', ');
                    
                    const fullAddress = [addressLine1, cityStateZip, editCountry].filter(Boolean).join('\n');
                    setShippingAddress(fullAddress);
                    setEditShippingAddress(fullAddress);
                    
                    // Add activity log entries
                    if (trackingNumber !== editTrackingNumber && editTrackingNumber) {
                      addActivityLog(`Tracking number ${trackingNumber ? `changed to ${editTrackingNumber}` : `set to ${editTrackingNumber}`}`, employee || 'System');
                    }
                    if (shippingAddress !== fullAddress && fullAddress) {
                      addActivityLog(`Shipping address ${shippingAddress ? 'updated' : 'set'}`, employee || 'System');
                    }
                    if (deliverTo !== editDeliverTo && editDeliverTo) {
                      addActivityLog(`Deliver to ${deliverTo ? `changed to ${editDeliverTo}` : `set to ${editDeliverTo}`}`, employee || 'System');
                    }
                    setIsShippingModalOpen(false);
                  }}>
                    <div className="form-group">
                      <label htmlFor="deliverTo" className="form-label">Deliver To</label>
                      <input
                        type="text"
                        id="deliverTo"
                        className="form-input"
                        value={editDeliverTo}
                        onChange={(e) => setEditDeliverTo(e.target.value)}
                        placeholder="Enter recipient name..."
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="shippingTrackingNumber" className="form-label">Tracking Number</label>
                      <input
                        type="text"
                        id="shippingTrackingNumber"
                        className="form-input"
                        value={editTrackingNumber}
                        onChange={(e) => setEditTrackingNumber(e.target.value)}
                        placeholder="e.g., TRK-123456789"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="streetAddress" className="form-label">Street Address</label>
                      <input
                        type="text"
                        id="streetAddress"
                        className="form-input"
                        value={editStreetAddress}
                        onChange={(e) => setEditStreetAddress(e.target.value)}
                        placeholder="Enter street address..."
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="aptNumber" className="form-label">Apartment, Suite, Unit, etc. (Optional)</label>
                      <input
                        type="text"
                        id="aptNumber"
                        className="form-input"
                        value={editAptNumber}
                        onChange={(e) => setEditAptNumber(e.target.value)}
                        placeholder="Apt, Unit, Suite, etc."
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group" style={{ flex: 2 }}>
                        <label htmlFor="city" className="form-label">City</label>
                        <input
                          type="text"
                          id="city"
                          className="form-input"
                          value={editCity}
                          onChange={(e) => setEditCity(e.target.value)}
                          placeholder="City"
                        />
                      </div>

                      <div className="form-group" style={{ flex: 1 }}>
                        <label htmlFor="state" className="form-label">State</label>
                        <input
                          type="text"
                          id="state"
                          className="form-input"
                          value={editState}
                          onChange={(e) => setEditState(e.target.value.toUpperCase())}
                          placeholder="State"
                          maxLength={2}
                          style={{ textTransform: 'uppercase' }}
                        />
                      </div>

                      <div className="form-group" style={{ flex: 1 }}>
                        <label htmlFor="zipCode" className="form-label">Zip Code</label>
                        <input
                          type="text"
                          id="zipCode"
                          className="form-input"
                          value={editZipCode}
                          onChange={(e) => setEditZipCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          placeholder="Zip Code"
                          maxLength={10}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="country" className="form-label">Country</label>
                      <input
                        type="text"
                        id="country"
                        className="form-input"
                        value={editCountry}
                        onChange={(e) => setEditCountry(e.target.value)}
                        placeholder="Country"
                      />
                    </div>

                    <div className="modal-actions">
                      <button type="button" className="cancel-button" onClick={() => setIsShippingModalOpen(false)}>
                        Cancel
                      </button>
                      <button type="submit" className="save-button">
                        Save
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Generate Invoice Modal (2 steps) */}
            {isGenerateInvoiceModalOpen && (
              <div className="modal-overlay">
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
                  <div className="modal-header">
                    <h3 className="modal-title">
                      {generateInvoiceStep === 'confirm' ? 'Confirm invoice details' : 'Invoice date & terms'}
                    </h3>
                    <button
                      className="modal-close-button"
                      onClick={() => !generateInvoiceSubmitting && (setIsGenerateInvoiceModalOpen(false), setGenerateInvoiceStep('confirm'))}
                      aria-label="Close"
                      disabled={generateInvoiceSubmitting}
                    >
                      ×
                    </button>
                  </div>
                  {generateInvoiceStep === 'confirm' ? (
                    <>
                      <div style={{ padding: '0 1.25rem 1rem' }}>
                        <p style={{ marginBottom: '0.75rem' }}><strong>Order:</strong> {orderData?.orderNumber} — {orderData?.companyName}</p>
                        <p style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Billing entity</p>
                        <p style={{ margin: '0 0 1rem', padding: '0.5rem', background: '#f5f5f5', borderRadius: 4, fontSize: '0.9rem' }}>
                          {invoiceBillingEntityFromClient || '—'}
                        </p>
                        <p style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Billing address</p>
                        <pre style={{ margin: '0 0 1rem', padding: '0.5rem', background: '#f5f5f5', borderRadius: 4, fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>
                          {invoiceBillingFromClient || '—'}
                        </pre>
                        <p style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Shipping address</p>
                        <pre style={{ margin: '0 0 1rem', padding: '0.5rem', background: '#f5f5f5', borderRadius: 4, fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>
                          {(() => {
                            const orderShipping = (shippingAddress || '').trim();
                            const contractShipping = (invoiceShippingFromContract || '').trim();
                            if (orderShipping) return orderShipping;
                            if (contractShipping && contractShipping !== (invoiceBillingFromClient || '').trim()) return contractShipping;
                            return 'Same as billing';
                          })()}
                        </pre>
                        <p style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Products</p>
                        <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                          {(() => {
                            const list = products.length ? products : invoiceProductsFromContract;
                            return list.length ? list.map((p) => (
                              <li key={p.id}>{p.productName}{p.serialNumber ? ` (${p.serialNumber})` : ''}</li>
                            )) : <li>No products</li>;
                          })()}
                        </ul>
                      </div>
                      <div className="modal-actions">
                        <button type="button" className="cancel-button" onClick={() => setIsGenerateInvoiceModalOpen(false)}>Cancel</button>
                        <button type="button" className="save-button" onClick={() => {
                          const today = new Date().toISOString().slice(0, 10);
                          setInvoiceDate(today);
                          setInvoiceDueDate(today);
                          setGenerateInvoiceStep('dates');
                        }}>Confirm & continue</button>
                      </div>
                    </>
                  ) : (
                    <form className="modal-form" style={{ padding: '0 1.25rem 1rem' }} onSubmit={async (e) => {
                      e.preventDefault();
                      if (!orderNumber || !orderData) return;
                      setGenerateInvoiceSubmitting(true);
                      try {
                        const termsStr = invoiceTermsType === 'One Time' ? 'One Time' : `Net ${invoiceTermsNetDays.trim() || '30'}`;
                        const productsToSend = products.length ? products : invoiceProductsFromContract;
                        const orderShipping = (shippingAddress || '').trim();
                        const contractShipping = (invoiceShippingFromContract || '').trim();
                        const billingTrim = (invoiceBillingFromClient || '').trim();
                        const shippingToSend = orderShipping || (contractShipping && contractShipping !== billingTrim ? contractShipping : billingTrim);
                        const invoiceRecipient = await sendInvoiceConfirmation(orderNumber, {
                          companyName: orderData.companyName,
                          billingEntity: invoiceBillingEntityFromClient || '',
                          billingAddress: invoiceBillingFromClient || '',
                          shippingAddress: shippingToSend || '',
                          products: productsToSend.map((p) => ({
                            productName: p.productName,
                            serialNumber: p.serialNumber ?? undefined,
                            quantity: p.quantity ?? 1,
                          })),
                          invoiceDate: invoiceDate || new Date().toISOString().slice(0, 10),
                          dueDate: invoiceDueDate || new Date().toISOString().slice(0, 10),
                          terms: termsStr,
                          userEmail: user?.email,
                        });
                        const updated = await updateOrder(orderNumber, { status: 'Awaiting Invoice' });
                        setOrderData(mapApiOrderToDetail(updated));
                        setStatus('Awaiting Invoice');
                        alert(
                          invoiceRecipient
                            ? `Invoice confirmation has been sent to ${invoiceRecipient}.`
                            : 'Invoice confirmation has been sent.'
                        );
                        setIsGenerateInvoiceModalOpen(false);
                        setGenerateInvoiceStep('confirm');
                      } catch (err) {
                        alert((err as Error).message || 'Failed to send invoice confirmation.');
                      } finally {
                        setGenerateInvoiceSubmitting(false);
                      }
                    }}>
                      <div className="form-group">
                        <label htmlFor="invoiceDate" className="form-label">Invoice date</label>
                        <input
                          type="date"
                          id="invoiceDate"
                          className="form-input"
                          value={invoiceDate}
                          onChange={(e) => setInvoiceDate(e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="invoiceDueDate" className="form-label">Due date</label>
                        <input
                          type="date"
                          id="invoiceDueDate"
                          className="form-input"
                          value={invoiceDueDate}
                          onChange={(e) => setInvoiceDueDate(e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="invoiceTermsType" className="form-label">Terms</label>
                        <select
                          id="invoiceTermsType"
                          className="form-input"
                          value={invoiceTermsType}
                          onChange={(e) => setInvoiceTermsType(e.target.value as 'One Time' | 'Net')}
                        >
                          <option value="One Time">One Time</option>
                          <option value="Net">Net</option>
                        </select>
                      </div>
                      {invoiceTermsType === 'Net' && (
                        <div className="form-group">
                          <label htmlFor="invoiceTermsNetDays" className="form-label">Net (number of days)</label>
                          <input
                            type="number"
                            id="invoiceTermsNetDays"
                            className="form-input"
                            min={1}
                            max={999}
                            value={invoiceTermsNetDays}
                            onChange={(e) => setInvoiceTermsNetDays(e.target.value.replace(/\D/g, '').slice(0, 3))}
                            placeholder="e.g. 30"
                          />
                        </div>
                      )}
                      <div className="modal-actions">
                        <button type="button" className="cancel-button" onClick={() => setGenerateInvoiceStep('confirm')}>Back</button>
                        <button type="submit" className="save-button" disabled={generateInvoiceSubmitting}>
                          {generateInvoiceSubmitting ? 'Sending…' : 'Submit & Send Email'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            )}

            {/* Upload Invoice PDF Modal */}
            {isUploadPdfModalOpen && (
              <div className="modal-overlay">
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
                  <div className="modal-header">
                    <h3 className="modal-title">Upload invoice PDF</h3>
                    <button
                      className="modal-close-button"
                      onClick={() => !uploadPdfSubmitting && (setIsUploadPdfModalOpen(false), setUploadPdfFile(null))}
                      aria-label="Close"
                      disabled={uploadPdfSubmitting}
                    >
                      ×
                    </button>
                  </div>
                  <form className="modal-form" style={{ padding: '0 1.25rem 1rem' }} onSubmit={async (e) => {
                    e.preventDefault();
                    if (!orderNumber || !uploadPdfFile) return;
                    setUploadPdfSubmitting(true);
                    try {
                      const reader = new FileReader();
                      const base64 = await new Promise<string>((resolve, reject) => {
                        reader.onload = () => resolve((reader.result as string) || '');
                        reader.onerror = () => reject(new Error('Failed to read file'));
                        reader.readAsDataURL(uploadPdfFile);
                      });
                      await createInvoiceWithPdf(orderNumber, { pdfBase64: base64 });
                      await fetchInvoicesForOrder();
                      addActivityLog('Invoice PDF uploaded', employee || 'System');
                      const updated = await fetchOrderByOrderNumber(orderNumber);
                      if (updated) {
                        setOrderData(mapApiOrderToDetail(updated));
                        setStatus(updated.status || status);
                      }
                      setIsUploadPdfModalOpen(false);
                      setUploadPdfFile(null);
                    } catch (err) {
                      alert((err as Error).message || 'Failed to upload PDF.');
                    } finally {
                      setUploadPdfSubmitting(false);
                    }
                  }}>
                    <div className="form-group">
                      <span className="form-label">PDF file</span>
                      <FileDropZone
                        id="invoicePdfFile"
                        accept=".pdf,application/pdf"
                        variant="compact"
                        hint="PDF only — drag here or browse."
                        onFiles={(files) => {
                          const f = files[0];
                          if (!f) {
                            setUploadPdfFile(null);
                            return;
                          }
                          const ok =
                            f.type === 'application/pdf' ||
                            /\.pdf$/i.test(f.name);
                          if (!ok) {
                            alert('Please choose a PDF file.');
                            return;
                          }
                          setUploadPdfFile(f);
                        }}
                      />
                      {uploadPdfFile && <p style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>{uploadPdfFile.name}</p>}
                    </div>
                    <div className="modal-actions">
                      <button type="button" className="cancel-button" onClick={() => { setIsUploadPdfModalOpen(false); setUploadPdfFile(null); }}>Cancel</button>
                      <button type="submit" className="save-button" disabled={!uploadPdfFile || uploadPdfSubmitting}>
                        {uploadPdfSubmitting ? 'Uploading…' : 'Upload'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Chat Log - commented out */}
            {false && (
            <>
            <div className="chat-log-section">
              <div className="chat-log-header">
                <h3 className="section-title">Chat Log</h3>
                {chatMessages.length > 0 && (
                  <button 
                    type="button"
                    className="chat-view-modal-button"
                    onClick={() => setIsChatLogModalOpen(true)}
                  >
                    View Full Log
                  </button>
                )}
              </div>
              <div className="chat-messages-container">
                {chatMessages.length > 0 ? (
                  <div className="chat-messages-list">
                    {chatMessages.map((msg) => (
                      <div key={msg.id} className="chat-message">
                        <div className="chat-message-header">
                          <span className="chat-message-user">{msg.user}</span>
                          <span className="chat-message-timestamp">{msg.timestamp}</span>
                        </div>
                        <div className="chat-message-content" style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</div>
                      </div>
                    ))}
                    <div ref={chatEndRef} />
                  </div>
                ) : (
                  <p className="chat-empty">No messages yet. Start a conversation about this order.</p>
                )}
              </div>
              <form className="chat-input-form" onSubmit={handleSendMessage}>
                <textarea
                  className="chat-input"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  rows={4}
                />
                <div className="chat-buttons-row">
                  <button 
                    type="button" 
                    className="chat-templated-button"
                    onClick={handleOpenTemplatedMessages}
                  >
                    Templated Messages
                  </button>
                  <button type="submit" className="chat-send-button" disabled={!newMessage.trim()}>
                    Send
                  </button>
                </div>
              </form>
            </div>

            {isChatLogModalOpen && (
              <div className="modal-overlay">
                <div className="modal-content chat-log-modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">Chat Log</h3>
                    <button 
                      className="modal-close-button"
                      onClick={() => setIsChatLogModalOpen(false)}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                  
                  <div className="chat-log-modal-container">
                    {chatMessages.length > 0 ? (
                      <div className="chat-messages-list">
                        {chatMessages.map((msg) => (
                          <div key={msg.id} className="chat-message">
                            <div className="chat-message-header">
                              <span className="chat-message-user">{msg.user}</span>
                              <span className="chat-message-timestamp">{msg.timestamp}</span>
                            </div>
                            <div className="chat-message-content" style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</div>
                          </div>
                        ))}
                        <div ref={chatEndRef} />
                      </div>
                    ) : (
                      <p className="chat-empty">No messages yet. Start a conversation about this order.</p>
                    )}
                  </div>
                  
                  <form className="chat-input-form modal-chat-form" onSubmit={handleSendMessage}>
                    <textarea
                      className="chat-input"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      rows={5}
                    />
                    <div className="chat-buttons-row">
                      <button 
                        type="button" 
                        className="chat-templated-button"
                        onClick={handleOpenTemplatedMessages}
                      >
                        Templated Messages
                      </button>
                      <button type="submit" className="chat-send-button" disabled={!newMessage.trim()}>
                        Send
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            </>
            )}

            {/* Set Up Installation Appointment Modal (Invoice stage) */}
            {isSetupInstallOpen && (
              <div className="modal-overlay">
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">Set Up Installation Appointment</h3>
                    <button className="modal-close-button" onClick={() => setIsSetupInstallOpen(false)} aria-label="Close">
                      ×
                    </button>
                  </div>
                  <div className="modal-form">
                    <p style={{ marginTop: 0, fontSize: '0.8125rem', color: '#6b7280' }}>
                      Details are prefilled from this order. The facility readiness checklist and products are brought over.
                      Serialized products (robots &amp; accessories) will need a unit selected from the database on the
                      appointment page. Creating this appointment moves the order to the Installation stage.
                    </p>
                    <div className="form-group">
                      <label className="form-label" htmlFor="setup-business-name">Business Name</label>
                      <input id="setup-business-name" type="text" className="form-input" value={setupBusinessName}
                        onChange={(e) => { setSetupBusinessName(e.target.value); setSetupError(''); }} disabled={setupBusy} />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="setup-business-address">Business Address</label>
                      <input id="setup-business-address" type="text" className="form-input" value={setupBusinessAddress}
                        onChange={(e) => setSetupBusinessAddress(e.target.value)} placeholder="Street, City, State, ZIP" disabled={setupBusy} />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="setup-poc">Point of Contact</label>
                      <input id="setup-poc" type="text" className="form-input" value={setupPoc}
                        onChange={(e) => setSetupPoc(e.target.value)} disabled={setupBusy} />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="setup-poc-email">Point of Contact's Email Address</label>
                      <input id="setup-poc-email" type="email" className="form-input" value={setupPocEmail}
                        onChange={(e) => setSetupPocEmail(e.target.value)} disabled={setupBusy} />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="setup-poc-phone">Point of Contact's Number</label>
                      <input id="setup-poc-phone" type="tel" className="form-input" value={setupPocPhone}
                        onChange={(e) => setSetupPocPhone(e.target.value)} disabled={setupBusy} />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="setup-install-at">Date &amp; Time of Installation</label>
                      <input id="setup-install-at" type="datetime-local" className="form-input" value={setupInstallAt}
                        onChange={(e) => { setSetupInstallAt(e.target.value); setSetupError(''); }} disabled={setupBusy} />
                    </div>
                    <div className="form-group">
                      <span className="form-label">Installer(s)</span>
                      <p style={{ marginTop: 0, marginBottom: '0.5rem', fontSize: '0.8125rem', color: '#6b7280' }}>
                        Employees with the Installation role are listed first.
                      </p>
                      <TaskAssigneePicker
                        users={installerPickerUsers}
                        value={setupInstallerIds}
                        onChange={setSetupInstallerIds}
                        addPlaceholder="Search employee to assign…"
                      />
                    </div>
                    {setupError ? <p className="create-order-error" role="alert">{setupError}</p> : null}
                    <div className="modal-actions">
                      <button type="button" className="cancel-button" disabled={setupBusy} onClick={() => setIsSetupInstallOpen(false)}>
                        Cancel
                      </button>
                      <button type="button" className="update-button" disabled={setupBusy} onClick={() => void submitSetupInstall()}>
                        {setupBusy ? 'Creating…' : 'Create appointment'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Installation Appointment Modal */}
            {isInstallationModalOpen && (
              <div className="modal-overlay">
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">Set Site Inspection</h3>
                    <button 
                      className="modal-close-button"
                      onClick={() => setIsInstallationModalOpen(false)}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                  
                  <form className="modal-form" onSubmit={async (e) => {
                    e.preventDefault();
                    if (!orderNumber) return;
                    // Build date and time string (convert 24-hour to 12-hour format for display)
                    const time12Hour = editInstallationTime ? convertTo12Hour(editInstallationTime) : '';
                    const dateTimeString = editInstallationDate && editInstallationTime 
                      ? `${editInstallationDate} ${time12Hour}`
                      : '';
                    const addressParts = [];
                    if (editSiteStreetAddress) addressParts.push(editSiteStreetAddress);
                    if (editSiteAptNumber) addressParts.push(`Apt ${editSiteAptNumber}`);
                    const addressLine1 = addressParts.join(', ');
                    const addressLine2 = [];
                    if (editSiteCity) addressLine2.push(editSiteCity);
                    if (editSiteState) addressLine2.push(editSiteState);
                    if (editSiteZipCode) addressLine2.push(editSiteZipCode);
                    const cityStateZip = addressLine2.join(', ');
                    const fullSiteLocation = [addressLine1, cityStateZip, editSiteCountry].filter(Boolean).join('\n');

                    try {
                      const updated = await updateOrder(orderNumber, {
                        installation_appointment_time: dateTimeString || null,
                        installation_employee_name: editInstallationEmployee?.trim() || null,
                        site_location: fullSiteLocation.trim() || null,
                        status: 'Scheduled',
                      });
                      setOrderData(mapApiOrderToDetail(updated));
                      setStatus('Scheduled');
                      setInstallationAppointmentTime(dateTimeString);
                      setEditInstallationAppointmentTime(dateTimeString);
                      setInstallationDate(editInstallationDate);
                      setInstallationTime(editInstallationTime);
                      setInstallationEmployee(editInstallationEmployee);
                      setSiteLocation(fullSiteLocation);
                      setEditSiteLocation(fullSiteLocation);
                      setSiteStreetAddress(editSiteStreetAddress);
                      setSiteAptNumber(editSiteAptNumber);
                      setSiteCity(editSiteCity);
                      setSiteState(editSiteState);
                      setSiteZipCode(editSiteZipCode);
                      setSiteCountry(editSiteCountry);

                      if (installationAppointmentTime !== dateTimeString && dateTimeString) {
                        addActivityLog(`Site inspection ${installationAppointmentTime ? `changed to ${dateTimeString}` : `set to ${dateTimeString}`}`, employee || 'System');
                      }
                      if (installationEmployee !== editInstallationEmployee && editInstallationEmployee) {
                        addActivityLog(`Site inspection employee ${installationEmployee ? `changed to ${editInstallationEmployee}` : `assigned to ${editInstallationEmployee}`}`, employee || 'System');
                      }
                      if (siteLocation !== fullSiteLocation && fullSiteLocation) {
                        addActivityLog(`Site location ${siteLocation ? 'updated' : 'set'}`, employee || 'System');
                      }

                      const assigneeUserId = editInstallationEmployee?.trim()
                        ? verifiedUsers.find((u) => u.username === editInstallationEmployee.trim())?.id
                        : undefined;

                      // Create a Site Inspection task assigned to the selected employee.
                      const taskName = `Site Inspection: ${orderData?.companyName || orderNumber}${fullSiteLocation ? ` - ${fullSiteLocation.split('\n')[0].trim()}` : ''}`;
                      const today = new Date().toISOString().split('T')[0];
                      await createTask({
                        name: taskName,
                        status: assigneeUserId ? 'To-Do' : 'Unassigned',
                        tags: ['installation'],
                        assigned_to_user_id: assigneeUserId,
                        assigned_by_user_id: user?.id ?? undefined,
                        client_id: apiClient?.id ?? undefined,
                        start_date: today,
                        due_date: editInstallationDate || undefined,
                        priority: 'Medium',
                        notes: orderNumber ? `Order: ${orderNumber}` : undefined,
                      });
                      addActivityLog('Site inspection task created and assigned', employee || 'System');

                      // Create the Site Inspection appointment so it appears in the Installation → Site
                      // Inspection tab, and email the client about the scheduled visit.
                      let createdInspectionId: number | null = null;
                      if (user?.id) {
                        const inspectionAtIso = editInstallationDate
                          ? new Date(`${editInstallationDate}T${editInstallationTime || '09:00'}`).toISOString()
                          : null;
                        try {
                          const inspection = await createSiteInspection({
                            user_id: user.id,
                            client_id: apiClient?.id ?? null,
                            order_number: orderNumber,
                            business_name: orderData?.companyName || orderNumber,
                            business_address: fullSiteLocation.trim() || null,
                            point_of_contact: apiClient?.point_of_contact ?? null,
                            poc_email: apiClient?.contact_email ?? null,
                            poc_phone: apiClient?.contact_phone ?? null,
                            inspection_at: inspectionAtIso,
                            assignee_user_ids: assigneeUserId ? [assigneeUserId] : [],
                            send_client_email: true,
                          });
                          createdInspectionId = inspection.id;
                          addActivityLog(
                            `Site inspection scheduled${apiClient?.contact_email ? ' and client notified by email' : ''}`,
                            employee || 'System'
                          );
                        } catch (inspErr) {
                          alert((inspErr as Error).message || 'The order was updated, but creating the site inspection failed.');
                        }
                      }

                      setIsInstallationModalOpen(false);
                      if (createdInspectionId != null) {
                        navigate(`/installation/site-inspection/${createdInspectionId}`);
                      }
                    } catch (err) {
                      alert((err as Error).message || 'Failed to save site inspection.');
                    }
                  }}>
                    <div className="form-group">
                      <label htmlFor="installationEmployee" className="form-label">Assign Employee</label>
                  <SearchableDropdown
                    options={verifiedUsers.map((u) => u.username)}
                        value={editInstallationEmployee}
                        onChange={setEditInstallationEmployee}
                    placeholder="Select or type employee name..."
                    required={false}
                  />
                    </div>

                    <div className="form-row">
                      <div className="form-group" style={{ flex: 1 }}>
                        <label htmlFor="installationDate" className="form-label">Site Inspection Date</label>
                        <input
                          type="date"
                          id="installationDate"
                          className="form-input"
                          value={editInstallationDate}
                          onChange={(e) => setEditInstallationDate(e.target.value)}
                        />
                      </div>

                      <div className="form-group" style={{ flex: 1 }}>
                        <label htmlFor="installationTime" className="form-label">Site Inspection Time</label>
                        <input
                          type="time"
                          id="installationTime"
                          className="form-input"
                          value={editInstallationTime}
                          onChange={(e) => setEditInstallationTime(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="siteStreetAddress" className="form-label">Street Address</label>
                      <input
                        type="text"
                        id="siteStreetAddress"
                        className="form-input"
                        value={editSiteStreetAddress}
                        onChange={(e) => setEditSiteStreetAddress(e.target.value)}
                        placeholder="Enter street address..."
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="siteAptNumber" className="form-label">Apartment, Suite, Unit, etc. (Optional)</label>
                      <input
                        type="text"
                        id="siteAptNumber"
                        className="form-input"
                        value={editSiteAptNumber}
                        onChange={(e) => setEditSiteAptNumber(e.target.value)}
                        placeholder="Apt, Unit, Suite, etc."
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group" style={{ flex: 2 }}>
                        <label htmlFor="siteCity" className="form-label">City</label>
                        <input
                          type="text"
                          id="siteCity"
                          className="form-input"
                          value={editSiteCity}
                          onChange={(e) => setEditSiteCity(e.target.value)}
                          placeholder="City"
                        />
                      </div>

                      <div className="form-group" style={{ flex: 1 }}>
                        <label htmlFor="siteState" className="form-label">State</label>
                        <input
                          type="text"
                          id="siteState"
                          className="form-input"
                          value={editSiteState}
                          onChange={(e) => setEditSiteState(e.target.value.toUpperCase())}
                          placeholder="State"
                          maxLength={2}
                          style={{ textTransform: 'uppercase' }}
                        />
                      </div>

                      <div className="form-group" style={{ flex: 1 }}>
                        <label htmlFor="siteZipCode" className="form-label">Zip Code</label>
                        <input
                          type="text"
                          id="siteZipCode"
                          className="form-input"
                          value={editSiteZipCode}
                          onChange={(e) => setEditSiteZipCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          placeholder="Zip Code"
                          maxLength={10}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="siteCountry" className="form-label">Country</label>
                      <input
                        type="text"
                        id="siteCountry"
                        className="form-input"
                        value={editSiteCountry}
                        onChange={(e) => setEditSiteCountry(e.target.value)}
                        placeholder="Country"
                      />
                    </div>

                    <div className="modal-actions">
                      <button type="button" className="cancel-button" onClick={() => setIsInstallationModalOpen(false)}>
                        Cancel
                      </button>
                      <button type="submit" className="save-button">
                        Save
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Confirm Inventory Modal - Installation stage */}
            {isConfirmInventoryModalOpen && (
              <div className="modal-overlay">
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px' }}>
                  <div className="modal-header">
                    <h3 className="modal-title">Confirm Inventory</h3>
                    <button
                      className="modal-close-button"
                      onClick={() => setIsConfirmInventoryModalOpen(false)}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                  <div className="modal-form" style={{ padding: '1rem' }}>
                    {(orderProductsFromContract.length > 0 ? orderProductsFromContract : products).length > 0 ? (
                      <div className="products-table-wrapper">
                        <table className="products-table">
                          <thead>
                            <tr>
                              <th>Product Name</th>
                              <th>Serial Number</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(orderProductsFromContract.length > 0 ? orderProductsFromContract : products).map((product, index) => {
                              const currentStatus = installationProductStatuses[index] ?? product.status ?? 'Pending';
                              const isRobotProduct = product.productName === 'TIM-E Bot' || product.productName === 'BIM-E bot' || product.productName === 'Mobile BIM-E' || product.productName === 'Kebb-i';
                              const displaySerial = installationSerialsByProductId[index] ?? product.serialNumber ?? 'N/A';
                              return (
                                <tr key={product.id}>
                                  <td>{product.productName}</td>
                                  <td>{displaySerial}</td>
                                  <td>
                                    <select
                                      className="form-input"
                                      value={currentStatus}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === 'Confirmed') {
                                          if (isRobotProduct) {
                                            setRobotPickerForProduct({ orderProductIndex: index, productName: product.productName });
                                            return;
                                          }
                                          const invProducts = inventoryProducts;
                                          const invProduct = invProducts.find((p) => p.name === product.productName);
                                          if (!invProduct) {
                                            setInstallationProductStatuses((prev) => ({ ...prev, [index]: 'Confirmed' }));
                                            return;
                                          }
                                          const availableCount = inventoryUnits.filter((u) => u.productId === invProduct.id && u.status === 'In Storage').length;
                                          if (availableCount < 1) {
                                            alert(`No availability for ${product.productName}. Cannot confirm.`);
                                            return;
                                          }
                                          const updated = invProducts.map((p) =>
                                            p.id === invProduct.id ? { ...p, availability: Math.max(0, p.availability - 1), inUse: (p.inUse || 0) + 1 } : p
                                          );
                                          setInventoryProducts(updated);
                                          setInstallationProductStatuses((prev) => ({ ...prev, [index]: 'Confirmed' }));
                                        } else {
                                          setInstallationProductStatuses((prev) => ({ ...prev, [index]: value }));
                                        }
                                      }}
                                      style={{ minWidth: '120px' }}
                                    >
                                      <option value="Pending">Pending</option>
                                      <option value="Confirmed">Confirmed</option>
                                      <option value="Delivered">Delivered</option>
                                    </select>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="no-products">No products found for this order.</p>
                    )}
                    <div className="modal-actions" style={{ marginTop: '1rem' }}>
                      <button type="button" className="save-button" onClick={() => setIsConfirmInventoryModalOpen(false)}>
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Robot picker modal - for TIM-E Bot / BIM-E / Mobile BIM-E */}
            {robotPickerForProduct && (
              <div className="modal-overlay">
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px' }}>
                  <div className="modal-header">
                    <h3 className="modal-title">Select robot — {robotPickerForProduct.productName}</h3>
                    <button
                      className="modal-close-button"
                      onClick={() => setRobotPickerForProduct(null)}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                  <div className="modal-form" style={{ padding: '1rem' }}>
                    {(() => {
                      const invProducts = inventoryProducts;
                      const invProduct = invProducts.find((p) => p.name === robotPickerForProduct.productName);
                      if (!invProduct) {
                        return <p className="no-products">No inventory product found for {robotPickerForProduct.productName}.</p>;
                      }
                      const units = inventoryUnits.filter((u) => u.productId === invProduct.id);
                      if (units.length === 0) {
                        return <p className="no-products">No robots for {robotPickerForProduct.productName}. Add units in Inventory first.</p>;
                      }
                      const city = installationContractData?.city ?? '';
                      const businessName = installationContractData?.businessName ?? '';
                      return (
                        <>
                          <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                            Assigning location: <strong>{city || '—'}</strong>, business: <strong>{businessName || '—'}</strong>
                          </p>
                          <div className="products-table-wrapper">
                            <table className="products-table">
                              <thead>
                                <tr>
                                  <th>Serial Number</th>
                                  <th>Model</th>
                                  <th>Status</th>
                                  <th>Current location</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {units.map((unit) => (
                                  <tr key={unit.id}>
                                    <td>{unit.serialNumber}</td>
                                    <td>{unit.model || '—'}</td>
                                    <td>{unit.status ?? '—'}</td>
                                    <td>{unit.location || '—'}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="save-button"
                                        onClick={async () => {
                                          setInventoryUnits(
                                            inventoryUnits.map((u) =>
                                              u.id === unit.id ? { ...u, status: 'Deployed' as const, location: city, business: businessName } : u
                                            )
                                          );
                                          try {
                                            await fetch(`${API_BASE}/api/robot-location-history`, {
                                              method: 'POST',
                                              headers: { 'Content-Type': 'application/json' },
                                              body: JSON.stringify({
                                                productId: robotPickerForProduct.productName,
                                                serialNumber: unit.serialNumber,
                                                location: city,
                                              }),
                                            });
                                          } catch {
                                            // best-effort; local inventory is updated
                                          }
                                          setInstallationProductStatuses((prev) => ({ ...prev, [robotPickerForProduct.orderProductIndex]: 'Confirmed' }));
                                          setInstallationSerialsByProductId((prev) => ({ ...prev, [robotPickerForProduct.orderProductIndex]: unit.serialNumber }));
                                          setRobotPickerForProduct(null);
                                        }}
                                      >
                                        Select
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Delivery Modal - contract data card then product table + Complete Order */}
            {isDeliveryModalOpen && deliveryCardData && (
              <div className="modal-overlay">
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: deliveryModalStep === 'products' ? '560px' : '480px' }}>
                  <div className="modal-header">
                    <h3 className="modal-title">Delivery Information</h3>
                    <button
                      className="modal-close-button"
                      onClick={() => { setIsDeliveryModalOpen(false); setDeliveryModalStep('card'); }}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                  <div className="modal-form" style={{ padding: '1rem' }}>
                    <div className="delivery-card" style={{
                      background: 'var(--card-bg, #f8f9fa)',
                      borderRadius: '8px',
                      padding: '1.25rem',
                      border: '1px solid var(--border-color, #dee2e6)',
                    }}>
                      <div className="delivery-card-row" style={{ marginBottom: '0.75rem' }}>
                        <span className="delivery-card-label" style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Business Name</span>
                        <span className="delivery-card-value">{deliveryCardData.businessName || '—'}</span>
                      </div>
                      <div className="delivery-card-row" style={{ marginBottom: '0.75rem' }}>
                        <span className="delivery-card-label" style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Service Address</span>
                        <span className="delivery-card-value" style={{ whiteSpace: 'pre-wrap' }}>{deliveryCardData.serviceAddress || '—'}</span>
                      </div>
                      <div className="delivery-card-row" style={{ marginBottom: '0.75rem' }}>
                        <span className="delivery-card-label" style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>City, State, Zip</span>
                        <span className="delivery-card-value">
                          {[deliveryCardData.city, deliveryCardData.state, deliveryCardData.zip].filter(Boolean).join(', ') || '—'}
                        </span>
                      </div>
                      <div className="delivery-card-row" style={{ marginBottom: '0.75rem' }}>
                        <span className="delivery-card-label" style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Location Contact Name</span>
                        <span className="delivery-card-value">{deliveryCardData.locationContactName || '—'}</span>
                      </div>
                      <div className="delivery-card-row" style={{ marginBottom: '0.75rem' }}>
                        <span className="delivery-card-label" style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Phone</span>
                        <span className="delivery-card-value">{deliveryCardData.locationContactPhone || '—'}</span>
                      </div>
                      <div className="delivery-card-row">
                        <span className="delivery-card-label" style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Location Contact Email</span>
                        <span className="delivery-card-value">{deliveryCardData.locationContactEmail || '—'}</span>
                      </div>
                    </div>

                    {deliveryModalStep === 'card' ? (
                      <div className="modal-actions" style={{ marginTop: '1rem' }}>
                        <button type="button" className="save-button" onClick={() => setDeliveryModalStep('products')}>
                          Confirm Delivery
                        </button>
                      </div>
                    ) : (
                      <>
                        <h4 style={{ marginTop: '1.25rem', marginBottom: '0.5rem' }}>Product status</h4>
                        {(orderProductsFromContract.length > 0 ? orderProductsFromContract : products).length > 0 ? (
                          <div className="products-table-wrapper">
                            <table className="products-table">
                              <thead>
                                <tr>
                                  <th>Product Name</th>
                                  <th>Serial Number</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(orderProductsFromContract.length > 0 ? orderProductsFromContract : products).map((product, index) => {
                                  const currentStatus = installationProductStatuses[index] ?? product.status ?? 'Pending';
                                  const displaySerial = installationSerialsByProductId[index] ?? product.serialNumber ?? 'N/A';
                                  return (
                                    <tr key={product.id}>
                                      <td>{product.productName}</td>
                                      <td>{displaySerial}</td>
                                      <td>
                                        <select
                                          className="form-input"
                                          value={currentStatus}
                                          onChange={(e) => setInstallationProductStatuses((prev) => ({ ...prev, [index]: e.target.value }))}
                                          style={{ minWidth: '120px' }}
                                        >
                                          <option value="Pending">Pending</option>
                                          <option value="Confirmed">Confirmed</option>
                                          <option value="Delivered">Delivered</option>
                                        </select>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="no-products">No products found for this order.</p>
                        )}
                        <div className="modal-actions" style={{ marginTop: '1rem', gap: '0.5rem' }}>
                          <button type="button" className="cancel-button" onClick={() => setDeliveryModalStep('card')}>
                            Back
                          </button>
                          <button
                            type="button"
                            className="save-button"
                            onClick={async () => {
                              if (!orderNumber) return;
                              try {
                                const updated = await updateOrder(orderNumber, { status: 'Completed', category: 'Completed' });
                                setOrderData(mapApiOrderToDetail(updated));
                                setStatus('Completed');
                                setStage('Completed');
                                addActivityLog('Order completed', employee || 'System');
                                setIsDeliveryModalOpen(false);
                                setDeliveryModalStep('card');
                              } catch (err) {
                                alert((err as Error).message || 'Failed to complete order.');
                              }
                            }}
                          >
                            Complete Order
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Templated Messages Modal */}
            {isTemplatedModalOpen && (
              <div className="modal-overlay">
                <div className="modal-content templated-modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">Select Templated Message</h3>
                    <button 
                      className="modal-close-button"
                      onClick={() => setIsTemplatedModalOpen(false)}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                  
                  <div className="templated-messages-list">
                    {templatedMessages.map((template) => (
                      <div 
                        key={template.id}
                        className="templated-message-item"
                        onClick={() => handleSelectTemplatedMessage(template.message)}
                      >
                        <div className="templated-message-title">{template.title}</div>
                        <div className="templated-message-preview">{template.message}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Contract Generation Modal */}
            {isContractModalOpen && (
              <div className={`modal-overlay ${isContractModalFullscreen ? 'contract-modal-fullscreen-overlay' : ''}`}>
                <div className={`modal-content contract-modal-content ${isContractModalFullscreen ? 'contract-modal-fullscreen' : ''}`} onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header contract-modal-header">
                    <h3 className="modal-title">
                      {contractModalStep === 'choose' && 'Choose Contract Type'}
                      {contractModalStep === 'upload' && 'Upload Contract'}
                      {contractModalStep === 'form' && 'Client & Agreement Details'}
                      {contractModalStep === 'form2' && 'Costs & Inventory'}
                      {contractModalStep === 'inventory_trial' && 'Inventory'}
                      {contractModalStep === 'scope' && 'Scope'}
                      {contractModalStep === 'revisions' && 'Revisions'}
                      {contractModalStep === 'edit-pdf' && 'Edit contract'}
                      {contractModalStep === 'signature' && 'TechForce Signature'}
                      {contractModalStep === 'email' && 'Send Contract Email'}
                    </h3>
                    <div className="contract-modal-header-actions">
                      <button
                        type="button"
                        className="contract-fullscreen-toggle"
                        onClick={() => setIsContractModalFullscreen(prev => !prev)}
                        title={isContractModalFullscreen ? 'Exit full screen' : 'Full screen'}
                        aria-label={isContractModalFullscreen ? 'Exit full screen' : 'Full screen'}
                      >
                        {isContractModalFullscreen ? 'Exit full screen' : 'Full screen'}
                      </button>
                      <button 
                        className="modal-close-button"
                        onClick={closeContractModal}
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                  
                  <div className="contract-modal-body" ref={contractModalBodyRef}>
                    {contractModalStep === 'choose' && (
                      <div className="contract-choose-section">
                        <p className="contract-choose-prompt">Select which contract to generate:</p>
                        <div className="contract-choose-buttons">
                          <button
                            type="button"
                            className="contract-type-button"
                            onClick={() => handleSelectContractType('service')}
                          >
                            <span className="contract-type-title">Robot Service Agreement</span>
                            <span className="contract-type-desc">Standard service agreement</span>
                          </button>
                          <button
                            type="button"
                            className="contract-type-button"
                            onClick={() => handleSelectContractType('trial')}
                          >
                            <span className="contract-type-title">Robot TRIAL Agreement</span>
                            <span className="contract-type-desc">Trial agreement</span>
                          </button>
                          <button
                            type="button"
                            className="contract-type-button"
                            onClick={handleStartContractUpload}
                          >
                            <span className="contract-type-title">Upload Contract</span>
                            <span className="contract-type-desc">
                              File a contract that was already filled out and signed
                            </span>
                          </button>
                        </div>
                      </div>
                    )}

                    {contractModalStep === 'upload' && (
                      <div className="contract-upload-section">
                        <p className="contract-upload-warning" role="note">
                          <strong>Upload completed contracts only.</strong>
                          <span>
                            The file you upload is filed as a signed contract exactly as it is —
                            nothing is filled in for you and the client is never asked to sign it.
                            Make sure the contract is fully filled out and signed by everyone before
                            uploading.
                          </span>
                        </p>

                        <div className="contract-upload-fields">
                          <label className="contract-upload-field">
                            <span className="contract-upload-label">Agreement type</span>
                            <select
                              className="form-select"
                              value={uploadContractType}
                              disabled={isUploadingContract}
                              onChange={(e) => setUploadContractType(e.target.value as 'service' | 'trial')}
                            >
                              <option value="service">Robot Service Agreement</option>
                              <option value="trial">Robot TRIAL Agreement</option>
                            </select>
                          </label>

                          <div className="contract-upload-field">
                            <span className="contract-upload-label" id="contract-upload-file-label">
                              Completed contract (PDF)
                            </span>
                            <label className="contract-upload-file-row">
                              <input
                                type="file"
                                className="contract-upload-file-input"
                                accept="application/pdf,.pdf"
                                disabled={isUploadingContract}
                                aria-labelledby="contract-upload-file-label"
                                onChange={(e) => handleContractFileChosen(e.target.files?.[0] ?? null)}
                              />
                              <span className="contract-upload-file-button">Choose PDF</span>
                              <span className={`contract-upload-file-name${uploadFile ? ' is-selected' : ''}`}>
                                {uploadFile
                                  ? `${uploadFile.name} (${formatFileSize(uploadFile.size)})`
                                  : 'No file chosen'}
                              </span>
                            </label>
                          </div>
                        </div>
                        {uploadError ? (
                          <p className="create-order-error" role="alert">
                            {uploadError}
                          </p>
                        ) : null}

                        <div className="contract-form-actions contract-upload-actions">
                          <button
                            type="button"
                            className="cancel-button"
                            disabled={isUploadingContract}
                            onClick={() => setContractModalStep('choose')}
                          >
                            Back
                          </button>
                          <button
                            type="button"
                            className="generate-link-button"
                            disabled={isUploadingContract || !uploadFile}
                            onClick={() => void handleUploadContract()}
                          >
                            {isUploadingContract ? 'Uploading…' : 'Upload contract'}
                          </button>
                        </div>
                      </div>
                    )}

                    {contractModalStep === 'form' && (
                      <form className="contract-form" onSubmit={handleContractFormSubmit}>
                        <div className="contract-form-section">
                          <h4 className="contract-form-section-title">Business & Location</h4>
                          <div className="contract-form-grid">
                            <div className="contract-form-group">
                              <label className="form-label">Business Name / Location</label>
                              <input
                                type="text"
                                className="form-input"
                                value={contractFormData.businessName}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, businessName: e.target.value }))}
                                required
                              />
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">Service Address</label>
                              <input
                                type="text"
                                className="form-input"
                                value={contractFormData.serviceAddress}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, serviceAddress: e.target.value }))}
                              />
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">City</label>
                              <input
                                type="text"
                                className="form-input"
                                value={contractFormData.city}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, city: e.target.value }))}
                                placeholder="City"
                              />
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">State</label>
                              <input
                                type="text"
                                className="form-input"
                                value={contractFormData.state}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, state: e.target.value }))}
                                placeholder="State"
                              />
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">Zip</label>
                              <input
                                type="text"
                                className="form-input"
                                value={contractFormData.zip}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, zip: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                                placeholder="Zip"
                              />
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">Location Contact Name</label>
                              <input
                                type="text"
                                className="form-input"
                                value={contractFormData.locationContactName}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, locationContactName: e.target.value }))}
                              />
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">Location Contact Phone</label>
                              <input
                                type="text"
                                className="form-input"
                                value={contractFormData.locationContactPhone}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, locationContactPhone: e.target.value }))}
                              />
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">Location Contact Email</label>
                              <input
                                type="email"
                                className="form-input"
                                value={contractFormData.locationContactEmail}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, locationContactEmail: e.target.value }))}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="contract-form-section">
                          <h4 className="contract-form-section-title">Authorized Person</h4>
                          <div className="contract-form-grid">
                            <div className="contract-form-group">
                              <label className="form-label">Authorized Person Name</label>
                              <input
                                type="text"
                                className="form-input"
                                value={contractFormData.authorizedPersonName}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, authorizedPersonName: e.target.value }))}
                              />
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">Authorized Person Title</label>
                              <input
                                type="text"
                                className="form-input"
                                value={contractFormData.authorizedPersonTitle}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, authorizedPersonTitle: e.target.value }))}
                              />
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">Authorized Person Email</label>
                              <input
                                type="email"
                                className="form-input"
                                value={contractFormData.authorizedPersonEmail}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, authorizedPersonEmail: e.target.value }))}
                              />
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">Authorized Person Phone</label>
                              <input
                                type="text"
                                className="form-input"
                                value={contractFormData.authorizedPersonPhone}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, authorizedPersonPhone: e.target.value }))}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="contract-form-section">
                          <h4 className="contract-form-section-title">Dates</h4>
                          <div className="contract-form-grid">
                            <div className="contract-form-group">
                              <label className="form-label">Effective Date</label>
                              <input
                                type="text"
                                className="form-input"
                                value={contractFormData.effectiveDate}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, effectiveDate: e.target.value }))}
                                placeholder="MM/DD/YYYY"
                              />
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">Term Start Date</label>
                              <input
                                type="text"
                                className="form-input"
                                value={contractFormData.termStartDate}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, termStartDate: e.target.value }))}
                                placeholder="MM/DD/YYYY"
                              />
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">Agreement Term Length</label>
                              <input
                                type="text"
                                className="form-input"
                                value={contractFormData.agreementTermLength ?? ''}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, agreementTermLength: e.target.value }))}
                                placeholder="e.g. 90 days, 7 months, 1 year"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="contract-form-actions">
                          <button type="button" className="cancel-button" onClick={() => setContractModalStep('choose')}>
                            Back
                          </button>
                          <button type="submit" className="generate-link-button">
                            {selectedContractType === 'trial' ? 'Next: Inventory' : 'Next: Costs & Inventory'}
                          </button>
                        </div>
                      </form>
                    )}

                    {contractModalStep === 'inventory_trial' && (
                      <form className="contract-form" onSubmit={handleContractInventoryTrialSubmit}>
                        <div className="contract-form-section">
                          {renderCatalogSectionTitle('monthly', 'Trial Inventory')}
                          <p className="contract-form-section-desc">Enter quantities for the trial agreement. "Edit prices" changes the monthly prices printed on the agreement.</p>
                          <div className="contract-form-grid contract-form-grid-3">
                            <div className="contract-form-group">
                              <label className="form-label">Quantity of TIM-E robots</label>
                              <input
                                type="text"
                                className="form-input form-input-qty"
                                value={contractFormData.qtyTimEBot}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, qtyTimEBot: e.target.value }))}
                                placeholder="Qty"
                              />
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">Quantity of bins</label>
                              <input
                                type="text"
                                className="form-input form-input-qty"
                                value={contractFormData.qtyBinsTrial ?? ''}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, qtyBinsTrial: e.target.value }))}
                                placeholder="Qty"
                              />
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">Quantity of bases</label>
                              <input
                                type="text"
                                className="form-input form-input-qty"
                                value={contractFormData.qtyBasesTrial ?? ''}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, qtyBasesTrial: e.target.value }))}
                                placeholder="Qty"
                              />
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">Quantity of BIM-E robots</label>
                              <input
                                type="text"
                                className="form-input form-input-qty"
                                value={contractFormData.qtyBIME}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, qtyBIME: e.target.value }))}
                                placeholder="Qty"
                              />
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">Quantity of Kebb-i</label>
                              <input
                                type="text"
                                className="form-input form-input-qty"
                                value={contractFormData.qtyKebbi}
                                onChange={(e) => setContractFormData(prev => ({ ...prev, qtyKebbi: e.target.value }))}
                                placeholder="Qty"
                              />
                            </div>
                            {renderContractProductFields('monthly')}
                          </div>
                        </div>
                        <div className="contract-form-actions">
                          <button type="button" className="cancel-button" onClick={() => setContractModalStep('form')}>
                            Back
                          </button>
                          <button type="submit" className="generate-link-button">
                            Next: Scope
                          </button>
                        </div>
                      </form>
                    )}

                    {contractModalStep === 'scope' && (
                      <form className="contract-form" onSubmit={handleContractScopeSubmit}>
                        <div className="contract-form-section">
                          <h4 className="contract-form-section-title">Scope</h4>
                          <p className="contract-form-section-desc">Describe the scope of the trial agreement. This appears on the agreement PDF below the cost table.</p>
                          <div className="contract-form-group">
                            <label className="form-label">Scope / Description</label>
                            <textarea
                              className="form-input contract-scope-textarea"
                              value={contractFormData.scopeText ?? ''}
                              onChange={(e) => setContractFormData(prev => ({ ...prev, scopeText: e.target.value }))}
                              placeholder="Enter scope and description..."
                              rows={8}
                            />
                          </div>
                        </div>
                        <div className="contract-form-actions">
                          <button type="button" className="cancel-button" onClick={() => setContractModalStep('inventory_trial')}>
                            Back
                          </button>
                          <button type="submit" className="generate-link-button">
                            Next: Revisions
                          </button>
                        </div>
                      </form>
                    )}

                    {contractModalStep === 'form2' && (
                      <form className="contract-form contract-form-2" onSubmit={handleContractForm2Submit}>
                        <div className="contract-form-section">
                          <h4 className="contract-form-section-title">Costs & Fees</h4>
                          <div className="contract-form-grid contract-form-grid-3">
                            <div className="contract-form-group">
                              <label className="form-label">Implementation Cost</label>
                              <input type="text" className="form-input" value={contractFormData.implementationCost} onChange={(e) => setContractFormData(prev => ({ ...prev, implementationCost: e.target.value }))} placeholder="e.g. $0" />
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">Shipping Fee</label>
                              <input type="text" className="form-input" value={contractFormData.shippingFee} onChange={(e) => setContractFormData(prev => ({ ...prev, shippingFee: e.target.value }))} placeholder="e.g. $0" />
                            </div>
                          </div>
                        </div>
                        <div className="contract-form-section">
                          {renderCatalogSectionTitle('monthly', 'Robot Quantity, Model and Monthly Cost per Item')}
                          <div className="contract-form-grid contract-form-grid-3">
                            {renderCatalogQtyFields('monthly')}
                          </div>
                          <div className="contract-form-group contract-form-group-inline">
                            <label className="form-label">Monthly Robotic Service Cost ($)</label>
                            <input type="text" className="form-input contract-form-total" value={computedMonthlyRoboticServiceCost.toLocaleString()} readOnly aria-readonly />
                          </div>
                        </div>
                        <div className="contract-form-section">
                          {renderCatalogSectionTitle('accessory', 'Additional Accessories')}
                          <div className="contract-form-grid contract-form-grid-3">
                            {renderCatalogQtyFields('accessory')}
                          </div>
                          <div className="contract-form-group contract-form-group-inline">
                            <label className="form-label">Additional Accessories Cost ($)</label>
                            <input type="text" className="form-input contract-form-total" value={discountedAdditionalAccessoriesCost.toLocaleString()} readOnly aria-readonly />
                          </div>
                        </div>
                        <div className="contract-form-section">
                          <h4 className="contract-form-section-title">Apply discount (optional)</h4>
                          <div className="contract-form-grid contract-form-grid-3">
                            <div className="contract-form-group">
                              <label className="form-label">Apply discount to</label>
                              <select className="form-input" value={contractFormData.discountTarget} onChange={(e) => setContractFormData(prev => ({ ...prev, discountTarget: e.target.value }))}>
                                <option value="">None</option>
                                <option value="robots">Robots (monthly)</option>
                                <option value="accessories">Accessories (one-time)</option>
                              </select>
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">Discount type</label>
                              <select className="form-input" value={contractFormData.discountType} onChange={(e) => setContractFormData(prev => ({ ...prev, discountType: e.target.value }))} disabled={!contractFormData.discountTarget}>
                                <option value="flat">Flat amount ($)</option>
                                <option value="percent">Percentage (%)</option>
                              </select>
                            </div>
                            <div className="contract-form-group">
                              <label className="form-label">{contractFormData.discountType === 'percent' ? 'Discount (%)' : 'Discount ($)'}</label>
                              <input type="text" className="form-input form-input-qty" value={contractFormData.discountValue} onChange={(e) => setContractFormData(prev => ({ ...prev, discountValue: e.target.value }))} placeholder={contractFormData.discountType === 'percent' ? 'e.g. 10' : 'e.g. 500'} disabled={!contractFormData.discountTarget} />
                            </div>
                          </div>
                        </div>
                        <div className="contract-form-section">
                          <h4 className="contract-form-section-title">Cost Summary</h4>
                          <div className="contract-form-grid contract-form-grid-3">
                            <div className="contract-form-group"><label className="form-label">Monthly Robotic Service Cost ($)</label><input type="text" className="form-input" value={discountedMonthlyRoboticServiceCost.toLocaleString()} readOnly aria-readonly /></div>
                            <div className="contract-form-group"><label className="form-label">Additional Accessories Cost ($)</label><input type="text" className="form-input" value={discountedAdditionalAccessoriesCost.toLocaleString()} readOnly aria-readonly /></div>
                            <div className="contract-form-group"><label className="form-label">Total Monthly Cost ($) — sum of above</label><input type="text" className="form-input contract-form-total" value={computedTotalMonthlyCost.toLocaleString()} readOnly aria-readonly /></div>
                            <div className="contract-form-group"><label className="form-label">Implementation Cost Due ($) — 50% of Implementation Cost</label><input type="text" className="form-input" value={computedImplementationCostDue.toLocaleString()} readOnly aria-readonly /></div>
                          </div>
                        </div>
                        <div className="contract-form-actions">
                          <button type="button" className="cancel-button" onClick={() => setContractModalStep('form')}>
                            Back
                          </button>
                          <button type="submit" className="generate-link-button">
                            Next: Revisions
                          </button>
                        </div>
                      </form>
                    )}

                    {contractModalStep === 'revisions' && (
                      <form className="contract-form" onSubmit={handleContractRevisionsSubmit}>
                        <div className="contract-form-section">
                          <h4 className="contract-form-section-title">Addendums</h4>
                          <p className="contract-form-section-desc">
                            Would you like to add addendum(s)? Each addendum is printed on its own page of the
                            agreement, between the terms and the signature page.
                          </p>
                          <div className="contract-revisions-choice" role="group" aria-label="Add addendums">
                            <label>
                              <input
                                type="radio"
                                name="contract-addendum-choice"
                                checked={contractAddendums.length > 0}
                                onChange={() => {
                                  if (contractAddendums.length === 0) setContractAddendums(['']);
                                }}
                              />
                              Yes
                            </label>
                            <label>
                              <input
                                type="radio"
                                name="contract-addendum-choice"
                                checked={contractAddendums.length === 0}
                                onChange={() => setContractAddendums([])}
                              />
                              No
                            </label>
                          </div>
                          {contractAddendums.map((text, idx) => (
                            <div className="contract-form-group contract-addendum" key={`addendum-${idx}`}>
                              <label className="form-label">
                                Addendum {idx + 1}
                                <button
                                  type="button"
                                  className="contract-addendum-remove"
                                  onClick={() => handleRemoveAddendum(idx)}
                                  aria-label={`Remove addendum ${idx + 1}`}
                                >
                                  Remove
                                </button>
                              </label>
                              <textarea
                                className="form-input contract-scope-textarea"
                                value={text}
                                onChange={(e) => handleAddendumChange(idx, e.target.value)}
                                placeholder="Type the addendum language that should appear in the agreement..."
                                rows={6}
                                required
                              />
                            </div>
                          ))}
                          {contractAddendums.length > 0 && (
                            <button type="button" className="contract-add-product-button" onClick={handleAddAddendum}>
                              + Add another addendum
                            </button>
                          )}
                        </div>

                        <div className="contract-form-section">
                          <h4 className="contract-form-section-title">Additional signatures</h4>
                          <p className="contract-form-section-desc">
                            Would you like additional signatures? A second signature box is added to the
                            Authorization section under the company name, and the signing form asks the
                            client to complete it.
                          </p>
                          <div className="contract-revisions-choice" role="group" aria-label="Additional signatures">
                            <label>
                              <input
                                type="radio"
                                name="contract-additional-signature"
                                checked={contractFormData.additionalSignatureRequested === true}
                                onChange={() => setContractFormData(prev => ({ ...prev, additionalSignatureRequested: true }))}
                              />
                              Yes
                            </label>
                            <label>
                              <input
                                type="radio"
                                name="contract-additional-signature"
                                checked={contractFormData.additionalSignatureRequested !== true}
                                onChange={() => setContractFormData(prev => ({ ...prev, additionalSignatureRequested: false }))}
                              />
                              No
                            </label>
                          </div>
                        </div>

                        <div className="contract-form-section">
                          <h4 className="contract-form-section-title">Edit the PDF</h4>
                          <p className="contract-form-section-desc">
                            Open the contract and type directly on the page. Move lines, delete wording, or add a
                            signature line. The saved PDF is the file attached when you send the email.
                          </p>
                          {editedContractPdf ? (
                            <p className="contract-form-section-desc">PDF edits are saved and will be sent with the email.</p>
                          ) : null}
                          <button
                            type="button"
                            className="contract-add-product-button"
                            onClick={openContractPdfEditor}
                            disabled={isOpeningContractEditor}
                          >
                            {isOpeningContractEditor ? 'Opening…' : editedContractPdf ? 'Edit contract again' : 'Edit contract'}
                          </button>
                          {editedContractPdf ? (
                            <button
                              type="button"
                              className="contract-addendum-remove"
                              onClick={() => {
                                setEditedContractPdf(null);
                                setContractPdfBase(null);
                                setContractPdfModel(null);
                              }}
                            >
                              Discard PDF edits
                            </button>
                          ) : null}
                        </div>

                        <div className="contract-form-actions">
                          <button
                            type="button"
                            className="cancel-button"
                            onClick={() => setContractModalStep(selectedContractType === 'trial' ? 'scope' : 'form2')}
                          >
                            Back
                          </button>
                          <button type="submit" className="generate-link-button">
                            Next: Signature
                          </button>
                        </div>
                      </form>
                    )}

                    {contractModalStep === 'edit-pdf' && contractEditorBytes && (
                      <ContractPdfEditor
                        pdfBytes={contractEditorBytes}
                        initialModel={contractPdfModel}
                        onCancel={() => setContractModalStep('revisions')}
                        onSave={(bytes, model) => {
                          setContractPdfBase(contractEditorBytes);
                          setContractPdfModel(model);
                          setEditedContractPdf(bytes);
                          setContractEditorBytes(null);
                          setContractModalStep('revisions');
                        }}
                      />
                    )}

                    {contractModalStep === 'signature' && (
                      <div className="contract-signature-step">
                        <p className="contract-signature-prompt">Sign in the TechForce section. This signature will appear on the last page of the contract PDF.</p>
                        <div className="order-detail-signature-section">
                          <div className="signature-pad-container">
                            <canvas
                              ref={signatureCanvasRef}
                              className="signature-canvas"
                              width={560}
                              height={180}
                              onMouseDown={startDrawing}
                              onMouseMove={draw}
                              onMouseUp={stopDrawing}
                              onMouseLeave={stopDrawing}
                              onTouchStart={startDrawing}
                              onTouchMove={draw}
                              onTouchEnd={stopDrawing}
                            />
                            <div className="signature-actions">
                              <button type="button" className="order-detail-clear-signature-button" onClick={clearSignature}>
                                Clear
                              </button>
                              <button type="button" className="save-signature-button" onClick={saveSignature}>
                                Save Signature
                              </button>
                              <button
                                type="button"
                                className="order-detail-clear-signature-button"
                                onClick={() => signatureFileInputRef.current?.click()}
                              >
                                Upload Signature
                              </button>
                              <input
                                ref={signatureFileInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/jpg"
                                style={{ display: 'none' }}
                                onChange={handleSignatureFileUpload}
                              />
                            </div>
                            <p className="contract-signature-prompt" style={{ fontSize: '0.8rem', margin: '0.35rem 0 0' }}>
                              Draw your signature above, or upload a saved signature image.
                            </p>
                            {signature && (
                              <div className="signature-preview">
                                <p className="signature-saved-message">✓ Signature saved</p>
                                <img src={signature} alt="Saved signature" className="signature-image" />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="contract-form-actions">
                          <button type="button" className="cancel-button" onClick={() => { setSignature(null); setContractModalStep('revisions'); }}>
                            Back
                          </button>
                          <button
                            type="button"
                            className="generate-link-button"
                            onClick={handleContractSignatureSubmit}
                            disabled={!signature}
                          >
                            Generate Email
                          </button>
                        </div>
                      </div>
                    )}

                    {contractModalStep === 'email' && (
                      <>
                        <div className="contract-email-section">
                          {contractEmailSent ? (
                            <div className="contract-email-sent-banner">
                              <p className="contract-email-sent-title">✓ Email sent</p>
                              <p className="contract-email-sent-detail">
                                The contract was sent to: {emailRecipients.join(', ')}
                              </p>
                            </div>
                          ) : (
                            <p className="contract-link-help">
                              Review the email below and click <strong>Send Email</strong> to deliver the contract signing link. The agreement PDF
                              {editedContractPdf ? ' (including your edits)' : ''} is attached.
                            </p>
                          )}

                          <div className="contract-email-field">
                            <label className="form-label">Recipients</label>
                            <div className="contract-email-recipients">
                              {emailRecipients.map((email) => (
                                <span key={email} className="contract-email-chip">
                                  {email}
                                  <button
                                    type="button"
                                    className="contract-email-chip-remove"
                                    onClick={() => handleRemoveRecipient(email)}
                                    aria-label={`Remove ${email}`}
                                    disabled={isSendingContractEmail || contractEmailSent}
                                    title={`Remove ${email}`}
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                              <input
                                type="email"
                                className="contract-email-recipient-input"
                                value={emailRecipientInput}
                                onChange={(e) => setEmailRecipientInput(e.target.value)}
                                onKeyDown={handleRecipientInputKeyDown}
                                placeholder={emailRecipients.length === 0 ? 'Add recipient email…' : 'Add another email…'}
                                disabled={isSendingContractEmail || contractEmailSent}
                              />
                              <button
                                type="button"
                                className="contract-email-add-button"
                                onClick={handleAddRecipient}
                                disabled={isSendingContractEmail || contractEmailSent || !emailRecipientInput.trim()}
                              >
                                Add
                              </button>
                            </div>
                            <p className="contract-link-help">
                              Press Enter, comma, or click Add to attach a recipient. billing@techforcerobotics.com is included by default along with the location and authorized contact emails.
                            </p>
                          </div>

                          <div className="contract-email-field">
                            <label className="form-label" htmlFor="contract-email-subject">Subject</label>
                            <input
                              id="contract-email-subject"
                              type="text"
                              className="form-input"
                              value={emailSubject}
                              onChange={(e) => setEmailSubject(e.target.value)}
                              disabled={isSendingContractEmail || contractEmailSent}
                            />
                          </div>

                          <div className="contract-email-field">
                            <label className="form-label" htmlFor="contract-email-body">Message</label>
                            <textarea
                              id="contract-email-body"
                              className="form-input contract-email-body"
                              value={emailBody}
                              onChange={(e) => setEmailBody(e.target.value)}
                              rows={14}
                              disabled={isSendingContractEmail || contractEmailSent}
                            />
                            <p className="contract-link-help">
                              The contract signing link is included in the message. You can edit any of the wording before sending.
                            </p>
                          </div>

                          <div className="contract-email-field">
                            <label className="form-label">Attachments</label>
                            {/* Attached documents show as chips, removed ones as a dashed
                                button to put them back, all in one wrapping row. */}
                            <div className="contract-email-attachments">
                              {contractEmailDocs.map((doc) =>
                                doc.attached ? (
                                  <span key={doc.key} className="contract-email-attachment-chip">
                                    <FontAwesomeIcon icon={faFilePdf} className="contract-email-attachment-icon" />
                                    <span className="contract-email-attachment-name">{doc.filename}</span>
                                    <button
                                      type="button"
                                      className="contract-email-chip-remove"
                                      onClick={() => doc.setAttached(false)}
                                      aria-label={`Remove ${doc.filename} attachment`}
                                      disabled={isSendingContractEmail || contractEmailSent}
                                      title="Remove attachment"
                                    >
                                      ×
                                    </button>
                                  </span>
                                ) : (
                                  <button
                                    key={doc.key}
                                    type="button"
                                    className="contract-email-attach-button"
                                    onClick={() => doc.setAttached(true)}
                                    disabled={isSendingContractEmail || contractEmailSent}
                                  >
                                    <FontAwesomeIcon icon={faPaperclip} /> Attach {doc.filename}
                                  </button>
                                )
                              )}
                              {extraEmailFiles.map((file) => (
                                <span key={`${file.name}-${file.size}-${file.lastModified}`} className="contract-email-attachment-chip">
                                  <FontAwesomeIcon icon={faPaperclip} className="contract-email-attachment-icon is-extra" />
                                  <span className="contract-email-attachment-name" title={file.name}>{file.name}</span>
                                  <button
                                    type="button"
                                    className="contract-email-chip-remove"
                                    onClick={() =>
                                      setExtraEmailFiles((prev) =>
                                        prev.filter(
                                          (item) =>
                                            !(
                                              item.name === file.name &&
                                              item.size === file.size &&
                                              item.lastModified === file.lastModified
                                            )
                                        )
                                      )
                                    }
                                    aria-label={`Remove ${file.name}`}
                                    disabled={isSendingContractEmail || contractEmailSent}
                                    title="Remove attachment"
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                              <button
                                type="button"
                                className="contract-email-attach-button"
                                onClick={() => extraEmailFileInputRef.current?.click()}
                                disabled={isSendingContractEmail || contractEmailSent}
                              >
                                <FontAwesomeIcon icon={faPaperclip} /> Add files
                              </button>
                              <input
                                ref={extraEmailFileInputRef}
                                type="file"
                                multiple
                                className="contract-email-file-input"
                                onChange={(event) => {
                                  handleAddExtraEmailFiles(event.target.files);
                                  event.target.value = '';
                                }}
                              />
                            </div>
                            <p className="contract-link-help">
                              The TechForce Certificate of Insurance and W-9 are attached by default. Remove either with the × if it isn't needed. Use Add files for anything else that should go out with this email. Each extra file can be up to 15 MB, and the extra files together can be up to 20 MB.
                            </p>
                          </div>

                          <div className="contract-email-field">
                            <label className="contract-email-toggle">
                              <input
                                type="checkbox"
                                checked={includeElevatorSurvey}
                                onChange={(e) => setIncludeElevatorSurvey(e.target.checked)}
                                disabled={isSendingContractEmail || contractEmailSent}
                              />
                              <span>Include a link to the Elevator Initial Survey</span>
                            </label>
                            <p className="contract-link-help">
                              When enabled, the client also receives a link to complete a short elevator survey. Submitted surveys appear under Elevators → Initial Surveys.
                            </p>
                          </div>

                          <div className="contract-link-section">
                            <label className="form-label">Contract signing link</label>
                            <div className="contract-link-container">
                              <input
                                type="text"
                                className="contract-link-input"
                                value={contractLink}
                                readOnly
                              />
                              <button
                                type="button"
                                className="copy-link-button"
                                onClick={handleCopyLink}
                              >
                                Copy Link
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="contract-preview-section">
                          <h4 className="contract-form-section-title">Filled contract (copy for client)</h4>
                          {isGeneratingContractPdf && (
                            <div className="contract-preview-loading">Generating filled PDF…</div>
                          )}
                          {!isGeneratingContractPdf && generatedContractPdfUrl && (
                            <iframe
                              src={generatedContractPdfUrl}
                              title="Filled contract preview"
                              className="contract-preview-iframe"
                            />
                          )}
                          {!isGeneratingContractPdf && !generatedContractPdfUrl && (
                            <p className="contract-link-help">Preview could not be generated. The client will still see the filled contract when they open the link.</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="modal-actions">
                    {contractModalStep === 'email' && !contractEmailSent && (
                      <>
                        <button type="button" className="cancel-button" onClick={closeContractModal} disabled={isSendingContractEmail}>
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="generate-link-button"
                          onClick={handleSendContractEmail}
                          disabled={isSendingContractEmail || emailRecipients.length === 0}
                        >
                          {isSendingContractEmail ? 'Sending…' : 'Send Email'}
                        </button>
                      </>
                    )}
                    {contractModalStep === 'email' && contractEmailSent && (
                      <button type="button" className="cancel-button" onClick={closeContractModal}>
                        Close
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Add-a-product dialog for the contract generator; stacks over the contract modal. */}
            {contractProductSection && (
              <ContractProductModal
                isOpen
                section={contractProductSection}
                inventoryProducts={inventoryProducts}
                existingNames={[
                  ...(selectedContractType === 'trial'
                    ? ['TIM-E Bot', 'Bins (Trial)', 'Bases (Trial)', 'BIM-E bot', 'Kebb-i']
                    : CATALOG_ROWS[contractProductSection].map((row) => row.label)),
                  ...contractCustomProducts.map((item) => item.name),
                ]}
                onCancel={() => setContractProductSection(null)}
                onAdd={handleAddContractProduct}
              />
            )}

            {/* Reprice this contract's catalog lines; stacks over the contract modal. */}
            {contractPriceSection && (
              <ContractPriceModal
                isOpen
                section={contractPriceSection}
                unitPrices={contractUnitPrices}
                onCancel={() => setContractPriceSection(null)}
                onSave={(unitPrices) => {
                  setContractFormData(prev => ({ ...prev, unitPrices }));
                  setContractPriceSection(null);
                }}
              />
            )}

            {/* Appointments: site inspections, installation appointments, and customer
                sign-offs tied to this order. Clicking a row opens its detail page. */}
            {(installActivity.inspections.length > 0 ||
              installActivity.installations.length > 0 ||
              installActivity.signoffs.length > 0) && (
              <div className="order-detail-table-section">
                <div className="collapsible-header" onClick={() => toggleSection('appointments')}>
                  <h3 className="section-title">Appointments</h3>
                  <span className={`collapse-arrow${!collapsedSections.appointments ? ' collapse-arrow--open' : ''}`}>▶</span>
                </div>
                {!collapsedSections.appointments && (
                  <div className="products-table-wrapper">
                    <table className="products-table">
                      <thead>
                        <tr>
                          <StaticTh>Type</StaticTh>
                          <StaticTh>Business</StaticTh>
                          <StaticTh>Date</StaticTh>
                          <StaticTh>Status</StaticTh>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ...installActivity.inspections.map((r) => ({ ...r, kind: 'Site Inspection', to: `/installation/site-inspection/${r.id}` })),
                          ...installActivity.installations.map((r) => ({ ...r, kind: 'Installation', to: `/installation/${r.id}` })),
                          ...installActivity.signoffs.map((r) => ({ ...r, kind: 'Customer Sign-Off', to: `/installation/sign-off/${r.id}` })),
                        ].map((r) => (
                          <tr
                            key={`${r.kind}-${r.id}`}
                            className="events-table-row-clickable"
                            onClick={() => navigate(r.to)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>{r.kind}</td>
                            <td>{r.business_name}</td>
                            <td>{r.when ? formatDateTimePT(r.when) : '—'}</td>
                            <td>
                              <span className={`status-badge status-${String(r.status).toLowerCase().replace(/\s+/g, '-')}`}>
                                {r.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Contract stage: Contracts → Activity log → Invoice → Products */}
            {stage === 'Contract' && (
              <>
                <div className="order-detail-table-section">
                  <div className="collapsible-header" onClick={() => toggleSection('contracts')}>
                    <h3 className="section-title">Contracts</h3>
                    <span className={`collapse-arrow${!collapsedSections.contracts ? ' collapse-arrow--open' : ''}`}>▶</span>
                  </div>
                  {!collapsedSections.contracts && (
                    contractsLoading ? (
                      <p className="contracts-table-loading">Loading contracts…</p>
                    ) : orderContracts.length === 0 ? (
                      <p className="contracts-table-empty">No contracts yet. Generate a contract to create one.</p>
                    ) : (
                      <div className="contracts-table-wrapper">
                        <table className="contracts-table">
                          <thead>
                            <tr>
                              <SortableTh label="Type" columnKey="type" sortKey={contractsSort.sortKey} sortDir={contractsSort.sortDir} onSort={contractsSort.toggleSort} />
                              <SortableTh label="Status" columnKey="status" sortKey={contractsSort.sortKey} sortDir={contractsSort.sortDir} onSort={contractsSort.toggleSort} />
                              <SortableTh label="Generated" columnKey="generated" sortKey={contractsSort.sortKey} sortDir={contractsSort.sortDir} onSort={contractsSort.toggleSort} />
                              <SortableTh label="Signed" columnKey="signed" sortKey={contractsSort.sortKey} sortDir={contractsSort.sortDir} onSort={contractsSort.toggleSort} />
                              <StaticTh>Link</StaticTh>
                              <StaticTh>Actions</StaticTh>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedOrderContracts.map((c) => renderContractRow(c, { survey: true }))}
                          </tbody>
                        </table>
                      </div>
                    )
                  )}
                </div>
                <div className="order-detail-table-section">
                  <div className="collapsible-header">
                    <div className="collapsible-header-left" onClick={() => toggleSection('invoice')}>
                      <h3 className="section-title">Invoice</h3>
                      <span className={`collapse-arrow${!collapsedSections.invoice ? ' collapse-arrow--open' : ''}`}>▶</span>
                    </div>
                    <button
                      type="button"
                      className="section-header-btn"
                      onClick={() => { setUploadPdfFile(null); setIsUploadPdfModalOpen(true); }}
                    >
                      + Upload PDF
                    </button>
                  </div>
                  {!collapsedSections.invoice && (
                    orderInvoicesLoading ? (
                      <p className="invoice-table-empty">Loading invoices…</p>
                    ) : orderInvoices.length === 0 ? (
                      <p className="invoice-table-empty">No invoice data yet.</p>
                    ) : (
                      <div className="invoice-table-wrapper">
                        <table className="invoice-table">
                          <thead>
                            <tr>
                              <SortableTh label="Item" columnKey="item" sortKey={invoicesSort.sortKey} sortDir={invoicesSort.sortDir} onSort={invoicesSort.toggleSort} />
                              <SortableTh label="PDF" columnKey="pdf" sortKey={invoicesSort.sortKey} sortDir={invoicesSort.sortDir} onSort={invoicesSort.toggleSort} />
                            </tr>
                          </thead>
                          <tbody>
                            {sortedOrderInvoices.map((inv) => (
                              <tr key={inv.id}>
                                <td>{inv.invoice_number || `Invoice #${inv.id}`}</td>
                                <td>
                                  {inv.has_pdf ? (
                                    <a href={`${API_BASE}/api/invoices/${inv.id}/pdf`} target="_blank" rel="noopener noreferrer" className="invoice-view-pdf-link">View PDF</a>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  )}
                </div>
                <div className="order-detail-table-section">
                  <div className="collapsible-header" onClick={() => toggleSection('products')}>
                    <h3 className="section-title">Products</h3>
                    <span className={`collapse-arrow${!collapsedSections.products ? ' collapse-arrow--open' : ''}`}>▶</span>
                  </div>
                  {!collapsedSections.products && (
                    orderContracts.length === 0 ? (
                      <p className="no-products">No contracts yet. Generate a contract to see products.</p>
                    ) : orderProductsFromContract.length > 0 ? (
                      <div className="products-table-wrapper">
                        <table className="products-table">
                          <thead>
                            <tr>
                              <SortableTh label="Product Name" columnKey="name" sortKey={productsReadonlySort.sortKey} sortDir={productsReadonlySort.sortDir} onSort={productsReadonlySort.toggleSort} />
                              <SortableTh label="Serial Number" columnKey="serial" sortKey={productsReadonlySort.sortKey} sortDir={productsReadonlySort.sortDir} onSort={productsReadonlySort.toggleSort} />
                              <SortableTh label="Status" columnKey="status" sortKey={productsReadonlySort.sortKey} sortDir={productsReadonlySort.sortDir} onSort={productsReadonlySort.toggleSort} />
                            </tr>
                          </thead>
                          <tbody>
                            {sortedOrderProductsReadonly.map((product) => (
                              <tr key={product.id}>
                                <td>{product.productName}</td>
                                <td>{product.serialNumber || 'N/A'}</td>
                                <td>
                                  <span className={`status-badge status-${(product.status || 'pending').toLowerCase().replace(' ', '-')}`}>
                                    {product.status || 'Pending'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="no-products">No products found for this order.</p>
                    )
                  )}
                </div>
                <div className="order-detail-table-section">
                  <div className="collapsible-header" onClick={() => toggleSection('activityLog')}>
                    <h3 className="section-title">Activity Log</h3>
                    <span className={`collapse-arrow${!collapsedSections.activityLog ? ' collapse-arrow--open' : ''}`}>▶</span>
                  </div>
                  {!collapsedSections.activityLog && (
                    activityLog.length > 0 ? (
                      <div className="activity-log-container">
                        <table className="activity-log-table">
                          <thead>
                            <tr>
                              <SortableTh label="Timestamp" columnKey="timestamp" sortKey={activitySort.sortKey} sortDir={activitySort.sortDir} onSort={activitySort.toggleSort} />
                              <SortableTh label="Action" columnKey="action" sortKey={activitySort.sortKey} sortDir={activitySort.sortDir} onSort={activitySort.toggleSort} />
                              <SortableTh label="User" columnKey="user" sortKey={activitySort.sortKey} sortDir={activitySort.sortDir} onSort={activitySort.toggleSort} />
                            </tr>
                          </thead>
                          <tbody>
                            {sortedActivityLog.map((entry) => (
                              <tr key={entry.id}>
                                <td>{entry.timestamp}</td>
                                <td>{entry.action}</td>
                                <td>{entry.user}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="activity-log-empty">No activity recorded yet.</p>
                    )
                  )}
                </div>
                {contractToDelete && (
                  <div className="modal-overlay">
                    <div className="modal-content contract-delete-modal" onClick={(e) => e.stopPropagation()}>
                      <div className="modal-header">
                        <h3 className="modal-title">Delete contract</h3>
                        <button type="button" className="modal-close-button" onClick={() => setContractToDelete(null)} aria-label="Close">×</button>
                      </div>
                      <p className="contract-delete-message">Are you sure you want to delete this {contractToDelete.label}? This cannot be undone.</p>
                      <div className="modal-actions">
                        <button type="button" className="cancel-button" onClick={() => setContractToDelete(null)}>Cancel</button>
                        <button type="button" className="save-button contract-delete-confirm-button" onClick={handleConfirmDeleteContract}>Delete</button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Delivery stage: Invoice → Products → Contracts → Activity log */}
            {stage === 'Delivery' && (
              <>
                <div className="order-detail-table-section">
                  <div className="collapsible-header">
                    <div className="collapsible-header-left" onClick={() => toggleSection('invoice')}>
                      <h3 className="section-title">Invoice</h3>
                      <span className={`collapse-arrow${!collapsedSections.invoice ? ' collapse-arrow--open' : ''}`}>▶</span>
                    </div>
                    <button
                      type="button"
                      className="section-header-btn"
                      onClick={() => { setUploadPdfFile(null); setIsUploadPdfModalOpen(true); }}
                    >
                      + Upload PDF
                    </button>
                  </div>
                  {!collapsedSections.invoice && (
                    orderInvoicesLoading ? (
                      <p className="invoice-table-empty">Loading invoices…</p>
                    ) : orderInvoices.length === 0 ? (
                      <p className="invoice-table-empty">No invoice data yet.</p>
                    ) : (
                      <div className="invoice-table-wrapper">
                        <table className="invoice-table">
                          <thead>
                            <tr>
                              <SortableTh label="Item" columnKey="item" sortKey={invoicesSort.sortKey} sortDir={invoicesSort.sortDir} onSort={invoicesSort.toggleSort} />
                              <SortableTh label="PDF" columnKey="pdf" sortKey={invoicesSort.sortKey} sortDir={invoicesSort.sortDir} onSort={invoicesSort.toggleSort} />
                            </tr>
                          </thead>
                          <tbody>
                            {sortedOrderInvoices.map((inv) => (
                              <tr key={inv.id}>
                                <td>{inv.invoice_number || `Invoice #${inv.id}`}</td>
                                <td>
                                  {inv.has_pdf ? (
                                    <a href={`${API_BASE}/api/invoices/${inv.id}/pdf`} target="_blank" rel="noopener noreferrer" className="invoice-view-pdf-link">View PDF</a>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  )}
                </div>
                <div className="order-detail-table-section">
                  <div className="collapsible-header" onClick={() => toggleSection('products')}>
                    <h3 className="section-title">Products</h3>
                    <span className={`collapse-arrow${!collapsedSections.products ? ' collapse-arrow--open' : ''}`}>▶</span>
                  </div>
                  {!collapsedSections.products && (
                    (orderProductsFromContract.length > 0 ? orderProductsFromContract : products).length > 0 ? (
                      <div className="products-table-wrapper">
                        <table className="products-table">
                          <thead>
                            <tr>
                              <SortableTh label="Product Name" columnKey="name" sortKey={productsReadonlySort.sortKey} sortDir={productsReadonlySort.sortDir} onSort={productsReadonlySort.toggleSort} />
                              <SortableTh label="Serial Number" columnKey="serial" sortKey={productsReadonlySort.sortKey} sortDir={productsReadonlySort.sortDir} onSort={productsReadonlySort.toggleSort} />
                              <SortableTh label="Status" columnKey="status" sortKey={productsReadonlySort.sortKey} sortDir={productsReadonlySort.sortDir} onSort={productsReadonlySort.toggleSort} />
                            </tr>
                          </thead>
                          <tbody>
                            {sortedCombinedOrderProducts.map((product) => (
                              <tr key={product.id}>
                                <td>{product.productName}</td>
                                <td>{product.serialNumber || 'N/A'}</td>
                                <td>
                                  <span className={`status-badge status-${(product.status || 'pending').toLowerCase().replace(' ', '-')}`}>
                                    {product.status || 'Pending'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="no-products">No products found for this order.</p>
                    )
                  )}
                </div>
                <div className="order-detail-table-section">
                  <div className="collapsible-header" onClick={() => toggleSection('contracts')}>
                    <h3 className="section-title">Contracts</h3>
                    <span className={`collapse-arrow${!collapsedSections.contracts ? ' collapse-arrow--open' : ''}`}>▶</span>
                  </div>
                  {!collapsedSections.contracts && (
                    contractsLoading ? (
                      <p className="contracts-table-loading">Loading contracts…</p>
                    ) : orderContracts.length === 0 ? (
                      <p className="contracts-table-empty">No contracts yet. Generate a contract to create one.</p>
                    ) : (
                      <div className="contracts-table-wrapper">
                        <table className="contracts-table">
                          <thead>
                            <tr>
                              <SortableTh label="Type" columnKey="type" sortKey={contractsSort.sortKey} sortDir={contractsSort.sortDir} onSort={contractsSort.toggleSort} />
                              <SortableTh label="Status" columnKey="status" sortKey={contractsSort.sortKey} sortDir={contractsSort.sortDir} onSort={contractsSort.toggleSort} />
                              <SortableTh label="Generated" columnKey="generated" sortKey={contractsSort.sortKey} sortDir={contractsSort.sortDir} onSort={contractsSort.toggleSort} />
                              <SortableTh label="Signed" columnKey="signed" sortKey={contractsSort.sortKey} sortDir={contractsSort.sortDir} onSort={contractsSort.toggleSort} />
                              <StaticTh>Link</StaticTh>
                              <StaticTh>Actions</StaticTh>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedOrderContracts.map((c) => renderContractRow(c, { deletable: true }))}
                          </tbody>
                        </table>
                      </div>
                    )
                  )}
                </div>
                <div className="order-detail-table-section">
                  <div className="collapsible-header" onClick={() => toggleSection('activityLog')}>
                    <h3 className="section-title">Activity Log</h3>
                    <span className={`collapse-arrow${!collapsedSections.activityLog ? ' collapse-arrow--open' : ''}`}>▶</span>
                  </div>
                  {!collapsedSections.activityLog && (
                    activityLog.length > 0 ? (
                      <div className="activity-log-container">
                        <table className="activity-log-table">
                          <thead>
                            <tr>
                              <SortableTh label="Timestamp" columnKey="timestamp" sortKey={activitySort.sortKey} sortDir={activitySort.sortDir} onSort={activitySort.toggleSort} />
                              <SortableTh label="Action" columnKey="action" sortKey={activitySort.sortKey} sortDir={activitySort.sortDir} onSort={activitySort.toggleSort} />
                              <SortableTh label="User" columnKey="user" sortKey={activitySort.sortKey} sortDir={activitySort.sortDir} onSort={activitySort.toggleSort} />
                            </tr>
                          </thead>
                          <tbody>
                            {sortedActivityLog.map((entry) => (
                              <tr key={entry.id}>
                                <td>{entry.timestamp}</td>
                                <td>{entry.action}</td>
                                <td>{entry.user}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="activity-log-empty">No activity recorded yet.</p>
                    )
                  )}
                </div>
                {contractToDelete && (
                  <div className="modal-overlay">
                    <div className="modal-content contract-delete-modal" onClick={(e) => e.stopPropagation()}>
                      <div className="modal-header">
                        <h3 className="modal-title">Delete contract</h3>
                        <button type="button" className="modal-close-button" onClick={() => setContractToDelete(null)} aria-label="Close">×</button>
                      </div>
                      <p className="contract-delete-message">Are you sure you want to delete this {contractToDelete.label}? This cannot be undone.</p>
                      <div className="modal-actions">
                        <button type="button" className="cancel-button" onClick={() => setContractToDelete(null)}>Cancel</button>
                        <button type="button" className="save-button contract-delete-confirm-button" onClick={handleConfirmDeleteContract}>Delete</button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Installation stage: Products → Activity log → Invoice → Contracts */}
            {stage === 'Installation' && (
              <>
                <div className="order-detail-table-section">
                  <div className="collapsible-header" onClick={() => toggleSection('products')}>
                    <h3 className="section-title">Products</h3>
                    <span className={`collapse-arrow${!collapsedSections.products ? ' collapse-arrow--open' : ''}`}>▶</span>
                  </div>
                  {!collapsedSections.products && (
                    (orderProductsFromContract.length > 0 ? orderProductsFromContract : products).length > 0 ? (
                      <div className="products-table-wrapper">
                        <table className="products-table">
                          <thead>
                            <tr>
                              <th>Product Name</th>
                              <th>Serial Number</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(orderProductsFromContract.length > 0 ? orderProductsFromContract : products).map((product, index) => {
                                const displayStatus = installationProductStatuses[index] ?? product.status ?? 'Pending';
                                const displaySerial = installationSerialsByProductId[index] ?? product.serialNumber ?? 'N/A';
                                return (
                                  <tr key={product.id}>
                                    <td>{product.productName}</td>
                                    <td>{displaySerial}</td>
                                    <td>
                                      <span className={`status-badge status-${(displayStatus || 'pending').toLowerCase().replace(/\s+/g, '-')}`}>
                                        {displayStatus || 'Pending'}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="no-products">No products found for this order.</p>
                    )
                  )}
                </div>
                <div className="order-detail-table-section">
                  <div className="collapsible-header">
                    <div className="collapsible-header-left" onClick={() => toggleSection('invoice')}>
                      <h3 className="section-title">Invoice</h3>
                      <span className={`collapse-arrow${!collapsedSections.invoice ? ' collapse-arrow--open' : ''}`}>▶</span>
                    </div>
                    <button
                      type="button"
                      className="section-header-btn"
                      onClick={() => { setUploadPdfFile(null); setIsUploadPdfModalOpen(true); }}
                    >
                      + Upload PDF
                    </button>
                  </div>
                  {!collapsedSections.invoice && (
                    orderInvoicesLoading ? (
                      <p className="invoice-table-empty">Loading invoices…</p>
                    ) : orderInvoices.length === 0 ? (
                      <p className="invoice-table-empty">No invoice data yet.</p>
                    ) : (
                      <div className="invoice-table-wrapper">
                        <table className="invoice-table">
                          <thead>
                            <tr>
                              <SortableTh label="Item" columnKey="item" sortKey={invoicesSort.sortKey} sortDir={invoicesSort.sortDir} onSort={invoicesSort.toggleSort} />
                              <SortableTh label="PDF" columnKey="pdf" sortKey={invoicesSort.sortKey} sortDir={invoicesSort.sortDir} onSort={invoicesSort.toggleSort} />
                            </tr>
                          </thead>
                          <tbody>
                            {sortedOrderInvoices.map((inv) => (
                              <tr key={inv.id}>
                                <td>{inv.invoice_number || `Invoice #${inv.id}`}</td>
                                <td>
                                  {inv.has_pdf ? (
                                    <a href={`${API_BASE}/api/invoices/${inv.id}/pdf`} target="_blank" rel="noopener noreferrer" className="invoice-view-pdf-link">View PDF</a>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  )}
                </div>
                <div className="order-detail-table-section">
                  <div className="collapsible-header" onClick={() => toggleSection('contracts')}>
                    <h3 className="section-title">Contracts</h3>
                    <span className={`collapse-arrow${!collapsedSections.contracts ? ' collapse-arrow--open' : ''}`}>▶</span>
                  </div>
                  {!collapsedSections.contracts && (
                    contractsLoading ? (
                      <p className="contracts-table-loading">Loading contracts…</p>
                    ) : orderContracts.length === 0 ? (
                      <p className="contracts-table-empty">No contracts yet. Generate a contract to create one.</p>
                    ) : (
                      <div className="contracts-table-wrapper">
                        <table className="contracts-table">
                          <thead>
                            <tr>
                              <SortableTh label="Type" columnKey="type" sortKey={contractsSort.sortKey} sortDir={contractsSort.sortDir} onSort={contractsSort.toggleSort} />
                              <SortableTh label="Status" columnKey="status" sortKey={contractsSort.sortKey} sortDir={contractsSort.sortDir} onSort={contractsSort.toggleSort} />
                              <SortableTh label="Generated" columnKey="generated" sortKey={contractsSort.sortKey} sortDir={contractsSort.sortDir} onSort={contractsSort.toggleSort} />
                              <SortableTh label="Signed" columnKey="signed" sortKey={contractsSort.sortKey} sortDir={contractsSort.sortDir} onSort={contractsSort.toggleSort} />
                              <StaticTh>Link</StaticTh>
                              <StaticTh>Actions</StaticTh>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedOrderContracts.map((c) => renderContractRow(c, { deletable: true }))}
                          </tbody>
                        </table>
                      </div>
                    )
                  )}
                </div>
                <div className="order-detail-table-section">
                  <div className="collapsible-header" onClick={() => toggleSection('activityLog')}>
                    <h3 className="section-title">Activity Log</h3>
                    <span className={`collapse-arrow${!collapsedSections.activityLog ? ' collapse-arrow--open' : ''}`}>▶</span>
                  </div>
                  {!collapsedSections.activityLog && (
                    activityLog.length > 0 ? (
                      <div className="activity-log-container">
                        <table className="activity-log-table">
                          <thead>
                            <tr>
                              <SortableTh label="Timestamp" columnKey="timestamp" sortKey={activitySort.sortKey} sortDir={activitySort.sortDir} onSort={activitySort.toggleSort} />
                              <SortableTh label="Action" columnKey="action" sortKey={activitySort.sortKey} sortDir={activitySort.sortDir} onSort={activitySort.toggleSort} />
                              <SortableTh label="User" columnKey="user" sortKey={activitySort.sortKey} sortDir={activitySort.sortDir} onSort={activitySort.toggleSort} />
                            </tr>
                          </thead>
                          <tbody>
                            {sortedActivityLog.map((entry) => (
                              <tr key={entry.id}>
                                <td>{entry.timestamp}</td>
                                <td>{entry.action}</td>
                                <td>{entry.user}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="activity-log-empty">No activity recorded yet.</p>
                    )
                  )}
                </div>
                {contractToDelete && (
                  <div className="modal-overlay">
                    <div className="modal-content contract-delete-modal" onClick={(e) => e.stopPropagation()}>
                      <div className="modal-header">
                        <h3 className="modal-title">Delete contract</h3>
                        <button type="button" className="modal-close-button" onClick={() => setContractToDelete(null)} aria-label="Close">×</button>
                      </div>
                      <p className="contract-delete-message">Are you sure you want to delete this {contractToDelete.label}? This cannot be undone.</p>
                      <div className="modal-actions">
                        <button type="button" className="cancel-button" onClick={() => setContractToDelete(null)}>Cancel</button>
                        <button type="button" className="save-button contract-delete-confirm-button" onClick={handleConfirmDeleteContract}>Delete</button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {stage !== 'Contract' && stage !== 'Delivery' && stage !== 'Installation' && (
              <>
                <div className="order-detail-table-section">
                  <div className="collapsible-header" onClick={() => toggleSection('products')}>
                    <h3 className="section-title">Products</h3>
                    <span className={`collapse-arrow${!collapsedSections.products ? ' collapse-arrow--open' : ''}`}>▶</span>
                  </div>
                  {!collapsedSections.products && (
                    products.length > 0 ? (
                      <div className="products-table-wrapper">
                        <table className="products-table">
                          <thead>
                            <tr>
                              <SortableTh label="Product Name" columnKey="name" sortKey={productsReadonlySort.sortKey} sortDir={productsReadonlySort.sortDir} onSort={productsReadonlySort.toggleSort} />
                              <SortableTh label="Serial Number" columnKey="serial" sortKey={productsReadonlySort.sortKey} sortDir={productsReadonlySort.sortDir} onSort={productsReadonlySort.toggleSort} />
                              <SortableTh label="Status" columnKey="status" sortKey={productsReadonlySort.sortKey} sortDir={productsReadonlySort.sortDir} onSort={productsReadonlySort.toggleSort} />
                            </tr>
                          </thead>
                          <tbody>
                            {sortedProductsOnly.map((product) => (
                              <tr key={product.id}>
                                <td>{product.productName}</td>
                                <td>{product.serialNumber || 'N/A'}</td>
                                <td>
                                  <span className={`status-badge status-${(product.status || 'pending').toLowerCase().replace(' ', '-')}`}>
                                    {product.status || 'Pending'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="no-products">No products found for this order.</p>
                    )
                  )}
                </div>
                <div className="order-detail-table-section">
                  <div className="collapsible-header" onClick={() => toggleSection('activityLog')}>
                    <h3 className="section-title">Activity Log</h3>
                    <span className={`collapse-arrow${!collapsedSections.activityLog ? ' collapse-arrow--open' : ''}`}>▶</span>
                  </div>
                  {!collapsedSections.activityLog && (
                    activityLog.length > 0 ? (
                      <div className="activity-log-container">
                        <table className="activity-log-table">
                          <thead>
                            <tr>
                              <SortableTh label="Timestamp" columnKey="timestamp" sortKey={activitySort.sortKey} sortDir={activitySort.sortDir} onSort={activitySort.toggleSort} />
                              <SortableTh label="Action" columnKey="action" sortKey={activitySort.sortKey} sortDir={activitySort.sortDir} onSort={activitySort.toggleSort} />
                              <SortableTh label="User" columnKey="user" sortKey={activitySort.sortKey} sortDir={activitySort.sortDir} onSort={activitySort.toggleSort} />
                            </tr>
                          </thead>
                          <tbody>
                            {sortedActivityLog.map((entry) => (
                              <tr key={entry.id}>
                                <td>{entry.timestamp}</td>
                                <td>{entry.action}</td>
                                <td>{entry.user}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="activity-log-empty">No activity recorded yet.</p>
                    )
                  )}
                </div>
              </>
            )}

          </div>
        </main>
      </div>

      {contractToVoid && (
        <div className="modal-overlay" onClick={() => !contractVoidSubmitting && setContractToVoid(null)}>
          <div className="modal-content order-void-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Void contract</h3>
              <button
                type="button"
                className="modal-close-button"
                onClick={() => setContractToVoid(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="order-void-modal-body">
              <p className="order-void-message">
                Voiding this {contractToVoid.contract_type === 'trial' ? 'Trial' : 'Service'} contract
                stops the follow-up reminders and prevents the client from signing it. The contract
                and its PDF stay on the order, and you can restore it later.
              </p>
              <div className="form-group">
                <label htmlFor="void-contract-reason" className="form-label">Reason (optional)</label>
                <textarea
                  id="void-contract-reason"
                  className="form-input order-void-reason-textarea"
                  value={contractVoidReason}
                  onChange={(e) => {
                    setContractVoidReason(e.target.value);
                    setContractVoidError('');
                  }}
                  rows={3}
                  placeholder="e.g. Replaced by a corrected contract"
                  disabled={contractVoidSubmitting}
                  autoFocus
                />
              </div>
              {contractVoidError ? (
                <p className="create-order-error" role="alert">{contractVoidError}</p>
              ) : null}
              <div className="modal-actions order-void-modal-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setContractToVoid(null)}
                  disabled={contractVoidSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="order-void-confirm-button"
                  onClick={() => void handleConfirmVoidContract()}
                  disabled={contractVoidSubmitting}
                >
                  {contractVoidSubmitting ? 'Voiding…' : 'Void contract'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {voidOrderModalOpen && (
        <div className="modal-overlay" onClick={closeVoidOrderModal}>
          <div className="modal-content order-void-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Void order</h3>
              <button type="button" className="modal-close-button" onClick={closeVoidOrderModal} aria-label="Close">×</button>
            </div>
            <div className="order-void-modal-body">
              <p className="order-void-message">
                Voiding <strong>{orderData.orderNumber}</strong> will move it to the Voided Orders table.
                Please provide a reason — it will be shown on the Order Information card.
              </p>
              <div className="form-group">
                <label htmlFor="void-order-reason" className="form-label">Reason *</label>
                <textarea
                  id="void-order-reason"
                  className="form-input order-void-reason-textarea"
                  value={voidReasonInput}
                  onChange={(e) => {
                    setVoidReasonInput(e.target.value);
                    setVoidOrderError('');
                  }}
                  rows={4}
                  placeholder="Why is this order being voided?"
                  disabled={voidOrderSubmitting}
                  autoFocus
                />
              </div>
              {voidOrderError ? (
                <p className="create-order-error" role="alert">{voidOrderError}</p>
              ) : null}
              <div className="modal-actions order-void-modal-actions">
                <button type="button" className="cancel-button" onClick={closeVoidOrderModal} disabled={voidOrderSubmitting}>Cancel</button>
                <button
                  type="button"
                  className="order-void-confirm-button"
                  onClick={handleVoidOrder}
                  disabled={voidOrderSubmitting || !voidReasonInput.trim()}
                >
                  {voidOrderSubmitting ? 'Voiding…' : 'Void order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetail;

